import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { useColumnCustomizer } from '../components/ColumnCustomizer';
import { ClientLedgerLetterhead } from '../components/DocumentLetterheads';
import { printComponent } from '../utils/printComponent';

// Every fetch in this file must go through BASE, never a raw literal — a
// hardcoded literal URL works in local dev but is unreachable
// from a real visitor's browser in production, silently failing.
const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// The standard client-type list. It's the seed for every firm; from here on
// the live list is firm-editable (Settings → Client Types), so `client_type`
// is treated as a free slug, not a fixed enum.
const CLIENT_TYPE_OPTIONS = [
  { value: 'individual', label: 'Individual' },
  { value: 'sole_proprietor', label: 'Sole Proprietor' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'llp', label: 'LLP' },
  { value: 'private_limited', label: 'Private Limited' },
  { value: 'public_limited', label: 'Public Limited' },
  { value: 'trust', label: 'Trust' },
  { value: 'society', label: 'Society' },
  { value: 'huf', label: 'HUF' },
  { value: 'other', label: 'Other' },
];

const STANDARD_TYPE_LABELS = Object.fromEntries(CLIENT_TYPE_OPTIONS.map(o => [o.value, o.label]));

// Title-case a raw slug for display when we have no explicit label for it.
const prettifyType = (v) =>
  String(v || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const slugifyType = (name) =>
  String(name || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60);

// Best-effort display label for a stored client_type slug, given the firm's
// live type list (falls back to the standard labels, then a prettified slug).
const typeLabelFor = (value, types) =>
  types?.find(t => t.value === value)?.name || STANDARD_TYPE_LABELS[value] || prettifyType(value) || '—';

// Maps free-text client-type labels other practice software exports onto our
// standard slugs. During import an unknown value is created as a new type
// rather than forced to 'other'.
const CLIENT_TYPE_ALIASES = {
  'individual': 'individual',
  'sole proprietorship': 'sole_proprietor',
  'sole proprietor': 'sole_proprietor',
  'proprietorship': 'sole_proprietor',
  'partnership firm': 'partnership',
  'partnership': 'partnership',
  'llp': 'llp',
  'limited liability partnership': 'llp',
  'private limited': 'private_limited',
  'private limited company': 'private_limited',
  'pvt ltd': 'private_limited',
  'pvt. ltd.': 'private_limited',
  'limited company': 'public_limited',
  'public limited': 'public_limited',
  'public limited company': 'public_limited',
  'ltd': 'public_limited',
  'trust': 'trust',
  'society': 'society',
  'huf': 'huf',
  'hindu undivided family': 'huf',
  'other': 'other',
};

// Returns a known slug for `raw`, or '' if it's an unrecognised type name.
const normalizeClientType = (raw) => {
  if (!raw) return '';
  const key = String(raw).trim().toLowerCase();
  if (CLIENT_TYPE_ALIASES[key]) return CLIENT_TYPE_ALIASES[key];
  const snake = slugifyType(key);
  if (CLIENT_TYPE_OPTIONS.some(o => o.value === snake)) return snake;
  return '';
};

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu & Kashmir', 'Ladakh', 'Puducherry', 'Chandigarh'
];

// Human column header → backend field key, for the import template. Also
// accepts the raw headers a Practive client export uses ("Name", "Type",
// "Users") so those files can be imported without reformatting.
const IMPORT_HEADER_MAP = {
  'file no': 'file_number', 'file no.': 'file_number', 'file number': 'file_number',
  'client name': 'client_name', 'name': 'client_name',
  'client type': 'client_type', 'type': 'client_type',
  'pan': 'pan', 'gstin': 'gstin',
  'mobile': 'mobile', 'secondary mobile': 'secondary_mobile',
  'email': 'email', 'contact person': 'contact_person',
  'address': 'address', 'city': 'city', 'state': 'state', 'pincode': 'pincode',
  'group': 'group_name', 'tags': 'tags',
  'assigned users': 'assigned_users', 'users': 'assigned_users',
  'status': 'status', 'opening balance': 'opening_balance', 'notes': 'notes',
  'constitution': 'constitution',
  'date of birth / incorporation': 'date_of_birth',
  'date of birth/incorporation': 'date_of_birth',
  'date of birth': 'date_of_birth',
  'date of incorporation': 'date_of_birth',
  'dob': 'date_of_birth',
};

// Normalise whatever someone typed for a date onto the plain 'YYYY-MM-DD'
// the API/Postgres wants. Accepts an Excel date cell, DD/MM/YYYY, DD-MM-YYYY,
// DD.MM.YYYY, 2-digit years, YYYY-MM-DD / YYYY/MM/DD, and raw Excel serials.
// Returns '' when it can't make sense of the value (import then warns rather
// than failing the whole row).
const toISODate = (v) => {
  if (v === null || v === undefined || v === '') return '';
  if (v instanceof Date && !isNaN(v)) {
    return `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, '0')}-${String(v.getDate()).padStart(2, '0')}`;
  }
  const s = String(v).trim();
  if (!s) return '';

  // YYYY-MM-DD or YYYY/MM/DD (optionally followed by a time part)
  let m = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;

  // DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY — 2- or 4-digit year
  m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})$/);
  if (m) {
    let d = parseInt(m[1], 10);
    let mo = parseInt(m[2], 10);
    let y = parseInt(m[3], 10);
    // if it's unambiguously month-first (e.g. 04/28/1983), swap
    if (mo > 12 && d <= 12) { [d, mo] = [mo, d]; }
    if (y < 100) y += (2000 + y > new Date().getFullYear()) ? 1900 : 2000;
    if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31) {
      return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    }
    return '';
  }

  // Bare Excel serial number (days since 1899-12-30)
  if (/^\d+(\.\d+)?$/.test(s)) {
    const n = parseFloat(s);
    if (n > 59 && n < 60000) {
      const dt = new Date(Date.UTC(1899, 11, 30) + n * 86400000);
      if (!isNaN(dt)) return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
    }
  }

  return '';
};

const CLIENT_COLUMNS = [
  { key: 'file_number', label: 'File No.', default: true },
  { key: 'client_name', label: 'Client Name', default: true },
  { key: 'tags', label: 'Tags', default: true },
  { key: 'client_type', label: 'Type', default: true },
  { key: 'pan', label: 'PAN', default: true },
  { key: 'gstin', label: 'GSTIN', default: true },
  { key: 'city', label: 'City', default: true },
  { key: 'group_name', label: 'Group', default: true },
  { key: 'mobile', label: 'Mobile', default: true },
  { key: 'email', label: 'Email', default: false },
  { key: 'state', label: 'State', default: false },
  { key: 'contact_person', label: 'Contact Person', default: false },
  { key: 'assigned_users', label: 'Assigned Users', default: true },
  { key: 'is_active', label: 'Status', default: true },
];

// ── SMART DATE PICKER (same as CRM module) ────────────────────────────────────
function SmartDatePicker({ label, value, onChange, placeholder = 'dd/mm/yyyy', darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(() => value ? new Date(value).getFullYear() : new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => value ? new Date(value).getMonth() : new Date().getMonth());
  const [mode, setMode] = useState('day');
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const DAYS = ['Su','Mo','Tu','We','Th','Fr','Sa'];
  const today = new Date(); today.setHours(0,0,0,0);
  const selected = value ? new Date(value + 'T00:00:00') : null;
  const getDays = () => {
    const first = new Date(viewYear, viewMonth, 1).getDay();
    const total = new Date(viewYear, viewMonth + 1, 0).getDate();
    const prev = new Date(viewYear, viewMonth, 0).getDate();
    const cells = [];
    for (let i = first - 1; i >= 0; i--) cells.push({ d: prev - i, curr: false });
    for (let d = 1; d <= total; d++) cells.push({ d, curr: true });
    while (cells.length % 7 !== 0) cells.push({ d: cells.length - first - total + 1, curr: false });
    return cells;
  };
  const selectDay = (d) => {
    const m = String(viewMonth + 1).padStart(2,'0'), dd = String(d).padStart(2,'0');
    onChange(`${viewYear}-${m}-${dd}`); setOpen(false);
  };
  const yearRange = Array.from({ length: 12 }, (_, i) => viewYear - 5 + i);
  const displayVal = value ? new Date(value + 'T00:00:00').toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '';
  return (
    <div style={{ position:'relative' }} ref={ref}>
      {label && <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>{label}</label>}
      <button type="button" onClick={() => { setOpen(o => !o); setMode('day'); }}
        style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:9, padding:'9px 12px', fontSize:13, textAlign:'left', display:'flex', alignItems:'center', justifyContent:'space-between', background:T.inputBg, cursor:'pointer', outline:'none', boxSizing:'border-box' }}>
        <span style={{ color: displayVal ? T.text : T.text3 }}>{displayVal || placeholder}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
      </button>
      {open && (
        <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:12, boxShadow: darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 12px 40px rgba(15,31,69,0.12)', zIndex:9999, width:288, overflow:'hidden' }}>
          {/* Header */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', borderBottom:`1px solid ${T.border}` }}>
            <button type="button" onClick={() => { if(mode==='day') setViewMonth(m => { if(m===0){setViewYear(y=>y-1);return 11;} return m-1; }); else if(mode==='year') setViewYear(y=>y-12); }}
              style={{ width:28, height:28, borderRadius:8, border:'none', background:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:T.text2 }}
              onMouseEnter={e => e.currentTarget.style.background=T.bg3}
              onMouseLeave={e => e.currentTarget.style.background='none'}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <div style={{ display:'flex', alignItems:'center', gap:4 }}>
              <button type="button" onClick={() => setMode(m => m==='month'?'day':'month')}
                style={{ padding:'4px 8px', borderRadius:8, border:'none', background:'none', fontSize:13, fontWeight:700, color:T.text, cursor:'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background=T.bg3}
                onMouseLeave={e => e.currentTarget.style.background='none'}>
                {MONTHS[viewMonth]}
              </button>
              <button type="button" onClick={() => setMode(m => m==='year'?'day':'year')}
                style={{ padding:'4px 8px', borderRadius:8, border:'none', background:'none', fontSize:13, fontWeight:700, color:T.text, cursor:'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background=T.bg3}
                onMouseLeave={e => e.currentTarget.style.background='none'}>
                {viewYear}
              </button>
            </div>
            <button type="button" onClick={() => { if(mode==='day') setViewMonth(m => { if(m===11){setViewYear(y=>y+1);return 0;} return m+1; }); else if(mode==='year') setViewYear(y=>y+12); }}
              style={{ width:28, height:28, borderRadius:8, border:'none', background:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:T.text2 }}
              onMouseEnter={e => e.currentTarget.style.background=T.bg3}
              onMouseLeave={e => e.currentTarget.style.background='none'}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>

          {/* Month picker */}
          {mode==='month' && (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:4, padding:12 }}>
              {MONTHS.map((m,i) => (
                <button key={m} type="button" onClick={() => { setViewMonth(i); setMode('day'); }}
                  style={{ padding:'8px 0', borderRadius:8, border:'none', fontSize:12, fontWeight:600, cursor:'pointer', background: viewMonth===i?'linear-gradient(135deg,#0f1f45,#1a3a6b)':'none', color: viewMonth===i?'#fff':T.text2, transition:'all 0.15s' }}
                  onMouseEnter={e => { if(viewMonth!==i) e.currentTarget.style.background=T.bg3; }}
                  onMouseLeave={e => { if(viewMonth!==i) e.currentTarget.style.background='none'; }}>
                  {m}
                </button>
              ))}
            </div>
          )}

          {/* Year picker */}
          {mode==='year' && (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:4, padding:12 }}>
              {yearRange.map(y => (
                <button key={y} type="button" onClick={() => { setViewYear(y); setMode('day'); }}
                  style={{ padding:'8px 0', borderRadius:8, border:'none', fontSize:12, fontWeight:600, cursor:'pointer', background: viewYear===y?'linear-gradient(135deg,#0f1f45,#1a3a6b)':'none', color: viewYear===y?'#fff':T.text2, transition:'all 0.15s' }}
                  onMouseEnter={e => { if(viewYear!==y) e.currentTarget.style.background=T.bg3; }}
                  onMouseLeave={e => { if(viewYear!==y) e.currentTarget.style.background='none'; }}>
                  {y}
                </button>
              ))}
            </div>
          )}

          {/* Day picker */}
          {mode==='day' && (
            <div style={{ padding:12 }}>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', marginBottom:4 }}>
                {DAYS.map(d => <div key={d} style={{ textAlign:'center', fontSize:11, fontWeight:600, color:T.text3, padding:'4px 0' }}>{d}</div>)}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)' }}>
                {getDays().map((cell,i) => {
                  const cellDate = new Date(viewYear, viewMonth, cell.d); cellDate.setHours(0,0,0,0);
                  const isSel = selected && cell.curr && cellDate.getTime()===selected.getTime();
                  const isToday = cell.curr && cellDate.getTime()===today.getTime();
                  return (
                    <button key={i} type="button" onClick={() => cell.curr && selectDay(cell.d)}
                      style={{
                        height:32, width:'100%', borderRadius:8, border: isToday&&!isSel?`1px solid #3b82f6`:'none',
                        background: isSel?'linear-gradient(135deg,#0f1f45,#1a3a6b)':'none',
                        color: !cell.curr?T.text3 : isSel?'#fff' : isToday?'#3b82f6' : T.text2,
                        fontSize:12, fontWeight: isSel||isToday?700:400,
                        cursor: cell.curr?'pointer':'default', transition:'all 0.1s',
                      }}
                      onMouseEnter={e => { if(cell.curr&&!isSel) e.currentTarget.style.background=T.bg3; }}
                      onMouseLeave={e => { if(!isSel) e.currentTarget.style.background='none'; }}>
                      {cell.d}
                    </button>
                  );
                })}
              </div>
              {value && (
                <button type="button" onClick={() => { onChange(''); setOpen(false); }}
                  style={{ width:'100%', marginTop:8, padding:'6px 0', fontSize:12, color:T.text3, background:'none', border:'none', cursor:'pointer', borderRadius:8 }}
                  onMouseEnter={e => { e.currentTarget.style.color='#ef4444'; e.currentTarget.style.background=darkMode?'rgba(239,68,68,0.1)':'#fef2f2'; }}
                  onMouseLeave={e => { e.currentTarget.style.color=T.text3; e.currentTarget.style.background='none'; }}>
                  Clear date
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Lightweight toast system — replaces native alert() for save/delete/error feedback.
function useToasts() {
  const [toasts, setToasts] = useState([]);
  const showToast = (message, type = 'success') => {
    const id = Date.now() + Math.random();
    setToasts(t => [...t, { id, message, type }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 4000);
  };
  return [toasts, showToast];
}

function ToastStack({ toasts }) {
  if (!toasts || !toasts.length) return null;
  return (
    <div style={{ position:'fixed', top:20, right:20, zIndex:100000, display:'flex', flexDirection:'column', gap:8 }}>
      {toasts.map(t => (
        <div key={t.id} style={{
          minWidth:260, maxWidth:380, padding:'12px 16px', borderRadius:10,
          background: t.type==='error' ? '#fef2f2' : '#f0fdf4',
          border: `1px solid ${t.type==='error' ? '#fecaca' : '#bbf7d0'}`,
          color: t.type==='error' ? '#991b1b' : '#166534',
          fontSize:13, fontWeight:500, boxShadow:'0 8px 24px rgba(0,0,0,0.16)',
          display:'flex', alignItems:'center', gap:8,
        }}>
          {t.type==='error' ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" style={{flexShrink:0}}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" style={{flexShrink:0}}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          )}
          <span>{t.message}</span>
        </div>
      ))}
    </div>
  );
}

function ConfirmModal({ open, title, message, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:99999, padding:16 }}>
      <div style={{ background:'#fff', borderRadius:16, width:'min(92vw,420px)', overflow:'hidden', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ padding:'20px 24px', borderBottom:'1px solid #f1f5f9' }}>
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:8 }}>
            <div style={{ width:36, height:36, borderRadius:10, background:'#fef2f2', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
            </div>
            <h3 style={{ fontSize:15, fontWeight:700, color:'#0f1f45', margin:0 }}>{title}</h3>
          </div>
          <p style={{ fontSize:13, color:'#64748b', margin:0, lineHeight:1.6 }}>{message}</p>
        </div>
        <div style={{ display:'flex', gap:10, padding:'16px 24px' }}>
          <button onClick={onCancel} style={{ flex:1, padding:'9px 0', borderRadius:9, border:'1px solid #e2e8f0', background:'#f8fafc', color:'#4a5568', fontSize:13, fontWeight:600, cursor:'pointer' }}>Cancel</button>
          <button onClick={onConfirm} style={{ flex:1, padding:'9px 0', borderRadius:9, border:'none', background:'#ef4444', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>Delete</button>
        </div>
      </div>
    </div>
  );
}

function Clients({ darkMode = false, T: theme, initialClientId }) {
  const T = theme || { bg:'#f1f5f9', bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [confirm, setConfirm] = useState(null);
  const showConfirm = (title, message, onConfirm) => setConfirm({ title, message, onConfirm });
  const [toasts, showToast] = useToasts();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [editClient, setEditClient] = useState(null);
  const [returnToDashboardAfterEdit, setReturnToDashboardAfterEdit] = useState(false);
  // Set only when this dashboard was deep-linked from a specific task (e.g. the
  // Client/File Number link on a task's Details tab) — captured once at mount,
  // not re-read from localStorage in onClose, so it can never leak into an
  // unrelated dashboard opened later by clicking a normal client row.
  const [dashboardReturnTaskId, setDashboardReturnTaskId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const cc = useColumnCustomizer(CLIENT_COLUMNS, { visKey: 'clientVisibleColumns', orderKey: 'clientColumnOrder', T, darkMode });
  const { orderedVisibleKeys } = cc;

  const [openKebabId, setOpenKebabId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignClient, setAssignClient] = useState(null);
  const [tagsClient, setTagsClient] = useState(null);
  const [allTags, setAllTags] = useState([]);
  const [clientTypes, setClientTypes] = useState([]);
  const [filterGroups, setFilterGroups] = useState([]);
  const [filterUsers, setFilterUsers] = useState([]);
  const [filterServices, setFilterServices] = useState([]);
  const [filterGroup, setFilterGroup] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [filterService, setFilterService] = useState('');
  const [filterClientId, setFilterClientId] = useState('');
  const [filterBillingProfile, setFilterBillingProfile] = useState('');
  const token = localStorage.getItem('token');

  // Close kebab on outside click
  useEffect(() => {
    const h = (e) => {
      if (!e.target.closest('[data-kebab]')) setOpenKebabId(null);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const fetchClients = async () => {
    try {
      const res = await fetch(`${BASE}/api/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setClients(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchTags = () => {
    fetch(`${BASE}/api/clients/tags/all`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setAllTags(Array.isArray(d) ? d : [])).catch(() => {});
  };

  const fetchTypes = () => {
    fetch(`${BASE}/api/clients/types/all`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setClientTypes(Array.isArray(d) ? d : [])).catch(() => {});
  };

  const fetchFilterGroups = () => {
    fetch(`${BASE}/api/clients/groups/all`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setFilterGroups(Array.isArray(d) ? d : [])).catch(() => {});
  };

  const fetchFilterUsers = () => {
    fetch(`${BASE}/api/users`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setFilterUsers(Array.isArray(d) ? d : [])).catch(() => {});
  };

  const fetchFilterServices = () => {
    fetch(`${BASE}/api/services`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setFilterServices(Array.isArray(d) ? d : [])).catch(() => {});
  };

  useEffect(() => {
    fetchTags();
    fetchTypes();
    fetchFilterGroups();
    fetchFilterUsers();
    fetchFilterServices();
    // Deep-linking straight to one client (below) shouldn't have to queue
    // behind this — it's the full firm client list with several joins/
    // aggregates per row, the heaviest query this page makes. Skip it here
    // and load it lazily once the user actually lands back on the list, so
    // the single-client fetch isn't left waiting on a DB connection behind it.
    if (!initialClientId) fetchClients();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep-link from the global "Search Clients" bar (or any other module) —
  // jump straight to this client's dashboard instead of the list.
  const [openingClient, setOpeningClient] = useState(!!initialClientId);
  useEffect(() => {
    if (!initialClientId) return;
    setOpeningClient(true);
    localStorage.removeItem('navClientId');
    const returnTaskId = localStorage.getItem('clientDashboardReturnTaskId');
    localStorage.removeItem('clientDashboardReturnTaskId');
    if (returnTaskId) setDashboardReturnTaskId(returnTaskId);
    fetch(`${BASE}/api/clients/${initialClientId}`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(c => { if (c) setSelectedClient(c); })
      .catch(() => {})
      .finally(() => setOpeningClient(false));
  }, [initialClientId]);



  const filtered = clients.filter(c => {
    const matchSearch = !searchTerm ||
      c.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.mobile?.includes(searchTerm) ||
      c.pan?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.gstin?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.file_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchType = !filterType || c.client_type === filterType;
    const matchStatus = !filterStatus || String(c.is_active) === filterStatus;
    const matchDateFrom = !filterDateFrom || new Date(c.created_at) >= new Date(filterDateFrom);
    const matchDateTo = !filterDateTo || new Date(c.created_at) <= new Date(filterDateTo + 'T23:59:59');
    const matchGroup = !filterGroup || String(c.group_id) === filterGroup;
    const matchTag = !filterTag || (c.tags_data || '').split(',').some(td => td.split(':')[0] === filterTag);
    const matchUser = !filterUser || (c.assigned_users_data || '').split(',').some(ud => ud.split(':')[0] === filterUser);
    const matchService = !filterService || (c.recurring_service_ids || '').split(',').includes(filterService);
    const matchClientId = !filterClientId || String(c.id) === filterClientId;
    const matchBillingProfile = !filterBillingProfile || c.billing_profile === filterBillingProfile;
    return matchSearch && matchType && matchStatus && matchDateFrom && matchDateTo
      && matchGroup && matchTag && matchUser && matchService && matchClientId && matchBillingProfile;
  });

  const billingProfileOptions = [...new Set(clients.map(c => c.billing_profile).filter(Boolean))].sort();

  const [showImport,     setShowImport]     = useState(false);
  const [importRows,     setImportRows]     = useState([]);
  const [importResult,   setImportResult]   = useState(null);
  const [importing,      setImporting]      = useState(false);
  const [importProgress, setImportProgress] = useState(null); // { current, total, phase }
  const importFileRef = useRef(null);

  const downloadClientTemplate = async () => {
    try {
      const [tagsRes, groupsRes, usersRes] = await Promise.all([
        fetch(`${BASE}/api/clients/tags/all`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${BASE}/api/clients/groups/all`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${BASE}/api/users`, { headers: { 'Authorization': `Bearer ${token}` } }),
      ]);
      const [tagsData, groupsData, usersData] = await Promise.all([tagsRes.json(), groupsRes.json(), usersRes.json()]);
      const tagNames = (Array.isArray(tagsData) ? tagsData : []).map(t => t.name);
      const groupNames = (Array.isArray(groupsData) ? groupsData : []).map(g => g.name);
      const userNames = (Array.isArray(usersData) ? usersData : []).map(u => u.full_name);

      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('Clients');
      const columns = [
        { header: 'File No.', key: 'file_number', width: 16 },
        { header: 'Client Name*', key: 'client_name', width: 28 },
        { header: 'Client Type*', key: 'client_type', width: 18 },
        { header: 'PAN', key: 'pan', width: 14 },
        { header: 'GSTIN', key: 'gstin', width: 18 },
        { header: 'Mobile', key: 'mobile', width: 14 },
        { header: 'Secondary Mobile', key: 'secondary_mobile', width: 16 },
        { header: 'Email', key: 'email', width: 22 },
        { header: 'Contact Person', key: 'contact_person', width: 18 },
        { header: 'Date of Birth / Incorporation', key: 'date_of_birth', width: 26 },
        { header: 'Address', key: 'address', width: 26 },
        { header: 'City', key: 'city', width: 16 },
        { header: 'State', key: 'state', width: 18 },
        { header: 'Pincode', key: 'pincode', width: 10 },
        { header: 'Group', key: 'group', width: 20 },
        { header: 'Tags', key: 'tags', width: 24 },
        { header: 'Assigned Users', key: 'assigned_users', width: 26 },
        { header: 'Status', key: 'status', width: 12 },
        { header: 'Opening Balance', key: 'opening_balance', width: 16 },
        { header: 'Notes', key: 'notes', width: 26 },
      ];
      ws.columns = columns;
      ws.getRow(1).font = { bold: true };
      ws.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F1F45' } };
      ws.getRow(1).eachCell(c => { c.font = { bold: true, color: { argb: 'FFFFFFFF' } }; });
      ws.getColumn('date_of_birth').numFmt = '@'; // keep dates as plain text, don't let Excel reformat
      ws.getCell(`${ws.getColumn('date_of_birth').letter}1`).note =
        'Any common format works: DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY, YYYY-MM-DD (2-digit year OK).';

      ws.addRow({
        client_name: 'ABC Private Limited', client_type: 'Private Limited',
        pan: 'AABCA1234A', gstin: '09AABCA1234A1Z5', mobile: '9876543210',
        email: 'info@abc.com', date_of_birth: '01/04/2015',
        address: '12, MG Road', city: 'Lucknow',
        state: 'Uttar Pradesh', pincode: '226001',
        group: groupNames[0] || '', tags: tagNames.slice(0, 2).join(', '),
        assigned_users: userNames.slice(0, 1).join(', '), status: 'Active',
        opening_balance: 0, notes: 'Sample row — replace or delete',
      });

      // Reference lists on a hidden sheet so "List" data validation can
      // point at a real range (inline lists are capped ~255 chars, too
      // short for a firm's full tag/group list).
      const refWs = wb.addWorksheet('Lists');
      refWs.state = 'veryHidden';
      const typeLabels = (clientTypes.length ? clientTypes.map(t => t.name) : CLIENT_TYPE_OPTIONS.map(o => o.label));
      typeLabels.forEach((v, i) => { refWs.getCell(i + 1, 1).value = v; });
      ['Active', 'Inactive'].forEach((v, i) => { refWs.getCell(i + 1, 2).value = v; });
      (groupNames.length ? groupNames : ['']).forEach((v, i) => { refWs.getCell(i + 1, 3).value = v; });
      (tagNames.length ? tagNames : ['']).forEach((v, i) => { refWs.getCell(i + 1, 4).value = v; });
      INDIAN_STATES.forEach((v, i) => { refWs.getCell(i + 1, 5).value = v; });

      const LAST_ROW = 501; // template supports up to 500 data rows
      const applyList = (key, refCol, refLen, { strict = true } = {}) => {
        const letter = ws.getColumn(key).letter;
        const formula = `Lists!$${refCol}$1:$${refCol}$${Math.max(refLen, 1)}`;
        for (let r = 2; r <= LAST_ROW; r++) {
          ws.getCell(`${letter}${r}`).dataValidation = {
            type: 'list', allowBlank: true, formulae: [formula],
            showErrorMessage: strict, errorStyle: strict ? 'error' : 'warning',
            error: strict ? 'Please pick a value from the dropdown.' : 'Not in the existing list — a new one will be added on import.',
          };
        }
      };
      applyList('client_type', 'A', typeLabels.length, { strict: false });
      applyList('status', 'B', 2, { strict: true });
      applyList('group', 'C', Math.max(groupNames.length, 1), { strict: false });
      applyList('tags', 'D', Math.max(tagNames.length, 1), { strict: false });
      applyList('state', 'E', INDIAN_STATES.length, { strict: false });

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'Client_Import_Template.xlsx';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { showToast('Could not generate template', 'error'); }
  };

  const handleImportFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const wb = XLSX.read(evt.target.result, { type: 'binary', cellDates: true });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(ws, { defval: '' });
      const normalized = data.map(row => {
        const out = {};
        Object.entries(row).forEach(([k, v]) => {
          const cleanLabel = k.replace(/\s*\([^)]*\)/g, '').replace(/\*/g, '').trim().toLowerCase();
          const field = IMPORT_HEADER_MAP[cleanLabel] || cleanLabel.replace(/\s+/g, '_');
          if (field === 'date_of_birth') {
            const iso = toISODate(v);
            out.date_of_birth = iso;
            const rawStr = v instanceof Date ? '' : String(v).trim();
            if (!iso && rawStr) out.date_of_birth_raw = rawStr; // couldn't parse — keep for the warning
          } else {
            out[field] = String(v).trim();
          }
        });
        return out;
      });
      setImportRows(normalized);
      setImportResult(null);
    };
    reader.readAsBinaryString(file);
  };

  const handleImportSubmit = async () => {
    if (!importRows.length) return;
    setImporting(true);
    setImportResult(null);
    setImportProgress({ current: 0, total: importRows.length, phase: 'preparing' });
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const jsonHeaders = { 'Content-Type': 'application/json', ...headers };

      const [groupsRes, tagsRes, usersRes, typesRes] = await Promise.all([
        fetch(`${BASE}/api/clients/groups/all`, { headers }),
        fetch(`${BASE}/api/clients/tags/all`, { headers }),
        fetch(`${BASE}/api/users`, { headers }),
        fetch(`${BASE}/api/clients/types/all`, { headers }),
      ]);
      const [groupsData, tagsData, usersData, typesData] = await Promise.all([groupsRes.json(), tagsRes.json(), usersRes.json(), typesRes.json()]);
      const groupMap = new Map((Array.isArray(groupsData) ? groupsData : []).map(g => [g.name.toLowerCase(), g.id]));
      const tagMap = new Map((Array.isArray(tagsData) ? tagsData : []).map(t => [t.name.toLowerCase(), t.id]));
      const userMap = new Map((Array.isArray(usersData) ? usersData : []).map(u => [u.full_name.toLowerCase(), u.id]));
      // type lookup: match by slug value OR display name (case-insensitive)
      const typeMap = new Map();
      (Array.isArray(typesData) ? typesData : []).forEach(t => { typeMap.set(t.value.toLowerCase(), t.value); typeMap.set(t.name.toLowerCase(), t.value); });

      const resolveType = async (raw) => {
        const s = String(raw || '').trim();
        if (!s) return 'other';
        const hit = typeMap.get(s.toLowerCase());
        if (hit) return hit;
        const known = normalizeClientType(s);
        if (known) { typeMap.set(s.toLowerCase(), known); return known; }
        // brand-new type name → create it
        const res = await fetch(`${BASE}/api/clients/types`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ name: s }) });
        const d = await res.json().catch(() => null);
        if (d?.value) { typeMap.set(s.toLowerCase(), d.value); typeMap.set(d.value.toLowerCase(), d.value); return d.value; }
        return 'other';
      };

      const resolveGroup = async (name) => {
        const key = name.trim().toLowerCase();
        if (groupMap.has(key)) return groupMap.get(key);
        const res = await fetch(`${BASE}/api/clients/groups`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ name: name.trim() }) });
        const d = await res.json().catch(() => null);
        if (d?.id) { groupMap.set(key, d.id); return d.id; }
        return null;
      };
      const resolveTag = async (name) => {
        const key = name.trim().toLowerCase();
        if (tagMap.has(key)) return tagMap.get(key);
        const res = await fetch(`${BASE}/api/clients/tags`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ name: name.trim() }) });
        const d = await res.json().catch(() => null);
        if (d?.id) { tagMap.set(key, d.id); return d.id; }
        return null;
      };

      let success = 0;
      let skipped = 0;
      const errors = [];
      const warnings = [];
      const seen = new Map(); // normalised key -> row number, to catch dupes within the file itself
      const dupKey = (r) => [
        (r.client_name || '').replace(/\s+/g, ' ').trim().toLowerCase(),
        (r.pan || '').replace(/\s+/g, '').toUpperCase(),
        (r.gstin || '').replace(/\s+/g, '').toUpperCase(),
      ];
      const tick = (i) => setImportProgress({
        current: i + 1, total: importRows.length, phase: 'importing',
        ok: success, skip: skipped, fail: errors.length,
      });
      for (const [i, row] of importRows.entries()) {
        const rowNum = i + 2;
        if (!row.client_name) { errors.push(`Row ${rowNum}: client_name is required`); tick(i); continue; }

        // Duplicate of an earlier row in this same file?
        const [kName, kPan, kGstin] = dupKey(row);
        const selfDup = (kPan && seen.get('pan:' + kPan)) || (kGstin && seen.get('gstin:' + kGstin)) || (kName && seen.get('name:' + kName));
        if (selfDup) {
          skipped++;
          warnings.push(`Row ${rowNum}: "${row.client_name}" duplicates row ${selfDup} in this file — skipped`);
          tick(i);
          continue;
        }

        try {
          let group_id = null;
          if (row.group_name) group_id = await resolveGroup(row.group_name);

          let tag_ids = [];
          if (row.tags) {
            for (const raw of row.tags.split(',').map(t => t.trim()).filter(Boolean)) {
              const id = await resolveTag(raw);
              if (id) tag_ids.push(id);
            }
          }

          let assigned_users = [];
          if (row.assigned_users) {
            for (const raw of row.assigned_users.split(',').map(u => u.trim()).filter(Boolean)) {
              const id = userMap.get(raw.toLowerCase());
              if (id) assigned_users.push(id);
              else warnings.push(`Row ${rowNum}: user "${raw}" not found, skipped`);
            }
          }

          const statusText = (row.status || '').trim().toLowerCase();
          const is_active = statusText === 'inactive' ? false : true;

          if (row.date_of_birth_raw) warnings.push(`Row ${rowNum}: couldn't read date "${row.date_of_birth_raw}", left blank`);

          const client_type = await resolveType(row.client_type);

          const res = await fetch(`${BASE}/api/clients`, {
            method: 'POST',
            headers: jsonHeaders,
            body: JSON.stringify({
              file_number: row.file_number || null,
              client_name: row.client_name,
              client_type,
              pan: row.pan || null,
              gstin: row.gstin || null,
              mobile: row.mobile || null,
              secondary_mobile: row.secondary_mobile || null,
              email: row.email || null,
              contact_person: row.contact_person || null,
              address: row.address || null,
              city: row.city || null,
              state: row.state || null,
              pincode: row.pincode || null,
              group_id, tag_ids, assigned_users, is_active,
              constitution: row.constitution || null,
              date_of_birth: row.date_of_birth || null,
              opening_balance: parseFloat(row.opening_balance) || 0,
              notes: row.notes || null,
            })
          });
          if (res.ok) {
            success++;
            if (kName) seen.set('name:' + kName, rowNum);
            if (kPan) seen.set('pan:' + kPan, rowNum);
            if (kGstin) seen.set('gstin:' + kGstin, rowNum);
          } else if (res.status === 409) {
            skipped++;
            const d = await res.json().catch(() => ({}));
            const match = (d.duplicates && d.duplicates[0]) ? ` (already exists as ${d.duplicates[0].file_number || d.duplicates[0].client_name})` : '';
            warnings.push(`Row ${rowNum}: "${row.client_name}" looks like a duplicate${match} — skipped`);
          } else {
            const d = await res.json().catch(() => ({}));
            errors.push(`Row ${rowNum}: ${d.message || 'failed'}`);
          }
        } catch (e) { errors.push(`Row ${rowNum}: ${e.message}`); }
        tick(i);
      }
      setImportResult({ success, skipped, errors, warnings, total: importRows.length });
      if (success > 0) fetchClients();
    } finally { setImporting(false); setImportProgress(null); }
  };

  const exportToXLSX = () => {
    const exportData = filtered.map(c => ({
      'File No.': c.file_number || '',
      'Client Name': c.client_name || '',
      'Type': clientTypeLabel(c.client_type),
      'PAN': c.pan || '',
      'GSTIN': c.gstin || '',
      'Mobile': c.mobile || '',
      'Email': c.email || '',
      'City': c.city || '',
      'State': c.state || '',
      'Group': c.group_name || '',
      'Assigned Users': c.assigned_users || '',
      'Status': c.is_active ? 'Active' : 'Inactive',
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    ws['!cols'] = Object.keys(exportData[0] || {}).map(k => ({ wch: Math.max(k.length + 2, 18) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Clients');
    XLSX.writeFile(wb, `ChanakyaX_Clients_${new Date().toLocaleDateString('en-IN').replace(/\//g, '-')}.xlsx`);
  };

  const handleDeleteClient = (client) => {
    showConfirm('Delete Client', `Delete client "${client.client_name}"? This cannot be undone.`, async () => {
      try {
        const res = await fetch(`${BASE}/api/clients/${client.id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) { fetchClients(); showToast('Client deleted successfully.', 'success'); }
        else { const d = await res.json(); showToast(d.message || 'Could not delete client', 'error'); }
      } catch (e) { showToast('Server error. Could not delete client.', 'error'); }
    });
  };

  const toggleSelectAll = () => {
    const visibleIds = filtered.map(c => c.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.includes(id));
    setSelectedIds(allSelected ? [] : visibleIds);
  };

  const handleBulkDelete = () => {
    if (!selectedIds.length) return;
    showConfirm(
      'Delete Selected Clients',
      `Delete ${selectedIds.length} selected client${selectedIds.length > 1 ? 's' : ''}? Clients with tasks, invoices, services, retainers or other activity on record will be skipped automatically.`,
      async () => {
        setBulkDeleting(true);
        try {
          const res = await fetch(`${BASE}/api/clients/bulk-delete`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ ids: selectedIds })
          });
          if (res.ok) {
            const { deleted, blocked } = await res.json();
            if (deleted.length) fetchClients();
            setSelectedIds([]);
            if (blocked.length) {
              showToast(
                `Deleted ${deleted.length} client${deleted.length === 1 ? '' : 's'}. Skipped ${blocked.length}: ${blocked.map(b => `"${b.name}" (${b.reason})`).join('; ')}`,
                deleted.length ? 'error' : 'error'
              );
            } else {
              showToast(`Deleted ${deleted.length} client${deleted.length === 1 ? '' : 's'} successfully.`, 'success');
            }
          } else {
            const d = await res.json();
            showToast(d.message || 'Could not delete clients', 'error');
          }
        } catch (e) { showToast('Server error. Could not delete clients.', 'error'); }
        finally { setBulkDeleting(false); }
      }
    );
  };

  const clientTypeLabel = (type) => typeLabelFor(type, clientTypes);
    const renderClientCell = (client, key) => {
if (key === 'file_number') {
        return (
          <button onClick={() => setSelectedClient(client)}
            style={{ background:'none', border:'none', cursor:'pointer', fontSize:12, fontWeight:600, color: darkMode?'#93c5fd':'#2563eb', whiteSpace:'nowrap' }}
            onMouseEnter={e => e.currentTarget.style.textDecoration='underline'}
            onMouseLeave={e => e.currentTarget.style.textDecoration='none'}>
            {client.file_number || '-'}
          </button>
        );
      }
      if (key === 'client_name') {
        return (
          <button onClick={() => setSelectedClient(client)}
            style={{ background:'none', border:'none', cursor:'pointer', textAlign:'left', maxWidth:256, display:'block' }}
            onMouseEnter={e => e.currentTarget.style.color='#f97316'}
            onMouseLeave={e => e.currentTarget.style.color= darkMode?'#f1f5f9':'#0f1f45'}>
            <span style={{ fontSize:13, fontWeight:600, color: darkMode?'#f1f5f9':'#0f1f45', whiteSpace:'nowrap' }}>
              {client.client_name || '-'}
            </span>
          </button>
        );
      }

      if (key === 'tags') {
        return client.tags ? (
          <div className="flex flex-nowrap gap-1">
            {client.tags.split(',').slice(0, 2).map((tag, i) => (
              <span key={i} className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full text-xs whitespace-nowrap">{tag}</span>
            ))}
          </div>
        ) : '-';
      }

      if (key === 'client_type') return clientTypeLabel(client.client_type);
      if (key === 'gstin') return client.gstin ? client.gstin.substring(0, 15) : '-';

      if (key === 'assigned_users') {
        if (!client.assigned_users_data && !client.assigned_users) return <span style={{ color:T.text3, fontSize:12 }}>—</span>;
        // id:name:avatar_url triples when available (assigned_users_data), else fall back to plain names
        const users = client.assigned_users_data
          ? client.assigned_users_data.split(',').map(t => { const [id,name,avatar] = t.split(':'); return { id, name, avatar }; }).filter(u => u.name)
          : client.assigned_users.split(',').map(n => ({ name: n.trim() })).filter(u => u.name);
        const COLORS = ['#0f1f45','#3b82f6','#8b5cf6','#10b981','#f59e0b','#ef4444'];
        const visible = users.slice(0, 3);
        const extra   = users.length - 3;
        return (
          <div style={{ display:'flex', alignItems:'center' }}>
            {visible.map((u, i) => (
              <div key={i} title={u.name} style={{
                width: 28, height: 28, borderRadius:'50%',
                background: u.avatar ? 'transparent' : `linear-gradient(135deg, ${COLORS[i % COLORS.length]}, ${COLORS[(i+1) % COLORS.length]})`,
                border: `2px solid ${darkMode?'#0f172a':'#fff'}`,
                marginLeft: i === 0 ? 0 : -8,
                display:'flex', alignItems:'center', justifyContent:'center',
                color:'#fff', fontSize:11, fontWeight:700,
                zIndex: visible.length - i, position:'relative',
                flexShrink:0, cursor:'default', overflow:'hidden',
                boxShadow:'0 1px 3px rgba(0,0,0,0.2)',
              }}>
                {u.avatar
                  ? <img src={`${BASE}${u.avatar}`} alt={u.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                  : u.name.charAt(0).toUpperCase()}
              </div>
            ))}
            {extra > 0 && (
              <div title={users.slice(3).map(u => u.name).join(', ')} style={{
                width:28, height:28, borderRadius:'50%',
                background: darkMode?'#334155':'#e2e8f0',
                border:`2px solid ${darkMode?'#0f172a':'#fff'}`,
                marginLeft:-8, display:'flex', alignItems:'center',
                justifyContent:'center', fontSize:10, fontWeight:700,
                color: darkMode?'#94a3b8':'#64748b', cursor:'default',
                position:'relative', zIndex:0, flexShrink:0,
              }}>
                +{extra}
              </div>
            )}
          </div>
        );
      }

      if (key === 'mobile') {
        return (
          <div className="flex items-center gap-2 whitespace-nowrap">
            <span style={{ fontSize:13, color: darkMode?'#cbd5e1':'#374151' }}>{client.mobile || '-'}</span>
            {client.mobile && (
              <>
                {/* WhatsApp */}
                <a href={`https://wa.me/91${client.mobile}`} target="_blank" rel="noreferrer"
                  title="Send WhatsApp message"
                  className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-green-50 transition flex-shrink-0">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="#25D366">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </a>
                {/* Call */}
                <a href={`tel:+91${client.mobile}`} title="Call client"
                  className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-blue-50 transition flex-shrink-0">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.96a16 16 0 0 0 6.09 6.09l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                </a>
              </>
            )}
          </div>
        );
      }

      if (key === 'is_active') {
        const active = client.is_active;
        return (
          <span style={{
            display:'inline-flex', alignItems:'center', gap:6,
            padding:'4px 10px', borderRadius:6,
            background: active
              ? (darkMode?'rgba(16,185,129,0.1)':'rgba(16,185,129,0.08)')
              : (darkMode?'rgba(239,68,68,0.1)':'rgba(239,68,68,0.08)'),
            border: `1px solid ${active?(darkMode?'rgba(16,185,129,0.3)':'rgba(16,185,129,0.25)'):(darkMode?'rgba(239,68,68,0.3)':'rgba(239,68,68,0.25)')}`,
          }}>
            {/* Pulsing dot */}
            <span style={{ position:'relative', width:7, height:7, flexShrink:0 }}>
              <span style={{
                position:'absolute', inset:0, borderRadius:'50%',
                background: active?'#10b981':'#ef4444',
                animation: active?'cx-pulse 2s ease-in-out infinite':'none',
              }}/>
              <span style={{
                position:'absolute', inset:0, borderRadius:'50%',
                background: active?'#10b981':'#ef4444',
              }}/>
            </span>
            <span style={{
              fontSize:12, fontWeight:600, letterSpacing:'0.02em',
              color: active?(darkMode?'#34d399':'#059669'):(darkMode?'#f87171':'#dc2626'),
            }}>
              {active ? 'Active' : 'Inactive'}
            </span>
          </span>
        );
      }

      return client[key] || '-';
    };

  if (!document.getElementById('cx-styles-v4')) {
    document.getElementById('cx-styles')?.remove();
    document.getElementById('cx-styles-v3')?.remove();
    const s = document.createElement('style');
    s.id = 'cx-styles-v4';
    s.textContent = `
      @keyframes cx-pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.4;transform:scale(1.8)} }
      @keyframes cx-stripe { from { background-position: 0 0; } to { background-position: 28px 0; } }
      @keyframes cx-spin { to { transform: rotate(360deg); } }
      .cx-noscroll { scrollbar-width: none; -ms-overflow-style: none; }
      .cx-noscroll::-webkit-scrollbar { display: none; width: 0; height: 0; }
    `;
    document.head.appendChild(s);
  }

  return (
    <div style={{
      height:'100vh', padding:24, display:'flex', flexDirection:'column', overflow:'hidden', boxSizing:'border-box',
      background: darkMode
        ? 'radial-gradient(ellipse at 80% 0%,rgba(249,115,22,0.07) 0%,transparent 60%),radial-gradient(ellipse at 20% 100%,rgba(15,31,69,0.4) 0%,transparent 60%),#0a0f1e'
        : 'radial-gradient(ellipse at 80% 0%,rgba(249,115,22,0.05) 0%,transparent 50%),radial-gradient(ellipse at 0% 100%,rgba(15,31,69,0.04) 0%,transparent 50%),#f4f7ff',
    }}>
      <ConfirmModal open={!!confirm} title={confirm?.title||''} message={confirm?.message||''} onConfirm={()=>{confirm?.onConfirm();setConfirm(null);}} onCancel={()=>setConfirm(null)} />
      <ToastStack toasts={toasts} />
      {/* Instant feedback for a deep-link straight into one client's dashboard
          (global search, etc.) — shows immediately on click, before the
          single-client fetch resolves, so the jump never feels stalled. */}
      {openingClient && !selectedClient && (
        <div style={{
          position:'fixed', inset:0, zIndex:70, display:'flex', alignItems:'center', justifyContent:'center',
          background: darkMode ? 'rgba(10,15,30,0.6)' : 'rgba(244,247,255,0.7)', backdropFilter:'blur(2px)',
        }}>
          <div style={{
            display:'flex', alignItems:'center', gap:12, padding:'16px 22px', borderRadius:14,
            background:T.bg2, border:`1px solid ${T.border2}`,
            boxShadow: darkMode ? '0 12px 40px rgba(0,0,0,0.5)' : '0 12px 40px rgba(15,31,69,0.14)',
          }}>
            <div style={{
              width:18, height:18, borderRadius:'50%', flexShrink:0,
              border:`2.5px solid ${darkMode ? 'rgba(59,130,246,0.25)' : '#dbeafe'}`, borderTopColor:'#3b82f6',
              animation:'cx-spin 0.7s linear infinite',
            }} />
            <span style={{ fontSize:13, fontWeight:600, color:T.text }}>Opening client…</span>
          </div>
        </div>
      )}
      {/* Header */}
      <div style={{ marginBottom:20 }}>
        {/* Stats Row */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:16 }}>
          {[
            {
              label: 'Total Clients',
              value: clients.length,
              icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={darkMode?'#e2e8f0':'#0f1f45'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
              iconBg: darkMode?'rgba(255,255,255,0.08)':'#e8edf5',
              val: darkMode?'#e2e8f0':'#0f1f45',
              border: darkMode?'rgba(255,255,255,0.06)':'#e2e8f0',
              bg: darkMode?'rgba(255,255,255,0.03)':'#fff',
              glow: 'rgba(15,31,69,0.08)',
            },
            {
              label: 'Active',
              value: clients.filter(c => c.is_active !== false).length,
              icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
              iconBg: darkMode?'rgba(16,185,129,0.15)':'#f0fdf4',
              val: darkMode?'#34d399':'#16a34a',
              border: darkMode?'rgba(16,185,129,0.2)':'#bbf7d0',
              bg: darkMode?'rgba(16,185,129,0.05)':'#f0fdf4',
              glow: 'rgba(16,185,129,0.1)',
            },
            {
              label: 'Inactive',
              value: clients.filter(c => c.is_active === false).length,
              icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>,
              iconBg: darkMode?'rgba(239,68,68,0.15)':'#fef2f2',
              val: darkMode?'#f87171':'#dc2626',
              border: darkMode?'rgba(239,68,68,0.2)':'#fecaca',
              bg: darkMode?'rgba(239,68,68,0.05)':'#fef2f2',
              glow: 'rgba(239,68,68,0.08)',
            },
            {
              label: 'This Month',
              value: clients.filter(c => {
                const d = new Date(c.created_at);
                const n = new Date();
                return d.getMonth()===n.getMonth() && d.getFullYear()===n.getFullYear();
              }).length,
              icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
              iconBg: darkMode?'rgba(249,115,22,0.15)':'#fff7ed',
              val: darkMode?'#fb923c':'#ea580c',
              border: darkMode?'rgba(249,115,22,0.2)':'#fed7aa',
              bg: darkMode?'rgba(249,115,22,0.05)':'#fff7ed',
              glow: 'rgba(249,115,22,0.1)',
            },
          ].map(stat => (
            <div key={stat.label} style={{
              background: stat.bg,
              border: `1px solid ${stat.border}`,
              borderRadius:14, padding:'16px 20px',
              display:'flex', alignItems:'center', gap:14,
              boxShadow: `0 4px 16px ${stat.glow}`,
              position:'relative', overflow:'hidden',
            }}>
              {/* Glow */}
              <div style={{ position:'absolute', top:-20, right:-20, width:70, height:70, borderRadius:'50%', background:stat.glow, filter:'blur(14px)', pointerEvents:'none' }}/>
              {/* Icon */}
              <div style={{ width:44, height:44, borderRadius:12, background:stat.iconBg, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                {stat.icon}
              </div>
              {/* Text */}
              <div>
                <p style={{ fontSize:26, fontWeight:800, color:stat.val, margin:0, lineHeight:1 }}>{stat.value}</p>
                <p style={{ fontSize:11, color:T.text3, margin:'5px 0 0', fontWeight:500 }}>{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Action Buttons Row */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-end', gap:8 }}>
          {selectedIds.length > 0 && (
            <button onClick={handleBulkDelete} disabled={bulkDeleting} style={{
              display:'flex', alignItems:'center', gap:6, padding:'8px 14px',
              borderRadius:9, border:'1.5px solid #fecaca', background: darkMode?'rgba(239,68,68,0.1)':'#fef2f2',
              color:'#ef4444', fontSize:13, fontWeight:600, cursor: bulkDeleting?'default':'pointer', opacity: bulkDeleting?0.6:1,
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
              {bulkDeleting ? 'Deleting...' : `Delete Selected (${selectedIds.length})`}
            </button>
          )}
          {[
            { label:'Import', onClick:() => setShowImport(true), icon:<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>, icon2:<polyline points="17 8 12 3 7 8"/>, icon3:<line x1="12" y1="3" x2="12" y2="15"/> },
            { label:'Export', onClick:exportToXLSX, icon:<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>, icon2:<polyline points="7 10 12 15 17 10"/>, icon3:<line x1="12" y1="15" x2="12" y2="3"/> },
          ].map(btn => (
            <button key={btn.label} onClick={btn.onClick} style={{
              display:'flex', alignItems:'center', gap:6, padding:'8px 14px',
              borderRadius:9, border:`1.5px solid ${T.border2}`, background:T.bg2,
              color:T.text2, fontSize:13, fontWeight:500, cursor:'pointer',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor='#f97316'; e.currentTarget.style.color='#f97316'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor=T.border2; e.currentTarget.style.color=T.text2; }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{btn.icon}{btn.icon2}{btn.icon3}</svg>
              {btn.label}
            </button>
          ))}
          <button onClick={() => setShowAddModal(true)} style={{
            display:'flex', alignItems:'center', gap:6, padding:'8px 18px',
            borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)',
            color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer',
            boxShadow:'0 4px 12px rgba(15,31,69,0.3)',
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            New Client
          </button>
        </div>
      </div>

      {/* Search & Filters */}
      <div style={{ background:T.bg2, borderRadius:14, border:`1px solid ${T.cardBorder}`, padding:16, marginBottom:14, boxShadow: darkMode?'0 2px 12px rgba(0,0,0,0.2)':'0 1px 4px rgba(15,31,69,0.05)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <input type="text" placeholder="Search by name, file no., PAN, GSTIN, mobile, email..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            style={{ flex:1, border:`1px solid ${T.inputBorder}`, borderRadius:9, padding:'8px 14px', fontSize:13, outline:'none', background:T.inputBg, color:T.text }} />
          <button onClick={() => setShowFilters(f => !f)} style={{
            display:'flex', alignItems:'center', gap:7, padding:'8px 14px',
            borderRadius:9, fontSize:13, fontWeight:500, cursor:'pointer',
            border: showFilters ? '1.5px solid #3b82f6' : `1.5px solid ${T.border2}`,
            background: showFilters ? (darkMode?'rgba(59,130,246,0.15)':'#eff6ff') : T.bg2,
            color: showFilters ? '#3b82f6' : T.text2,
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
            Filters
            {(filterType || filterStatus || filterDateFrom || filterDateTo || filterGroup || filterTag || filterUser || filterService || filterClientId || filterBillingProfile) && (
              <span style={{ background:'#3b82f6', color:'#fff', fontSize:10, borderRadius:'50%', width:16, height:16, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700 }}>
                {[filterType,filterStatus,filterDateFrom,filterDateTo,filterGroup,filterTag,filterUser,filterService,filterClientId,filterBillingProfile].filter(Boolean).length}
              </span>
            )}
          </button>
          <p style={{ fontSize:12, color:T.text3, whiteSpace:'nowrap' }}>{filtered.length} found</p>
        </div>
        {showFilters && (
          <div style={{ marginTop:14, paddingTop:14, borderTop:`1px solid ${T.border}`, display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Client Type</label>
              <select value={filterType} onChange={e => setFilterType(e.target.value)}
                style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:13, outline:'none', background:T.inputBg, color:T.text }}>
                <option value="">All Types</option>
                {(clientTypes.length ? clientTypes : CLIENT_TYPE_OPTIONS).map(t => (
                  <option key={t.value} value={t.value}>{t.name || t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Recurring Service</label>
              <select value={filterService} onChange={e => setFilterService(e.target.value)}
                style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:13, outline:'none', background:T.inputBg, color:T.text }}>
                <option value="">All Services</option>
                {filterServices.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Tag</label>
              <select value={filterTag} onChange={e => setFilterTag(e.target.value)}
                style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:13, outline:'none', background:T.inputBg, color:T.text }}>
                <option value="">All Tags</option>
                {allTags.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Group</label>
              <select value={filterGroup} onChange={e => setFilterGroup(e.target.value)}
                style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:13, outline:'none', background:T.inputBg, color:T.text }}>
                <option value="">All Groups</option>
                {filterGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Status</label>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:13, outline:'none', background:T.inputBg, color:T.text }}>
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>User</label>
              <select value={filterUser} onChange={e => setFilterUser(e.target.value)}
                style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:13, outline:'none', background:T.inputBg, color:T.text }}>
                <option value="">All Users</option>
                {filterUsers.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Client</label>
              <select value={filterClientId} onChange={e => setFilterClientId(e.target.value)}
                style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:13, outline:'none', background:T.inputBg, color:T.text }}>
                <option value="">All Clients</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.client_name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5 }}>Billing Profile</label>
              <select value={filterBillingProfile} onChange={e => setFilterBillingProfile(e.target.value)}
                style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:13, outline:'none', background:T.inputBg, color:T.text }}>
                <option value="">All Profiles</option>
                {billingProfileOptions.map(bp => <option key={bp} value={bp}>{bp}</option>)}
              </select>
            </div>
            <SmartDatePicker label="Created From" value={filterDateFrom} onChange={setFilterDateFrom} />
            <SmartDatePicker label="Created To" value={filterDateTo} onChange={setFilterDateTo} />
            <div style={{ gridColumn:'span 4', display:'flex', justifyContent:'flex-end' }}>
              <button onClick={() => {
                setFilterType(''); setFilterStatus(''); setFilterDateFrom(''); setFilterDateTo('');
                setFilterGroup(''); setFilterTag(''); setFilterUser(''); setFilterService('');
                setFilterClientId(''); setFilterBillingProfile('');
              }}
                style={{ padding:'6px 14px', borderRadius:8, border:`1px solid ${T.border2}`, background:T.bg2, color:T.text3, fontSize:12, cursor:'pointer' }}>
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Client Table */}
        <div style={{ flex:1, minHeight:0, background:T.bg2, borderRadius:14, border:`1px solid ${T.cardBorder}`, overflow:'hidden', display:'flex', flexDirection:'column', boxShadow: darkMode?'0 4px 24px rgba(0,0,0,0.3)':'0 2px 12px rgba(15,31,69,0.06)' }}>
          <div style={{ flex:1, overflowX:'auto', overflowY:'auto', scrollbarWidth:'none', msOverflowStyle:'none' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', position:'sticky', top:0, zIndex:10 }}>
                <tr>
                  <th style={{ padding:'10px 12px', width:36 }}>
                    <input type="checkbox"
                      checked={filtered.length > 0 && filtered.every(c => selectedIds.includes(c.id))}
                      onChange={toggleSelectAll}
                      style={{ cursor:'pointer' }} />
                  </th>
                  {orderedVisibleKeys.map(key => {
                    const col = CLIENT_COLUMNS.find(c => c.key === key);
                    return (
                      <th key={key} style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap' }}>
                        {col?.label}
                      </th>
                    );
                  })}
                  <th style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap' }}>Actions</th>
                  <th style={{ padding:'10px 8px', textAlign:'right', width:56, background:'transparent' }}>
                    {cc.menu}
                  </th>
                </tr>
              </thead>

<tbody>
                {loading ? (
                  <tr><td colSpan="20" style={{ padding:'32px 16px', textAlign:'center', color:T.text3 }}>Loading...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan="20" style={{ padding:'32px 16px', textAlign:'center', color:T.text3 }}>No clients found. Click "+ New Client" to add your first client!</td></tr>
                ) : (
                  filtered.map((client, idx) => (
                    <tr key={client.id}
                      style={{ borderBottom:`1px solid ${T.border}`, background: darkMode?(idx%2===0?T.bg2:T.bg3):(idx%2===0?'#fff':'#fafbff'), transition:'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = darkMode?'#1e3a5f':'#f0f4ff'}
                      onMouseLeave={e => e.currentTarget.style.background = darkMode?(idx%2===0?T.bg2:T.bg3):(idx%2===0?'#fff':'#fafbff')}>
                      <td style={{ padding:'10px 12px' }} onClick={e => e.stopPropagation()}>
                        <input type="checkbox"
                          checked={selectedIds.includes(client.id)}
                          onChange={() => setSelectedIds(prev => prev.includes(client.id) ? prev.filter(id => id !== client.id) : [...prev, client.id])}
                          style={{ cursor:'pointer' }} />
                      </td>
                      {orderedVisibleKeys.map(key => (
                        <td key={key} style={{
                          padding:'10px 16px', fontSize:13, whiteSpace:'nowrap',
                          color: (key === 'client_name' || key === 'file_number')
                            ? T.text
                            : T.text2,
                          fontWeight: (key === 'client_name' || key === 'file_number')
                            ? 600
                            : 400,
                        }}>
                          {renderClientCell(client, key)}
                        </td>
                      ))}
                      <td style={{ padding:'10px 16px', whiteSpace:'nowrap', position:'relative' }} data-kebab>
                        <button
                          onClick={(e) => { e.stopPropagation(); setOpenKebabId(openKebabId===client.id?null:client.id); }}
                          style={{ width:32, height:32, borderRadius:8, border:'none', background:'none', color:T.text2, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
                          onMouseEnter={e => e.currentTarget.style.background=T.bg3}
                          onMouseLeave={e => e.currentTarget.style.background='none'}
                          data-kebab>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/>
                          </svg>
                        </button>
                        {openKebabId === client.id && (
                          <div style={{ position:'absolute', right:0, top:40, background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:12, boxShadow: darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 8px 24px rgba(15,31,69,0.12)', zIndex:999, width:176, padding:6 }} data-kebab>
                            {[
                              { label:'View',        color:'#3b82f6', hoverBg: darkMode?'rgba(59,130,246,0.12)':'#eff6ff',  onClick:() => { setSelectedClient(client); setOpenKebabId(null); }, icon:<><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></> },
                              { label:'Edit',        color:T.text2,   hoverBg:T.bg3,                                         onClick:() => { setReturnToDashboardAfterEdit(false); setEditClient(client); setOpenKebabId(null); }, icon:<><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></> },
                              { label:'Assign User', color:T.text2,   hoverBg:T.bg3,                                         onClick:() => { setAssignClient(client); setShowAssignModal(true); setOpenKebabId(null); }, icon:<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></> },
                              { label:'Tags',        color:T.text2,   hoverBg:T.bg3,                                         onClick:() => { setTagsClient(client); setOpenKebabId(null); }, icon:<><path d="M20.59 13.41 11 3.83A2 2 0 0 0 9.59 3H4a1 1 0 0 0-1 1v5.59a2 2 0 0 0 .59 1.41l9.58 9.59a2 2 0 0 0 2.83 0l4.59-4.59a2 2 0 0 0 0-2.83z"/><circle cx="7.5" cy="7.5" r="1.5"/></> },
                            ].map(item => (
                              <button key={item.label} onClick={item.onClick}
                                style={{ width:'100%', textAlign:'left', padding:'8px 12px', borderRadius:8, border:'none', background:'none', color:item.color, fontSize:13, fontWeight:500, cursor:'pointer', display:'flex', alignItems:'center', gap:8 }}
                                onMouseEnter={e => e.currentTarget.style.background=item.hoverBg}
                                onMouseLeave={e => e.currentTarget.style.background='none'}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{item.icon}</svg>
                                {item.label}
                              </button>
                            ))}
                            <div style={{ height:1, background:T.border, margin:'4px 0' }}/>
                            <button onClick={() => { handleDeleteClient(client); setOpenKebabId(null); }}
                              style={{ width:'100%', textAlign:'left', padding:'8px 12px', borderRadius:8, border:'none', background:'none', color:'#ef4444', fontSize:13, fontWeight:500, cursor:'pointer', display:'flex', alignItems:'center', gap:8 }}
                              onMouseEnter={e => e.currentTarget.style.background=darkMode?'rgba(239,68,68,0.12)':'#fef2f2'}
                              onMouseLeave={e => e.currentTarget.style.background='none'}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                              Delete
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

      {/* Modals */}
      {showAddModal && (
        <ClientFormModal
          token={token}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => { fetchClients(); setShowAddModal(false); }}
          darkMode={darkMode} T={T}
        />
      )}

      {editClient && (
        <ClientFormModal
          token={token}
          client={editClient}
          darkMode={darkMode} T={T}
          onClose={() => {
            setEditClient(null);
            setReturnToDashboardAfterEdit(false);
          }}
          onSuccess={async () => {
            await fetchClients();

            if (returnToDashboardAfterEdit) {
              const res = await fetch(`${BASE}/api/clients/${editClient.id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              const updatedClient = await res.json();
              setSelectedClient(updatedClient);
            }

            setEditClient(null);
            setReturnToDashboardAfterEdit(false);
          }}
        />
      )}

      {showAssignModal && assignClient && (
        <AssignUserModal token={token} client={assignClient}
          onClose={() => { setShowAssignModal(false); setAssignClient(null); }}
          onSuccess={() => { fetchClients(); setShowAssignModal(false); setAssignClient(null); }} />
      )}

      {tagsClient && (
        <ClientTagsModal
          client={tagsClient}
          allTags={allTags}
          token={token}
          onClose={() => setTagsClient(null)}
          onSuccess={() => { fetchClients(); setTagsClient(null); }}
          onTagCreated={tag => setAllTags(prev => prev.some(t => t.id === tag.id) ? prev : [...prev, tag])}
        />
      )}

      {selectedClient && (
        <ClientDashboard
          client={selectedClient}
          token={token}
          darkMode={darkMode} T={T}
          onClose={() => {
            setSelectedClient(null);
            if (dashboardReturnTaskId) {
              // Came here from a specific task's Client/File Number link —
              // go back to that exact task instead of landing on the client list.
              const taskId = dashboardReturnTaskId;
              setDashboardReturnTaskId(null);
              window.dispatchEvent(new CustomEvent('navigateTo', { detail: { page:'tasks', taskId } }));
              return;
            }
            // The list query was skipped on a deep-link straight into this
            // dashboard (see mount effect above) — load it now that the
            // user is actually back at the list.
            if (loading) fetchClients();
          }}
          onEdit={() => {
            const clientToEdit = selectedClient;
            setSelectedClient(null);
            setReturnToDashboardAfterEdit(true);
            setEditClient(clientToEdit);
          }}
          onRefresh={fetchClients}
        />
      )}


      {/* ── IMPORT MODAL ── */}
      {showImport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full shadow-2xl" style={{ maxWidth:'580px' }}>

            <div className="flex items-center justify-between px-7 py-5 border-b border-gray-100">
              <div>
                <h3 className="text-lg font-bold" style={{ color:'#0f1f45' }}>Import Clients</h3>
                <p className="text-xs text-gray-400 mt-0.5">Bulk upload from Excel</p>
              </div>
              <button onClick={() => { if (importing) return; setShowImport(false); setImportRows([]); setImportResult(null); }}
                disabled={importing}
                className="w-9 h-9 rounded-xl hover:bg-gray-100 flex items-center justify-center text-gray-400 transition disabled:opacity-30 disabled:cursor-not-allowed">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <div className="p-7 space-y-5">
              {(!importing && !importResult) && (
              <>
              {/* Step 1 */}
              <div className="border border-gray-200 rounded-2xl p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-50 border border-green-100 rounded-xl flex items-center justify-center text-green-600">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">Download Template</p>
                      <p className="text-xs text-gray-400 mt-0.5">Pre-formatted Excel with all columns + sample row</p>
                    </div>
                  </div>
                  <button onClick={downloadClientTemplate}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-green-200 text-green-700 hover:bg-green-50 transition">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    Download
                  </button>
                </div>
              </div>

              {/* Step 2 */}
              <div className={`border border-gray-200 rounded-2xl p-5 transition ${importing ? 'opacity-50 pointer-events-none' : ''}`}>
                <p className="text-sm font-semibold text-gray-800 mb-3">Upload Filled Template</p>
                <label className={`flex flex-col items-center justify-center w-full h-28 border-2 border-dashed rounded-xl cursor-pointer transition ${importRows.length > 0 ? 'border-blue-300 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'}`}>
                  <input type="file" ref={importFileRef} accept=".xlsx,.xls" className="hidden" disabled={importing} onChange={handleImportFile} />
                  {importRows.length > 0 ? (
                    <div className="text-center">
                      <svg className="w-8 h-8 text-blue-500 mx-auto mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                      <p className="text-sm font-semibold text-blue-700">{importRows.length} rows ready</p>
                      <p className="text-xs text-blue-400 mt-0.5">Click to change file</p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <svg className="w-8 h-8 text-gray-300 mx-auto mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                      <p className="text-sm font-medium text-gray-500">Click to upload Excel file</p>
                      <p className="text-xs text-gray-400 mt-0.5">.xlsx or .xls only</p>
                    </div>
                  )}
                </label>
              </div>
              </>
              )}

              {/* Progress */}
              {importing && importProgress && (() => {
                const preparing = importProgress.phase === 'preparing';
                const pct = importProgress.total ? Math.round((importProgress.current / importProgress.total) * 100) : 0;
                return (
                  <div className="rounded-2xl border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <span className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                          <span className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"/>
                        </span>
                        <div>
                          <p className="text-sm font-semibold text-gray-800">
                            {preparing ? 'Preparing import…' : 'Importing clients'}
                          </p>
                          <p className="text-xs text-gray-400">Please keep this window open</p>
                        </div>
                      </div>
                      <p className="text-lg font-extrabold" style={{ color:'#0f1f45', fontVariantNumeric:'tabular-nums' }}>
                        {importProgress.current}<span className="text-gray-300 font-semibold text-sm"> / {importProgress.total}</span>
                      </p>
                    </div>

                    <div style={{ height: 12, width: '100%', borderRadius: 999, background: '#eef1f6', overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', borderRadius: 999,
                        width: `${preparing ? 8 : Math.max(pct, 2)}%`,
                        background: preparing
                          ? 'linear-gradient(90deg,#cbd5e1,#e2e8f0)'
                          : 'linear-gradient(90deg,#0f1f45,#2a4d82)',
                        transition: 'width 0.35s ease-out',
                        position: 'relative', overflow: 'hidden',
                      }}>
                        {!preparing && (
                          <div style={{
                            position: 'absolute', inset: 0,
                            backgroundImage: 'linear-gradient(45deg, rgba(255,255,255,0.22) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.22) 50%, rgba(255,255,255,0.22) 75%, transparent 75%, transparent)',
                            backgroundSize: '28px 28px',
                            animation: 'cx-stripe 0.7s linear infinite',
                          }} />
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center gap-4 text-xs font-semibold" style={{ fontVariantNumeric:'tabular-nums' }}>
                        <span className="text-green-600">✓ {importProgress.ok || 0} added</span>
                        <span className="text-amber-600">⊘ {importProgress.skip || 0} skipped</span>
                        <span className={importProgress.fail ? 'text-red-600' : 'text-gray-300'}>✕ {importProgress.fail || 0} failed</span>
                      </div>
                      <p className="text-xs font-bold text-gray-500" style={{ fontVariantNumeric:'tabular-nums' }}>{preparing ? '' : `${pct}%`}</p>
                    </div>
                  </div>
                );
              })()}

              {/* Report */}
              {importResult && (
                <div className="rounded-2xl border border-gray-200 overflow-hidden">
                  <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-gray-100" style={{ background:'#f8faff' }}>
                    <span className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: importResult.errors?.length ? '#fef2f2' : '#f0fdf4' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={importResult.errors?.length ? '#dc2626' : '#16a34a'} strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                    </span>
                    <p className="text-sm font-bold" style={{ color:'#0f1f45' }}>Import complete</p>
                  </div>
                  <div className="grid grid-cols-3 divide-x divide-gray-100">
                    {[
                      { label:'Imported', value: importResult.success || 0,           color:'#16a34a', bg:'#f0fdf4' },
                      { label:'Skipped',  value: importResult.skipped || 0,           color:'#d97706', bg:'#fffbeb' },
                      { label:'Failed',   value: importResult.errors?.length || 0,    color:'#dc2626', bg:'#fef2f2' },
                    ].map(s => (
                      <div key={s.label} className="p-4 text-center" style={{ background: s.value ? s.bg : '#fff' }}>
                        <p className="text-2xl font-extrabold" style={{ color: s.value ? s.color : '#94a3b8', fontVariantNumeric:'tabular-nums' }}>{s.value}</p>
                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mt-0.5">{s.label}</p>
                      </div>
                    ))}
                  </div>
                  {(importResult.errors?.length > 0 || importResult.warnings?.length > 0) && (
                    <div className="border-t border-gray-100 p-4 space-y-1.5 max-h-40 overflow-y-auto bg-gray-50">
                      {importResult.errors?.map((e, i) => (
                        <p key={'e' + i} className="text-xs text-red-600 flex gap-1.5"><span className="flex-shrink-0">✕</span><span>{e}</span></p>
                      ))}
                      {importResult.warnings?.map((w, i) => (
                        <p key={'w' + i} className="text-xs text-gray-500 flex gap-1.5"><span className="flex-shrink-0 text-amber-500 font-bold">!</span><span>{w}</span></p>
                      ))}
                    </div>
                  )}
                  <div className="border-t border-gray-100 px-4 py-2.5 bg-white">
                    <p className="text-xs text-gray-500">
                      {importResult.success > 0
                        ? `${importResult.success} of ${importResult.total} row${importResult.total === 1 ? '' : 's'} added to your client list.`
                        : 'No new clients were added.'}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="px-7 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3 rounded-b-2xl">
              <button onClick={() => { if (importing) return; setShowImport(false); setImportRows([]); setImportResult(null); }}
                disabled={importing}
                className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-white transition disabled:opacity-40 disabled:cursor-not-allowed">
                {importResult ? 'Done' : 'Close'}
              </button>
              {importRows.length > 0 && !importResult && (
                <button onClick={handleImportSubmit} disabled={importing}
                  className="px-6 py-2.5 rounded-xl text-white text-sm font-semibold disabled:opacity-60 flex items-center gap-2"
                  style={{ background:'#0f1f45' }}>
                  {importing && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>}
                  {importing
                    ? (importProgress ? `Importing… ${importProgress.current}/${importProgress.total}` : 'Importing…')
                    : `Import ${importRows.length} Clients`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// CLIENT FORM MODAL
function ClientFormModal({ token, client, onClose, onSuccess, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [tags, setTags] = useState([]);
  const [clientTypes, setClientTypes] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [saving, setSaving] = useState(false);
  const [gstFetching, setGstFetching] = useState(false);
  const [gstMsg, setGstMsg] = useState(null);
  const [toasts, showToast] = useToasts();
  const [duplicates, setDuplicates] = useState([]);
  const dupTimer = useRef(null);
  const [form, setForm] = useState({
    client_name: client?.client_name || '',
    client_type: client?.client_type || '',
    group_id: client?.group_id || '',
    pan: client?.pan || '',
    gstin: client?.gstin || '',
    cin: client?.cin || '',
    mobile: client?.mobile || '',
    secondary_mobile: client?.secondary_mobile || '',
    email: client?.email || '',
    address: client?.address || '',
    city: client?.city || '',
    state: client?.state || '',
    pincode: client?.pincode || '',
    date_of_birth: client?.date_of_birth ? client.date_of_birth.split('T')[0] : '',
    contact_person: client?.contact_person || '',
    billing_profile: client?.billing_profile || '',
    opening_balance: client?.opening_balance || 0,
    is_active: client?.is_active !== false,
    allow_login: client?.allow_login || false,
    allow_notifications: client?.allow_notifications !== false,
    create_invoice_automatically: client?.create_invoice_automatically || false,
    notes: client?.notes || ''
  });

  const INDIAN_STATES = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Delhi', 'Jammu & Kashmir', 'Ladakh', 'Puducherry', 'Chandigarh'
  ];

  useEffect(() => {
    Promise.all([
      fetch(`${BASE}/api/users`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json()),
      fetch(`${BASE}/api/clients/groups/all`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json()),
      fetch(`${BASE}/api/clients/tags/all`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json()),
      fetch(`${BASE}/api/clients/types/all`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json()),
    ]).then(([u, g, t, types]) => {
      setUsers(Array.isArray(u) ? u : []);
      setGroups(Array.isArray(g) ? g : []);
      setTags(Array.isArray(t) ? t : []);
      setClientTypes(Array.isArray(types) && types.length ? types : CLIENT_TYPE_OPTIONS.map(o => ({ value: o.value, name: o.label })));

      // Pre-populate tags for edit mode
      if (client?.tags_data) {
        const existingTagIds = client.tags_data.split(',')
          .map(td => parseInt(td.split(':')[0]))
          .filter(id => !isNaN(id));
        setSelectedTags(existingTagIds);
      }

      // Pre-populate assigned users for edit mode
      if (client?.assigned_users_data) {
        const existingUserIds = client.assigned_users_data.split(',')
          .map(ud => parseInt(ud.split(':')[0]))
          .filter(id => !isNaN(id));
        setSelectedUsers(existingUserIds);
      }
    }).catch(console.error);
  }, [token]);

  // Live duplicate lookup — fires (debounced) when identity fields lose focus.
  const checkDuplicates = () => {
    clearTimeout(dupTimer.current);
    dupTimer.current = setTimeout(async () => {
      if (!form.client_name.trim() && !form.pan.trim() && !form.gstin.trim()) { setDuplicates([]); return; }
      try {
        const res = await fetch(`${BASE}/api/clients/check-duplicate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            client_name: form.client_name, pan: form.pan, gstin: form.gstin,
            exclude_id: client?.id || null,
          }),
        });
        const data = await res.json();
        setDuplicates(Array.isArray(data.duplicates) ? data.duplicates : []);
      } catch { /* non-blocking */ }
    }, 400);
  };

  const handleCreateType = async (name) => {
    try {
      const res = await fetch(`${BASE}/api/clients/types`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ name }),
      });
      const t = await res.json();
      if (res.ok && t?.value) {
        setClientTypes(prev => prev.some(x => x.value === t.value) ? prev : [...prev, t]);
        return t;
      }
      showToast(t.message || 'Could not add type', 'error');
    } catch { showToast('Cannot connect to server.', 'error'); }
    return null;
  };

  const handleCreateGroup = async (name) => {
    try {
      const res = await fetch(`${BASE}/api/clients/groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ name }),
      });
      const g = await res.json();
      if (res.ok && g?.id) {
        setGroups(prev => prev.some(x => x.id === g.id) ? prev : [...prev, g]);
        return { value: g.id, name: g.name };
      }
      showToast(g.message || 'Could not add group', 'error');
    } catch { showToast('Cannot connect to server.', 'error'); }
    return null;
  };

  const TAG_PALETTE = ['#ef4444','#f97316','#f59e0b','#84cc16','#10b981','#0d9488','#3b82f6','#6366f1','#8b5cf6','#ec4899'];
  const handleCreateTag = async (name) => {
    try {
      const res = await fetch(`${BASE}/api/clients/tags`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ name, color: TAG_PALETTE[tags.length % TAG_PALETTE.length] }),
      });
      const tag = await res.json();
      if (res.ok && tag?.id) {
        setTags(prev => prev.some(x => x.id === tag.id) ? prev : [...prev, tag]);
        return tag;
      }
      showToast(tag.message || 'Could not add tag', 'error');
    } catch { showToast('Cannot connect to server.', 'error'); }
    return null;
  };

  const handleSubmit = async (e, forceDuplicate = false) => {
  if (e && e.preventDefault) e.preventDefault();
  if (saving) return;
  setSaving(true);
  try {
    const url = client
      ? `${BASE}/api/clients/${client.id}`
      : `${BASE}/api/clients`;
    const method = client ? 'PUT' : 'POST';
    const payload = { ...form, assigned_users: selectedUsers, tag_ids: selectedTags, allow_duplicate: forceDuplicate };
    const res = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(payload)
    });
    const data = await res.json();
    if (res.ok) { onSuccess(); }
    else if (res.status === 409 && Array.isArray(data.duplicates) && data.duplicates.length) {
      setDuplicates(data.duplicates);
      showToast('Possible duplicate — review the warning, then use "Create anyway" if it\'s a new client.', 'error');
    }
    else { showToast(data.message || 'Something went wrong', 'error'); }
  } catch (err) {
    showToast('Cannot connect to server.', 'error');
  } finally {
    setSaving(false);
  }
};

  const is = { width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:9, padding:'9px 12px', fontSize:13, outline:'none', background:T.inputBg, color:T.text, boxSizing:'border-box' };
  const SL = ({ children }) => <label style={{ display:'block', fontSize:11, fontWeight:600, color:T.text3, marginBottom:5, textTransform:'uppercase', letterSpacing:'0.05em' }}>{children}</label>;
  const SC = ({ title, accent }) => <p style={{ fontSize:10, fontWeight:700, color: accent||T.text3, textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:12 }}>{title}</p>;

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:50, padding:12 }}>
      <ToastStack toasts={toasts} />
      <div style={{ background:T.bg2, borderRadius:20, display:'flex', flexDirection:'column', boxShadow: darkMode?'0 24px 60px rgba(0,0,0,0.6)':'0 24px 60px rgba(15,31,69,0.2)', width:'100%', maxWidth:1440, height:'94vh' }}>

        {/* HEADER */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 24px', borderBottom:`1px solid ${T.border}`, flexShrink:0, background:T.bg2, borderRadius:'20px 20px 0 0' }}>
          <div>
            <h2 style={{ fontSize:15, fontWeight:700, color:T.text, margin:0 }}>{client ? 'Edit Client' : 'New Client'}</h2>
            <p style={{ fontSize:14, color:T.text3, marginTop:3 }}>{client ? `${client.file_number} · ${client.client_name}` : 'Fill in the details below to Add a New Client'}</p>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, background:T.bg3, border:`1px solid ${T.border}`, borderRadius:9, padding:'6px 12px' }}>
              <span style={{ fontSize:12, color:T.text2, fontWeight:500 }}>Active</span>
              <button type="button" onClick={() => setForm(f => ({ ...f, is_active: !f.is_active }))}
                style={{ position:'relative', width:36, height:18, borderRadius:9, border:'none', cursor:'pointer', background: form.is_active?'#3b82f6':(darkMode?'#334155':'#d1d5db'), transition:'background 0.2s', flexShrink:0 }}>
                <span style={{ position:'absolute', top:2, left: form.is_active?20:2, width:14, height:14, borderRadius:'50%', background:'#fff', transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }}/>
              </button>
            </div>
            <button type="button" onClick={onClose} style={{ width:32, height:32, borderRadius:'50%', border:'none', background:T.bg3, color:T.text3, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>

        {/* BODY */}
        <form onSubmit={handleSubmit} style={{ flex:1, minHeight:0, display:'flex', flexDirection:'column' }}>
          <div style={{ flex:1, minHeight:0, display:'flex', overflow:'hidden' }}>

            {/* COL 1 — Identity 50% */}
            <div style={{ width:'50%', borderRight:`1px solid ${T.border}`, flexShrink:0, display:'flex', flexDirection:'column', background:T.bg3, padding:24, overflowY:'auto', scrollbarWidth:'none', msOverflowStyle:'none' }}>
              <SC title="Client Identity" accent="#f97316" />

              {!client && duplicates.length > 0 && (
                <div style={{ marginBottom:16, padding:'12px 14px', borderRadius:10, background: darkMode?'rgba(245,158,11,0.12)':'#fffbeb', border:`1px solid ${darkMode?'rgba(245,158,11,0.3)':'#fde68a'}` }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                    <span style={{ fontSize:12, fontWeight:700, color:'#b45309' }}>
                      {duplicates.length === 1 ? 'A matching client already exists' : `${duplicates.length} matching clients already exist`}
                    </span>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
                    {duplicates.slice(0, 5).map(d => (
                      <p key={d.id} style={{ fontSize:12, color:'#92400e', margin:0 }}>
                        • <strong>{d.client_name}</strong> {d.file_number ? `(${d.file_number})` : ''} — matches {d.matched.join(', ')}
                      </p>
                    ))}
                  </div>
                  <p style={{ fontSize:11, color:'#a16207', margin:'8px 0 0' }}>
                    If this really is a new, separate client, use <strong>Create anyway</strong> below.
                  </p>
                </div>
              )}

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', columnGap:24, rowGap:16 }}>
                <div style={{ gridColumn:'span 2' }}>
                  <SL>Client Name *</SL>
                  <input required type="text" placeholder="Full legal name" value={form.client_name}
                    onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))} onBlur={checkDuplicates} style={is} />
                </div>
                <div>
                  <SL>Client Type *</SL>
                  <TypeDropdown
                    types={clientTypes}
                    value={form.client_type}
                    onChange={v => setForm(f => ({ ...f, client_type: v }))}
                    onCreate={handleCreateType}
                    darkMode={darkMode} T={T} />
                </div>
                <div>
                  <SL>Group</SL>
                  <TypeDropdown
                    types={groups.map(g => ({ value: g.id, name: g.name }))}
                    value={form.group_id}
                    onChange={v => setForm(f => ({ ...f, group_id: v }))}
                    onCreate={handleCreateGroup}
                    placeholder="Select Group" addLabel="Add new group" addPlaceholder="e.g. Corporate Clients"
                    darkMode={darkMode} T={T} />
                </div>
                <div>
                  <SL>Contact Person</SL>
                  <input type="text" placeholder="Name of contact" value={form.contact_person}
                    onChange={e => setForm(f => ({ ...f, contact_person: e.target.value }))} style={is} />
                </div>
                <div>
                  <SmartDatePicker label="Date of Birth / Incorporation" value={form.date_of_birth}
                    onChange={v => setForm(f => ({ ...f, date_of_birth: v }))} darkMode={darkMode} T={T} />
                </div>
                <div>
                  <SL>PAN</SL>
                  <input type="text" placeholder="ABCDE1234F" value={form.pan}
                    onChange={e => setForm(f => ({ ...f, pan: e.target.value.toUpperCase() }))} onBlur={checkDuplicates} style={is} maxLength={10} />
                </div>
                <div>
                  <SL>GSTIN</SL>
                  <div style={{ display:'flex', gap:8 }}>
                    <input type="text" placeholder="22AAAAA0000A1Z5" value={form.gstin}
                      onChange={e => setForm(f => ({ ...f, gstin: e.target.value.toUpperCase() }))} onBlur={checkDuplicates}
                      style={{ ...is, flex:1 }} maxLength={15} />
                    <button type="button" disabled={form.gstin?.length !== 15 || gstFetching}
                      onClick={async () => {
                        if (form.gstin?.length !== 15) return;
                        setGstFetching(true); setGstMsg(null);
                        try {
                          const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/clients/gstin/${form.gstin}`, { headers: { 'Authorization': `Bearer ${token}` } });
                          const data = await res.json();
                          if (data?.data?.lgnm) {
                            setForm(f => ({
                              ...f,
                              client_name:  f.client_name  || data.data.lgnm || '',
                              state:        f.state        || data.data.pradr?.addr?.stcd || '',
                              city:         f.city         || data.data.pradr?.addr?.dst  || '',
                              pincode:      f.pincode      || data.data.pradr?.addr?.pncd || '',
                              address:      f.address      || [data.data.pradr?.addr?.bno, data.data.pradr?.addr?.bnm, data.data.pradr?.addr?.st, data.data.pradr?.addr?.loc].filter(Boolean).join(', '),
                              client_type:  f.client_type  || (data.data.ctb === 'Proprietorship' ? 'sole_proprietor' : data.data.ctb === 'Partnership' ? 'partnership' : data.data.ctb?.includes('Private') ? 'private_limited' : f.client_type),
                            }));
                            setGstMsg({ type:'success', text:`✓ Fetched: ${data.data.lgnm}` });
                          } else {
                            setGstMsg({ type:'error', text:'No data found for this GSTIN' });
                          }
                        } catch {
                          setGstMsg({ type:'error', text:'GST server unreachable. Try again.' });
                        }
                        setGstFetching(false);
                      }}
                      style={{ padding:'0 16px', height:38, borderRadius:9, border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff', fontSize:12, fontWeight:600, cursor: form.gstin?.length===15 && !gstFetching ? 'pointer' : 'not-allowed', flexShrink:0, transition:'all 0.15s', whiteSpace:'nowrap', opacity: form.gstin?.length===15 && !gstFetching ? 1 : 0.4 }}>
                      {gstFetching ? '...' : 'Fetch'}
                    </button>
                  </div>
                  {gstMsg && (
                    <p style={{ fontSize:11, marginTop:5, color: gstMsg.type==='success' ? '#16a34a' : '#ef4444', fontWeight:500 }}>{gstMsg.text}</p>
                  )}
                  {!gstMsg && <p style={{ fontSize:11, color:T.text3, marginTop:4 }}>Enter 15-digit GSTIN then click Fetch</p>}
                </div>
                <div>
                  <SL>CIN / LLPIN</SL>
                  <input type="text" placeholder="U12345MH2020PTC123456" value={form.cin || ''}
                    onChange={e => setForm(f => ({ ...f, cin: e.target.value.toUpperCase() }))} style={is} maxLength={21} />
                  <p style={{ fontSize:11, color:T.text3, marginTop:4 }}>Company / LLP Identification Number</p>
                </div>
              </div>
            </div>

            {/* COL 2 — Contact + Address 25% */}
            <div style={{ width:'25%', borderRight:`1px solid ${T.border}`, flexShrink:0, display:'flex', flexDirection:'column', background:T.bg2, padding:20, gap:20, overflowY:'auto', scrollbarWidth:'none', msOverflowStyle:'none' }}>
              <div>
                <SC title="Contact Details" accent="#f97316" />
                <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                  {[
                    { label:'Mobile No.', field:'mobile', type:'text', ph:'10-digit number', max:10 },
                    { label:'Secondary Mobile', field:'secondary_mobile', type:'text', ph:'Alternate number', max:10 },
                    { label:'Email Address', field:'email', type:'email', ph:'email@example.com' },
                  ].map(f => (
                    <div key={f.field}>
                      <SL>{f.label}</SL>
                      <input type={f.type} placeholder={f.ph} value={form[f.field]}
                        onChange={e => setForm(fm => ({ ...fm, [f.field]: e.target.value }))}
                        style={is} maxLength={f.max} />
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ borderTop:`1px solid ${T.border}`, paddingTop:18 }}>
                <SC title="Address" />
                <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                  <div>
                    <SL>Street Address</SL>
                    <textarea rows="3" placeholder="Building / Street / Area" value={form.address}
                      onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                      style={{ ...is, resize:'vertical' }} />
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                    <div>
                      <SL>City</SL>
                      <input type="text" placeholder="City" value={form.city}
                        onChange={e => setForm(f => ({ ...f, city: e.target.value }))} style={is} />
                    </div>
                    <div>
                      <SL>Pincode</SL>
                      <input type="text" placeholder="6-digit" value={form.pincode}
                        onChange={e => setForm(f => ({ ...f, pincode: e.target.value }))} style={is} maxLength={6} />
                    </div>
                  </div>
                  <div>
                    <SL>State</SL>
                    <select value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} style={is}>
                      <option value="">Select State</option>
                      {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* COL 3 — Team + Settings + Notes 25% */}
            <div style={{ width:'25%', flexShrink:0, display:'flex', flexDirection:'column', background:T.bg3, padding:20, gap:18, overflowY:'auto', scrollbarWidth:'none', msOverflowStyle:'none' }}>
              <div>
                <SC title="Team Assignment" accent="#f97316" />
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                  <div>
                    <SL>Tags</SL>
                    <TagDropdown tags={tags} selectedTags={selectedTags} setSelectedTags={setSelectedTags} onCreate={handleCreateTag} darkMode={darkMode} T={T} />
                  </div>
                  <div>
                    <SL>Assign Users</SL>
                    <UserDropdown users={users} selectedUsers={selectedUsers} setSelectedUsers={setSelectedUsers} darkMode={darkMode} T={T} />
                  </div>
                </div>
              </div>

              <div style={{ borderTop:`1px solid ${T.border}`, paddingTop:16 }}>
                <SC title="Financial" />
                <div style={{ maxWidth:180 }}>
                  <SL>Opening Balance (₹)</SL>
                  <input type="number" placeholder="0.00" value={form.opening_balance}
                    onChange={e => setForm(f => ({ ...f, opening_balance: e.target.value }))} style={is} />
                </div>
              </div>

              <div style={{ borderTop:`1px solid ${T.border}`, paddingTop:16 }}>
                <SC title="Settings" />
                <div style={{ background:T.bg2, borderRadius:12, border:`1px solid ${T.border}`, overflow:'hidden' }}>
                  {[
                    { key:'allow_notifications', label:'Allow Notifications', desc:'Send alerts & reminders' },
                    { key:'allow_login', label:'Client Portal Login', desc:'Let client access portal' },
                    { key:'create_invoice_automatically', label:'Auto-create Invoice', desc:'Generate invoices automatically' },
                  ].map(({ key, label, desc }, i, arr) => (
                    <div key={key} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px', borderBottom: i < arr.length-1 ? `1px solid ${T.border}` : 'none' }}>
                      <div>
                        <p style={{ fontSize:13, fontWeight:500, color:T.text, margin:0 }}>{label}</p>
                        <p style={{ fontSize:11, color:T.text3, margin:'2px 0 0' }}>{desc}</p>
                      </div>
                      <button type="button" onClick={() => setForm(f => ({ ...f, [key]: !f[key] }))}
                        style={{ position:'relative', width:36, height:18, borderRadius:9, border:'none', cursor:'pointer', background: form[key]?'#3b82f6':(darkMode?'#334155':'#d1d5db'), transition:'background 0.2s', flexShrink:0 }}>
                        <span style={{ position:'absolute', top:2, left: form[key]?20:2, width:14, height:14, borderRadius:'50%', background:'#fff', transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }}/>
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ borderTop:`1px solid ${T.border}`, paddingTop:16 }}>
                <SC title="Notes" />
                <textarea rows="4" placeholder="Any additional notes about this client..."
                  value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  style={{ ...is, resize:'vertical' }} />
              </div>
            </div>
          </div>

          {/* FOOTER */}
          <div style={{ padding:'12px 24px', borderTop:`1px solid ${T.border}`, display:'flex', alignItems:'center', gap:10, flexShrink:0, background:T.bg2, borderRadius:'0 0 20px 20px' }}>
            <button type="button" onClick={onClose} style={{ padding:'9px 20px', borderRadius:9, border:`1.5px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, fontWeight:500, cursor:'pointer' }}>
              Cancel
            </button>
            {!client && duplicates.length > 0 && (
              <button type="button" disabled={saving} onClick={() => handleSubmit(null, true)} style={{
                marginLeft:'auto', padding:'9px 22px', borderRadius:9, border:'1.5px solid #f59e0b',
                background: darkMode?'rgba(245,158,11,0.12)':'#fffbeb', color:'#b45309',
                fontSize:13, fontWeight:600, cursor: saving?'default':'pointer', opacity: saving?0.7:1,
              }}>
                Create anyway
              </button>
            )}
            <button type="submit" disabled={saving} style={{
              marginLeft: (!client && duplicates.length > 0) ? 0 : 'auto', padding:'9px 28px', borderRadius:9, border:'none',
              background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff',
              fontSize:13, fontWeight:600, cursor:'pointer',
              boxShadow:'0 4px 12px rgba(15,31,69,0.3)', opacity: saving?0.7:1,
              display:'flex', alignItems:'center', gap:8,
            }}>
              {saving && <span style={{ width:13, height:13, border:'2px solid rgba(255,255,255,0.4)', borderTopColor:'#fff', borderRadius:'50%', display:'inline-block', animation:'spin 0.6s linear infinite' }}/>}
              {saving ? 'Saving...' : client ? 'Save Changes' : 'Create Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// TAG DROPDOWN
function TagDropdown({ tags, selectedTags, setSelectedTags, onCreate, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff' };
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setAdding(false); } };
    document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h);
  }, []);
  const names = tags.filter(t => selectedTags.includes(t.id)).map(t => t.name);

  const submitNew = async () => {
    const name = newName.trim();
    if (!name || busy || !onCreate) return;
    setBusy(true);
    try {
      const created = await onCreate(name);
      if (created?.id) setSelectedTags(prev => prev.includes(created.id) ? prev : [...prev, created.id]);
      setNewName(''); setAdding(false);
    } finally { setBusy(false); }
  };

  return (
    <div ref={ref} style={{ position:'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ width:'100%', border:`1px solid ${open ? '#3b82f6' : T.inputBorder}`, borderRadius:9, padding:'8px 12px', fontSize:13, textAlign:'left', display:'flex', alignItems:'center', justifyContent:'space-between', background:T.inputBg, cursor:'pointer', minHeight:38, transition:'border-color 0.15s' }}>
        <div style={{ display:'flex', flexWrap:'wrap', gap:4, flex:1, minWidth:0 }}>
          {names.length === 0
            ? <span style={{ color:T.text3 }}>Select tags...</span>
            : names.slice(0,3).map((n,i) => <span key={i} style={{ padding:'2px 8px', background: darkMode?'rgba(99,102,241,0.15)':'#eef2ff', color:'#6366f1', borderRadius:20, fontSize:11, fontWeight:600 }}>{n}</span>)}
          {names.length > 3 && <span style={{ fontSize:11, color:T.text3 }}>+{names.length-3}</span>}
        </div>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2" style={{ flexShrink:0, marginLeft:4, transform: open ? 'rotate(180deg)' : 'none', transition:'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>
      </button>
      {open && (
        <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, right:0, background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:12, boxShadow: darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 12px 40px rgba(15,31,69,0.12)', zIndex:9999, overflow:'hidden' }}>
          <div className="cx-noscroll" style={{ maxHeight:200, overflowY:'auto', padding:6 }}>
            {tags.length === 0 && <p style={{ fontSize:12, color:T.text3, padding:12, textAlign:'center' }}>No tags yet</p>}
            {tags.map(tag => {
              const sel = selectedTags.includes(tag.id);
              return (
                <label key={tag.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 10px', borderRadius:8, cursor:'pointer', background: sel?(darkMode?'rgba(99,102,241,0.1)':'#eef2ff'):'transparent' }}
                  onMouseEnter={e => { if(!sel) e.currentTarget.style.background=T.bg3; }}
                  onMouseLeave={e => { if(!sel) e.currentTarget.style.background='transparent'; }}>
                  <div style={{ width:16, height:16, borderRadius:4, border:`2px solid ${sel?(tag.color||'#6366f1'):(T.border2)}`, background: sel?(tag.color||'#6366f1'):'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'all 0.15s' }}>
                    {sel && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                  </div>
                  <input type="checkbox" style={{ display:'none' }} checked={sel}
                    onChange={() => setSelectedTags(prev => prev.includes(tag.id) ? prev.filter(t => t !== tag.id) : [...prev, tag.id])} />
                  <span style={{ fontSize:13, color:T.text }}>{tag.name}</span>
                </label>
              );
            })}
          </div>

          {onCreate && (
            <div style={{ borderTop:`1px solid ${T.border}`, padding:6 }}>
              {adding ? (
                <div style={{ display:'flex', gap:6, alignItems:'center', padding:'2px' }}>
                  <input autoFocus type="text" value={newName} placeholder="New tag name"
                    onChange={e => setNewName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submitNew(); } if (e.key === 'Escape') { setAdding(false); setNewName(''); } }}
                    style={{ flex:1, border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:12, outline:'none', background:T.bg3, color:T.text, boxSizing:'border-box' }} />
                  <button type="button" onClick={submitNew} disabled={busy || !newName.trim()}
                    style={{ padding:'7px 12px', borderRadius:8, border:'none', background:'#0f1f45', color:'#fff', fontSize:12, fontWeight:600, cursor: busy||!newName.trim() ? 'default' : 'pointer', opacity: busy||!newName.trim() ? 0.5 : 1, flexShrink:0 }}>
                    {busy ? '…' : 'Add'}
                  </button>
                </div>
              ) : (
                <button type="button" onClick={() => setAdding(true)}
                  style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderRadius:8, border:'none', background:'transparent', cursor:'pointer', fontSize:12.5, fontWeight:600, color:'#3b82f6', textAlign:'left' }}
                  onMouseEnter={e => e.currentTarget.style.background = darkMode?'rgba(59,130,246,0.1)':'#eff6ff'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Add new tag
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// USER DROPDOWN
function UserDropdown({ users, selectedUsers, setSelectedUsers, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const COLORS = ['#0f1f45','#3b82f6','#8b5cf6','#10b981','#f59e0b','#ef4444','#ec4899'];

  useEffect(() => {
    const h = e => { if(ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const filtered = users.filter(u => u.full_name?.toLowerCase().includes(search.toLowerCase()));
  const selected = users.filter(u => selectedUsers.includes(u.id));

  return (
    <div ref={ref} style={{ position:'relative' }}>
      {/* Trigger */}
      <button type="button" onClick={() => { setOpen(o => !o); setSearch(''); }}
        style={{ width:'100%', border:`1px solid ${T.inputBorder}`, borderRadius:9, padding:'7px 10px', fontSize:13, textAlign:'left', display:'flex', alignItems:'center', justifyContent:'space-between', background:T.inputBg, cursor:'pointer', minHeight:38, gap:6 }}>
        <div style={{ display:'flex', alignItems:'center', gap:4, flex:1, minWidth:0, flexWrap:'nowrap', overflow:'hidden' }}>
          {selected.length === 0 ? (
            <span style={{ color:T.text3, fontSize:12 }}>Select users...</span>
          ) : (
            <>
              {/* Overlapping avatars */}
              <div style={{ display:'flex', alignItems:'center' }}>
                {selected.slice(0,4).map((u,i) => (
                  <div key={u.id} title={u.full_name} style={{
                    width:22, height:22, borderRadius:'50%', marginLeft: i===0?0:-6,
                    background: u.avatar_url ? 'transparent' : `linear-gradient(135deg,${COLORS[i%COLORS.length]},${COLORS[(i+1)%COLORS.length]})`,
                    border:`2px solid ${T.inputBg}`, display:'flex', alignItems:'center',
                    justifyContent:'center', color:'#fff', fontSize:9, fontWeight:700,
                    flexShrink:0, zIndex:4-i, position:'relative', overflow:'hidden',
                  }}>
                    {u.avatar_url
                      ? <img src={`${BASE}${u.avatar_url}`} alt={u.full_name} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                      : u.full_name?.charAt(0)?.toUpperCase()}
                  </div>
                ))}
                {selected.length > 4 && (
                  <div style={{ width:22, height:22, borderRadius:'50%', marginLeft:-6, background:T.bg3, border:`2px solid ${T.inputBg}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, fontWeight:700, color:T.text3, flexShrink:0 }}>
                    +{selected.length-4}
                  </div>
                )}
              </div>
              <span style={{ fontSize:12, color:T.text2, whiteSpace:'nowrap' }}>
                {selected.length === 1 ? selected[0].full_name : `${selected.length} users`}
              </span>
            </>
          )}
        </div>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"
          style={{ flexShrink:0, transform: open?'rotate(180deg)':'rotate(0)', transition:'transform 0.2s' }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position:'absolute', top:'calc(100% + 4px)', left:0, right:0,
          background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:12,
          boxShadow: darkMode?'0 12px 40px rgba(0,0,0,0.5)':'0 12px 40px rgba(15,31,69,0.12)',
          zIndex:9999, overflow:'hidden',
        }}>
          {/* Search */}
          <div style={{ padding:'8px 10px', borderBottom:`1px solid ${T.border}` }}>
            <div style={{ position:'relative' }}>
              <svg style={{ position:'absolute', left:8, top:'50%', transform:'translateY(-50%)' }} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input type="text" placeholder="Search users..." value={search}
                onChange={e => setSearch(e.target.value)} autoFocus
                style={{ width:'100%', paddingLeft:26, paddingRight:10, paddingTop:6, paddingBottom:6, border:`1px solid ${T.border}`, borderRadius:7, fontSize:12, outline:'none', background:T.bg3, color:T.text, boxSizing:'border-box' }} />
            </div>
          </div>

          {/* Assign all toggle */}
          {filtered.length > 1 && (() => {
            const filteredIds = filtered.map(u => u.id);
            const allOn = filteredIds.every(id => selectedUsers.includes(id));
            return (
              <button type="button"
                onClick={() => setSelectedUsers(prev => allOn
                  ? prev.filter(id => !filteredIds.includes(id))
                  : [...new Set([...prev, ...filteredIds])])}
                style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'8px 10px', border:'none', borderBottom:`1px solid ${T.border}`, background:'transparent', cursor:'pointer', fontSize:12.5, fontWeight:700, color:'#0f1f45', textAlign:'left' }}
                onMouseEnter={e => e.currentTarget.style.background=T.bg3}
                onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                <div style={{ width:16, height:16, borderRadius:4, border:`2px solid ${allOn?'#0f1f45':T.border2}`, background: allOn?'#0f1f45':'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  {allOn && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                </div>
                {allOn ? 'Unassign all' : `Assign all${search ? ' (matching)' : ''}`}
              </button>
            );
          })()}

          {/* List — hidden scrollbar, max 5 visible */}
          <div className="cx-noscroll" style={{ maxHeight:200, overflowY:'auto', padding:6 }}>
            {filtered.length === 0 && <p style={{ fontSize:12, color:T.text3, padding:'10px 0', textAlign:'center' }}>No users found</p>}
            {filtered.map((u, i) => {
                const sel = selectedUsers.includes(u.id);
                return (
                  <button key={u.id} type="button"
                    onClick={() => setSelectedUsers(prev => prev.includes(u.id) ? prev.filter(id => id !== u.id) : [...prev, u.id])}
                    style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'7px 8px', borderRadius:8, border:'none', cursor:'pointer', background: sel?(darkMode?'rgba(15,31,69,0.25)':'#f0f4ff'):'transparent', transition:'background 0.1s', textAlign:'left' }}
                    onMouseEnter={e => { if(!sel) e.currentTarget.style.background=T.bg3; }}
                    onMouseLeave={e => { if(!sel) e.currentTarget.style.background='transparent'; }}>
                    <div style={{ width:26, height:26, borderRadius:'50%', background: u.avatar_url ? 'transparent' : `linear-gradient(135deg,${COLORS[i%COLORS.length]},${COLORS[(i+1)%COLORS.length]})`, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:10, fontWeight:700, flexShrink:0, overflow:'hidden' }}>
                      {u.avatar_url
                        ? <img src={`${BASE}${u.avatar_url}`} alt={u.full_name} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                        : u.full_name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <p style={{ fontSize:12, fontWeight:600, color:T.text, margin:0, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{u.full_name}</p>
                      <p style={{ fontSize:10, color:T.text3, margin:0, textTransform:'capitalize' }}>{u.role}</p>
                    </div>
                    <div style={{ width:16, height:16, borderRadius:4, border:`2px solid ${sel?'#0f1f45':T.border2}`, background: sel?'#0f1f45':'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'all 0.15s' }}>
                      {sel && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                    </div>
                  </button>
                );
              })}
          </div>

          {/* Footer */}
          {selectedUsers.length > 0 && (
            <div style={{ padding:'6px 10px', borderTop:`1px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <span style={{ fontSize:11, color:T.text3 }}>{selectedUsers.length} selected</span>
              <button type="button" onClick={() => setSelectedUsers([])}
                style={{ fontSize:11, color:'#ef4444', background:'none', border:'none', cursor:'pointer', fontWeight:600 }}>
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// CLIENT TYPE DROPDOWN — the client's legal constitution. The list is
// firm-editable (Settings → Client Types); this dropdown can also add a new
// one inline. Trigger looks like a normal field; the popup list is rounded
// with hover-highlighted options.
function TypeDropdown({ types, value, onChange, onCreate, darkMode = false, T: theme, placeholder = 'Select Type', addLabel = 'Add new type', addPlaceholder = 'e.g. Section 8 Company' }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const inputRef = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setAdding(false); setSearch(''); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const selected = types.find(t => String(t.value) === String(value));
  const filtered = types.filter(t => t.name.toLowerCase().includes(search.trim().toLowerCase()));

  const submitNew = async (nameOverride) => {
    const name = (nameOverride ?? newName).trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      const created = await onCreate(name);
      if (created?.value) onChange(created.value);
      setNewName(''); setAdding(false); setOpen(false); setSearch('');
    } finally { setBusy(false); }
  };

  return (
    <div ref={ref} style={{ position:'relative' }}>
      <div style={{ position:'relative' }}>
        <input
          ref={inputRef}
          type="text"
          value={open ? search : (selected ? selected.name : '')}
          placeholder={open ? (selected ? selected.name : placeholder) : placeholder}
          onFocus={() => { setOpen(true); setSearch(''); }}
          onChange={e => { setSearch(e.target.value); setOpen(true); }}
          onKeyDown={e => {
            if (e.key === 'Escape') { setOpen(false); setSearch(''); inputRef.current?.blur(); }
            if (e.key === 'Enter') { e.preventDefault(); if (filtered.length === 1) { onChange(filtered[0].value); setOpen(false); setSearch(''); } }
          }}
          style={{
            width:'100%', border:`1px solid ${open ? '#3b82f6' : T.inputBorder}`, borderRadius:9,
            padding:'9px 34px 9px 12px', fontSize:13, background:T.inputBg, color:T.text,
            transition:'border-color 0.15s', boxSizing:'border-box', outline:'none', minHeight:38,
          }} />
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"
          onClick={() => { if (open) { setOpen(false); inputRef.current?.blur(); } else { inputRef.current?.focus(); } }}
          style={{ position:'absolute', right:12, top:'50%', transform: `translateY(-50%) ${open?'rotate(180deg)':''}`, transition:'transform 0.2s', cursor:'pointer' }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </div>

      {open && (
        <div style={{
          position:'absolute', top:'calc(100% + 6px)', left:0, right:0, background:T.bg2,
          border:`1px solid ${T.border2}`, borderRadius:12, zIndex:9999, overflow:'hidden',
          boxShadow: darkMode ? '0 12px 40px rgba(0,0,0,0.5)' : '0 12px 40px rgba(15,31,69,0.14)',
        }}>
          <div className="cx-noscroll" style={{ maxHeight:210, overflowY:'auto', padding:5 }}>
            {filtered.map(t => {
              const sel = String(t.value) === String(value);
              return (
                <button key={t.value} type="button" onClick={() => { onChange(t.value); setOpen(false); setSearch(''); }}
                  style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, color:T.text, textAlign:'left', fontWeight: sel ? 700 : 500, background: sel ? (darkMode?'rgba(59,130,246,0.12)':'#eff6ff') : 'transparent', transition:'background 0.12s' }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = T.bg3; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = 'transparent'; }}>
                  <span style={{ flex:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{t.name}</span>
                  {sel && <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <p style={{ fontSize:12, color:T.text3, padding:'10px' }}>
                {types.length === 0 ? 'No options configured' : `No match for "${search}"`}
              </p>
            )}
          </div>

          <div style={{ borderTop:`1px solid ${T.border}`, padding:5 }}>
            {adding ? (
              <div style={{ display:'flex', gap:6, alignItems:'center', padding:'2px' }}>
                <input autoFocus type="text" value={newName} placeholder={addPlaceholder}
                  onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submitNew(); } if (e.key === 'Escape') { setAdding(false); setNewName(''); } }}
                  style={{ flex:1, border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:12, outline:'none', background:T.bg3, color:T.text, boxSizing:'border-box' }} />
                <button type="button" onClick={() => submitNew()} disabled={busy || !newName.trim()}
                  style={{ padding:'7px 12px', borderRadius:8, border:'none', background:'#0f1f45', color:'#fff', fontSize:12, fontWeight:600, cursor: busy||!newName.trim() ? 'default' : 'pointer', opacity: busy||!newName.trim() ? 0.5 : 1, flexShrink:0 }}>
                  {busy ? '…' : 'Add'}
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => { setAdding(true); setNewName(search.trim()); }}
                style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderRadius:8, border:'none', background:'transparent', cursor:'pointer', fontSize:12.5, fontWeight:600, color:'#3b82f6', textAlign:'left' }}
                onMouseEnter={e => e.currentTarget.style.background = darkMode?'rgba(59,130,246,0.1)':'#eff6ff'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                {search.trim() ? `${addLabel}: "${search.trim()}"` : addLabel}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ASSIGN USER MODAL (quick assign from kebab)
// CLIENT TAGS MODAL — same tags_master the Tasks module uses; a tag created
// here shows up there too, and vice versa. Mirrors Tasks → kebab → Tags.
function ClientTagsModal({ client, allTags, token, onClose, onSuccess, onTagCreated }) {
  const [selected, setSelected] = useState(() =>
    client.tags_data ? client.tags_data.split(',').map(t => parseInt(t.split(':')[0])).filter(id => !isNaN(id)) : []
  );
  const [saving, setSaving] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [creatingTag, setCreatingTag] = useState(false);
  const [toasts, showToast] = useToasts();
  const TAG_COLORS = ['#ef4444','#f97316','#f59e0b','#84cc16','#10b981','#0d9488','#3b82f6','#6366f1','#8b5cf6','#ec4899'];

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    setCreatingTag(true);
    try {
      const color = TAG_COLORS[allTags.length % TAG_COLORS.length];
      const res = await fetch(`${BASE}/api/clients/tags`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ name: newTagName.trim(), color })
      });
      const tag = await res.json();
      if (tag?.id) {
        onTagCreated(tag);
        setSelected(prev => prev.includes(tag.id) ? prev : [...prev, tag.id]);
        setNewTagName('');
      } else showToast(tag.message || 'Could not create tag', 'error');
    } catch { showToast('Error creating tag', 'error'); }
    finally { setCreatingTag(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/clients/${client.id}/tags`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ tag_ids: selected })
      });
      if (res.ok) { onSuccess(); onClose(); }
      else showToast('Error saving tags', 'error');
    } catch { showToast('Error saving tags', 'error'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <ToastStack toasts={toasts} />
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div>
            <p className="text-sm font-bold" style={{ color: '#0f1f45' }}>Tags</p>
            <p className="text-xs text-gray-400 mt-0.5 truncate" style={{ maxWidth: '16rem' }}>
              {client.file_number ? `${client.file_number} · ` : ''}{client.client_name}
            </p>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500 transition">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="p-4 space-y-1.5 max-h-64 overflow-y-auto">
          {allTags.length === 0 && <p className="text-center text-gray-400 text-sm py-4">No tags yet — create one below</p>}
          {allTags.map(tag => {
            const isSelected = selected.includes(tag.id);
            return (
              <label key={tag.id}
                className={`flex items-center gap-3 p-2.5 rounded-xl cursor-pointer transition border ${isSelected ? 'border-gray-200' : 'bg-white border-gray-100 hover:bg-gray-50'}`}
                style={isSelected ? { background: (tag.color || '#6366f1') + '14' } : {}}>
                <div className="w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition"
                  style={isSelected ? { background: tag.color || '#6366f1', borderColor: tag.color || '#6366f1' } : { borderColor: '#d1d5db' }}>
                  {isSelected && (
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  )}
                </div>
                <input type="checkbox" className="hidden" checked={isSelected}
                  onChange={() => setSelected(prev => prev.includes(tag.id) ? prev.filter(id => id !== tag.id) : [...prev, tag.id])} />
                <span className="text-sm font-medium" style={{ color: tag.color || '#374151' }}>{tag.name}</span>
              </label>
            );
          })}
        </div>

        <div className="px-4 pb-3 flex gap-2">
          <input type="text" placeholder="New tag name..." value={newTagName}
            onChange={e => setNewTagName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleCreateTag())}
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
          <button type="button" onClick={handleCreateTag} disabled={creatingTag || !newTagName.trim()}
            className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm hover:bg-gray-50 disabled:opacity-40">
            {creatingTag ? '...' : '+ Add'}
          </button>
        </div>

        <div className="px-5 py-4 border-t flex gap-3">
          <button onClick={onClose}
            className="flex-1 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50"
            style={{ background: '#0f1f45' }}>
            {saving ? 'Saving...' : `Save${selected.length > 0 ? ` (${selected.length})` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function AssignUserModal({ token, client, onClose, onSuccess }) {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [toasts, showToast] = useToasts();
  useEffect(() => {
    fetch(`${BASE}/api/users`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setUsers(Array.isArray(d) ? d : []));
    if (client?.assigned_users_data) {
      const ids = client.assigned_users_data.split(',').map(td => parseInt(td.split(':')[0])).filter(id => !isNaN(id));
      setSelected(ids);
    }
  }, []);
  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/clients/${client.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ ...client, assigned_users: selected })
      });
      if (res.ok) onSuccess();
      else showToast('Failed to assign users', 'error');
    } catch { showToast('Server error', 'error'); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <ToastStack toasts={toasts} />
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div>
            <p className="text-sm font-bold" style={{ color: '#0f1f45' }}>Assign Users</p>
            <p className="text-xs text-gray-400 mt-0.5">{client.client_name}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500 transition">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        {users.length > 0 && (
          <div className="px-5 pt-3 flex items-center justify-between">
            <span className="text-xs text-gray-400">{selected.length} of {users.length} selected</span>
            <button type="button"
              onClick={() => setSelected(prev => prev.length === users.length ? [] : users.map(u => u.id))}
              className="text-xs font-medium text-blue-600 hover:text-blue-800">
              {selected.length === users.length ? 'Clear All' : 'Assign All'}
            </button>
          </div>
        )}
        <div className="p-4 space-y-1.5 max-h-64 overflow-y-auto">
          {users.map(u => (
            <label key={u.id} className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition border ${selected.includes(u.id) ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-100 hover:bg-gray-50'}`}>
              <div className="w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition"
                style={selected.includes(u.id) ? { background: '#0f1f45', borderColor: '#0f1f45' } : { borderColor: '#d1d5db' }}>
                {selected.includes(u.id) && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
              </div>
              <input type="checkbox" className="hidden" checked={selected.includes(u.id)}
                onChange={() => setSelected(prev => prev.includes(u.id) ? prev.filter(id => id !== u.id) : [...prev, u.id])} />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-800">{u.full_name}</p>
                <p className="text-xs text-gray-400 capitalize">{u.role}</p>
              </div>
              {selected.includes(u.id) && <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">Assigned</span>}
            </label>
          ))}
          {users.length === 0 && <p className="text-center text-gray-400 py-4 text-sm">No users found</p>}
        </div>
        <div className="px-5 py-4 border-t flex gap-3">
          <button onClick={onClose} className="flex-1 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50"
            style={{ background: '#0f1f45' }}>
            {saving ? 'Saving...' : `Save (${selected.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}

// CLIENT DASHBOARD
// ── CLIENT TELEGRAM CARD — pairs this specific client's own chat ───────────
function ClientTelegramCard({ clientId, token, darkMode, T }) {
  const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const [status, setStatus] = useState({ connected: false, pairing_code: null, deep_link: null, bot_configured: true });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  const fetchStatus = useCallback(() => {
    fetch(`${BASE}/api/clients/${clientId}/telegram/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setStatus(d); })
      .catch(() => {});
  }, [BASE, clientId, token]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  useEffect(() => {
    if (status.connected || !status.pairing_code) return;
    const interval = setInterval(fetchStatus, 3000);
    return () => clearInterval(interval);
  }, [status.connected, status.pairing_code, fetchStatus]);

  const handleConnect = async () => {
    setLoading(true); setMsg(null);
    try {
      const res = await fetch(`${BASE}/api/clients/${clientId}/telegram/connect`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (res.ok) setStatus(s => ({ ...s, pairing_code: data.pairing_code, deep_link: data.deep_link }));
      else setMsg(data.message || 'Could not generate a connect code');
    } catch (e) { setMsg('Network error'); }
    finally { setLoading(false); }
  };

  const handleDisconnect = async () => {
    await fetch(`${BASE}/api/clients/${clientId}/telegram/disconnect`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    fetchStatus();
  };

  return (
    <div style={{ background:T.bg2, borderRadius:14, padding:20, border:`1px solid ${T.cardBorder}` }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14 }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#229ED9" strokeWidth="2"><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/></svg>
        <h3 style={{ fontSize:10, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.08em', margin:0 }}>Telegram</h3>
      </div>

      {status.connected ? (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <div style={{ width:9, height:9, borderRadius:'50%', background:'#16a34a' }}/>
            <span style={{ fontSize:12.5, color:T.text2, fontWeight:500 }}>Connected — invoices sent here</span>
          </div>
          <button onClick={handleDisconnect} style={{ padding:'5px 12px', borderRadius:7, border:'1px solid #fecaca', background:'transparent', color:'#dc2626', fontSize:11.5, fontWeight:600, cursor:'pointer' }}>
            Disconnect
          </button>
        </div>
      ) : status.pairing_code ? (
        <div style={{ textAlign:'center' }}>
          <p style={{ fontSize:12, color:T.text3, marginBottom:10 }}>Ask the client to open this link and tap Start:</p>
          {status.deep_link ? (
            <a href={status.deep_link} target="_blank" rel="noreferrer"
              style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'8px 16px', borderRadius:8, background:'#229ED9', color:'#fff', fontSize:12.5, fontWeight:700, textDecoration:'none' }}>
              {status.pairing_code} — Open Link →
            </a>
          ) : (
            <span style={{ fontSize:13, fontWeight:700, letterSpacing:2, color:'#229ED9', fontFamily:'monospace' }}>{status.pairing_code}</span>
          )}
          <p style={{ fontSize:11, color:T.text3, marginTop:10 }}>Waiting for confirmation…</p>
        </div>
      ) : (
        <div style={{ textAlign:'center' }}>
          <p style={{ fontSize:12, color:T.text3, marginBottom:10 }}>Connect this client's Telegram to send invoices directly.</p>
          <button onClick={handleConnect} disabled={loading || !status.bot_configured}
            style={{ padding:'7px 16px', borderRadius:8, border:'none', background: status.bot_configured ? '#229ED9' : T.border2, color:'#fff', fontSize:12.5, fontWeight:700, cursor: status.bot_configured ? 'pointer' : 'not-allowed' }}>
            {loading ? 'Generating…' : 'Generate Connect Link'}
          </button>
        </div>
      )}
      {msg && <p style={{ fontSize:11.5, color:'#dc2626', marginTop:10, textAlign:'center' }}>{msg}</p>}
    </div>
  );
}

function ClientDashboard({ client, token, onClose, onEdit, onRefresh, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9' };
  const [activeTab, setActiveTab] = useState('details');
  const [stats, setStats] = useState(null);
  const [isReferralPartner, setIsReferralPartner] = useState(client.is_referral_partner || false);
  const [togglingReferral, setTogglingReferral] = useState(false);
  const [toasts, showToast] = useToasts();
  const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const handleReferralToggle = async () => {
    setTogglingReferral(true);
    try {
      const res = await fetch(`${BASE}/api/referral-partners/toggle-client`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ client_id: client.id, enable: !isReferralPartner })
      });
      if (res.ok) {
        setIsReferralPartner(!isReferralPartner);
        showToast(!isReferralPartner ? 'Marked as referral partner' : 'Removed as referral partner', 'success');
        if (onRefresh) onRefresh();
      } else {
        const d = await res.json().catch(() => ({}));
        showToast(d.message || 'Could not update referral partner status', 'error');
      }
    } catch (_) { showToast('Server error while updating referral partner status', 'error'); }
    setTogglingReferral(false);
  };

  const tabs = [
    { key: 'details', label: 'Details' },
    { key: 'services', label: 'Services' },
    { key: 'retainers', label: 'Retainers' },
    { key: 'tasks', label: 'Tasks' },
    { key: 'documents', label: 'Documents' },
    { key: 'ledger', label: 'Ledger' },
    { key: 'passwords', label: 'Passwords' },
    { key: 'expenses', label: 'Expenses' },
    { key: 'renewals', label: 'Renewals' },
    { key: 'quotations', label: 'Quotations' },
    { key: 'docs_inout',  label: 'Docs In-Out' },
    { key: 'management',  label: 'Management' },
  ];

  const clientTypeLabel = (type) => typeLabelFor(type);

  useEffect(() => {
    // Fetch stats from both endpoints and merge
    Promise.all([
      fetch(`${BASE}/api/clients/${client.id}/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(r => r.json()).catch(() => ({})),
      fetch(`${BASE}/api/tasks?client_id=${client.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(r => r.json()).catch(() => [])
    ]).then(([statsData, tasks]) => {
      const arr = Array.isArray(tasks) ? tasks : [];
      setStats({
        ...statsData,
        pending_tasks: arr.filter(t => t.status === 'pending').length,
        in_progress_tasks: arr.filter(t => t.status === 'in_progress').length,
        hold_tasks: arr.filter(t => t.status === 'hold').length,
        completed_tasks: arr.filter(t => t.status === 'completed').length,
        unbilled_tasks: arr.filter(t => t.billing_status === 'unbilled' && t.status === 'completed').length,
        ledger_balance: statsData?.ledger_balance ?? 0,
      });
    });
  }, [client.id]);

  const assignedUsers = client.assigned_users_data
    ? client.assigned_users_data.split(',').map(t => { const [id,name,avatar] = t.split(':'); return { id, name, avatar }; }).filter(u => u.name)
    : client.assigned_users
      ? client.assigned_users.split(',').map(n => ({ name: n.trim() })).filter(u => u.name)
      : [];

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:50, padding:12 }}>
      <ToastStack toasts={toasts} />
      <div style={{ background:T.bg2, borderRadius:20, display:'flex', flexDirection:'column', boxShadow: darkMode?'0 24px 60px rgba(0,0,0,0.6)':'0 24px 60px rgba(15,31,69,0.2)', width:'100%', maxWidth:1440, height:'94vh' }}>

        {/* TOP HEADER */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 24px', borderBottom:`1px solid ${T.border}`, flexShrink:0, background:T.bg2, borderRadius:'20px 20px 0 0' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <button onClick={onClose} style={{ fontSize:13, fontWeight:600, color:'#3b82f6', background:'none', border:'none', cursor:'pointer', padding:0 }}>Clients</button>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
            <span style={{ fontSize:13, color:T.text3 }}>Client Dashboard</span>
            <div style={{ width:1, height:16, background:T.border2, margin:'0 4px' }}/>
            <span style={{ fontSize:11, fontWeight:600, color:'#3b82f6', background: darkMode?'rgba(59,130,246,0.12)':'#eff6ff', padding:'2px 8px', borderRadius:6 }}>{client.file_number}</span>
            <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:20,
              background: client.is_active?(darkMode?'rgba(16,185,129,0.12)':'#f0fdf4'):(darkMode?'rgba(239,68,68,0.12)':'#fef2f2'),
              color: client.is_active?'#16a34a':'#dc2626',
              border: `1px solid ${client.is_active?(darkMode?'rgba(16,185,129,0.25)':'#bbf7d0'):(darkMode?'rgba(239,68,68,0.25)':'#fecaca')}`,
            }}>
              {client.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            {/* Referral Partner Toggle */}
            <div style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 12px', borderRadius:9, border:`1px solid ${isReferralPartner?(darkMode?'rgba(249,115,22,0.3)':'#fed7aa'):(T.border2)}`, background:isReferralPartner?(darkMode?'rgba(249,115,22,0.1)':'#fff7ed'):T.bg2 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={isReferralPartner?'#f97316':T.text3} strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              <span style={{ fontSize:12, fontWeight:600, color:isReferralPartner?'#f97316':T.text3 }}>Referral Partner</span>
              <div onClick={togglingReferral ? null : handleReferralToggle}
                style={{ width:36, height:20, borderRadius:10, background:isReferralPartner?'#f97316':(darkMode?'#334155':'#d1d5db'), position:'relative', cursor:togglingReferral?'wait':'pointer', transition:'background 0.2s', flexShrink:0 }}>
                <div style={{ position:'absolute', top:2, left:isReferralPartner?18:2, width:16, height:16, borderRadius:'50%', background:'#fff', transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }}/>
              </div>
            </div>
            <button onClick={onEdit} style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 14px', borderRadius:9, border:`1.5px solid ${T.border2}`, background:T.bg2, color:T.text2, fontSize:13, fontWeight:500, cursor:'pointer' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor='#f97316'; e.currentTarget.style.color='#f97316'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor=T.border2; e.currentTarget.style.color=T.text2; }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              Edit
            </button>
            <button onClick={onClose} style={{ width:32, height:32, borderRadius:'50%', border:'none', background:T.bg3, color:T.text3, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>

        {/* BODY */}
        <div style={{ display:'flex', flex:1, minHeight:0 }}>

          {/* LEFT SIDEBAR */}
          <div style={{ width:208, borderRight:`1px solid ${T.border}`, flexShrink:0, display:'flex', flexDirection:'column', overflowY:'auto', scrollbarWidth:'none', msOverflowStyle:'none', background:T.bg3, borderRadius:'0 0 0 20px' }}>

            {/* Client Identity */}
            <div style={{ padding:16, borderBottom:`1px solid ${T.border}`, background:T.bg2 }}>
              <h2 style={{ fontSize:14, fontWeight:700, color:T.text, margin:0, lineHeight:1.3 }}>{client.client_name}</h2>
              <p style={{ fontSize:11, color:T.text3, marginTop:4 }}>{clientTypeLabel(client.client_type)}</p>
              {client.group_name && (
                <p style={{ fontSize:11, color:T.text3, marginTop:6, display:'flex', alignItems:'center', gap:4 }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                  {client.group_name}
                </p>
              )}
              {client.tags && (
                <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginTop:8 }}>
                  {client.tags.split(',').slice(0,3).map((tag,i) => (
                    <span key={i} style={{ padding:'2px 7px', background: darkMode?'rgba(99,102,241,0.12)':'#eef2ff', color:'#6366f1', borderRadius:20, fontSize:10, fontWeight:600 }}>{tag.trim()}</span>
                  ))}
                </div>
              )}
            </div>

            {/* Assigned Users */}
            <div style={{ padding:16, borderBottom:`1px solid ${T.border}` }}>
              <p style={{ fontSize:10, fontWeight:700, color:T.text, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:10 }}>Assigned To</p>
              {assignedUsers.length === 0
                ? <p style={{ fontSize:11, color:T.text3, fontStyle:'italic' }}>No users assigned</p>
                : (
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {assignedUsers.map((u,i) => (
                      <div key={i} style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <div style={{ width:26, height:26, borderRadius:'50%', background: u.avatar ? 'transparent' : 'linear-gradient(135deg,#0f1f45,#1a3a6b)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:10, fontWeight:700, flexShrink:0, overflow:'hidden' }}>
                          {u.avatar
                            ? <img src={`${BASE}${u.avatar}`} alt={u.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                            : u.name.charAt(0).toUpperCase()}
                        </div>
                        <p style={{ fontSize:12, fontWeight:500, color:T.text2, margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{u.name}</p>
                      </div>
                    ))}
                  </div>
                )
              }
            </div>

            {/* Quick Actions */}
            <div style={{ padding:16 }}>
              <p style={{ fontSize:10, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:10 }}>Quick Actions</p>
              <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
                {client.mobile && (
                  <a href={`https://wa.me/91${client.mobile}`} target="_blank" rel="noreferrer"
                    style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 10px', borderRadius:9, textDecoration:'none', transition:'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background=darkMode?'rgba(37,211,102,0.1)':'#f0fdf4'}
                    onMouseLeave={e => e.currentTarget.style.background='none'}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    <span style={{ fontSize:12, color:T.text2 }}>{client.mobile}</span>
                  </a>
                )}
                {client.email && (
                  <a href={`mailto:${client.email}`}
                    style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 10px', borderRadius:9, textDecoration:'none', transition:'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background=darkMode?'rgba(59,130,246,0.1)':'#eff6ff'}
                    onMouseLeave={e => e.currentTarget.style.background='none'}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    <span style={{ fontSize:12, color:T.text2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:120 }}>{client.email}</span>
                  </a>
                )}
                {client.mobile && (
                  <a href={`tel:+91${client.mobile}`}
                    style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 10px', borderRadius:9, textDecoration:'none', transition:'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background=darkMode?'rgba(99,102,241,0.1)':'#eef2ff'}
                    onMouseLeave={e => e.currentTarget.style.background='none'}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.96a16 16 0 0 0 6.09 6.09l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                    <span style={{ fontSize:12, color:T.text2 }}>{client.mobile}</span>
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT CONTENT */}
          <div style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0 }}>

            {/* Stats Bar */}
            <div style={{ display:'flex', alignItems:'stretch', borderBottom:`1px solid ${T.border}`, flexShrink:0, background:T.bg2 }}>
              {[
                { label:'Pending Tasks',  value: stats?.pending_tasks ?? '—',  color:'#f59e0b' },
                { label:'In-Progress',    value: stats?.in_progress_tasks ?? '—', color:'#3b82f6' },
                { label:'Hold Tasks',     value: stats?.hold_tasks ?? '—',     color:'#8b5cf6' },
                { label:'Completed',      value: stats?.completed_tasks ?? '—', color:'#10b981' },
                { label:'Unbilled Tasks', value: stats?.unbilled_tasks ?? '—', color:'#ef4444' },
                { label:'Ledger Balance',
                  value: stats?.ledger_balance != null
                    ? `₹${Math.abs(Number(stats.ledger_balance)).toLocaleString('en-IN',{minimumFractionDigits:2})}${Number(stats.ledger_balance) < 0 ? ' Cr' : ''}`
                    : '₹0.00',
                  color: stats?.ledger_balance < 0 ? '#16a34a' : (darkMode?'#e2e8f0':'#0f1f45') },
              ].map((s, i, arr) => (
                <div key={s.label} style={{ flex:1, padding:'10px 16px', textAlign:'center', borderRight: i<arr.length-1?`1px solid ${T.border}`:'none' }}>
                  <p style={{ fontSize:15, fontWeight:800, color:s.color, margin:0 }}>{s.value}</p>
                  <p style={{ fontSize:10, color:T.text3, marginTop:3, whiteSpace:'nowrap' }}>{s.label}</p>
                </div>
              ))}
            </div>

            {/* Tabs */}
            <div style={{ display:'flex', borderBottom:`1px solid ${T.border}`, flexShrink:0, overflowX:'auto', scrollbarWidth:'none', background:T.bg2, padding:'0 8px' }}>
              {tabs.map(tab => (
                <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
                  padding:'12px 16px', fontSize:13, fontWeight:600, whiteSpace:'nowrap',
                  border:'none', borderBottom: activeTab===tab.key?'2px solid #3b82f6':'2px solid transparent',
                  color: activeTab===tab.key?'#3b82f6':T.text3,
                  background:'none', cursor:'pointer', transition:'all 0.15s',
                }}
                onMouseEnter={e => { if(activeTab!==tab.key) e.currentTarget.style.color=T.text2; }}
                onMouseLeave={e => { if(activeTab!==tab.key) e.currentTarget.style.color=T.text3; }}>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div style={{ flex:1, overflowY:'auto', padding:24, background:T.bg3 }}>

              {activeTab === 'details' && (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
                  {/* Left: Basic Info + Settings */}
                  <div style={{ background:T.bg2, borderRadius:14, padding:20, border:`1px solid ${T.cardBorder}` }}>
                    <h3 style={{ fontSize:10, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.08em', margin:'0 0 14px' }}>Basic Information</h3>
                    <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                      {[
                        { label:'Client Type',      value: clientTypeLabel(client.client_type) },
                        { label:'Contact Person',   value: client.contact_person },
                        { label:'Date of Birth / Incorporation', value: client.date_of_birth ? new Date(client.date_of_birth).toLocaleDateString('en-IN') : null },
                        { label:'PAN',              value: client.pan },
                        { label:'GSTIN',            value: client.gstin },
                        { label:'CIN / LLPIN',      value: client.cin },
                        { label:'Opening Balance',  value: client.opening_balance ? `₹${Number(client.opening_balance).toLocaleString('en-IN')}` : '₹0' },
                      ].map(item => item.value ? (
                        <div key={item.label} style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
                          <span style={{ fontSize:12, color:T.text3, width:130, flexShrink:0 }}>{item.label}</span>
                          <span style={{ fontSize:13, color:T.text, fontWeight:500 }}>{item.value}</span>
                        </div>
                      ) : null)}
                    </div>

                    <div style={{ borderTop:`1px solid ${T.border}`, marginTop:18, paddingTop:18 }}>
                      <h3 style={{ fontSize:10, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.08em', margin:'0 0 12px' }}>Settings</h3>
                      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                        {[
                          { label:'Allow Notifications', value: client.allow_notifications },
                          { label:'Allow Portal Login',  value: client.allow_login },
                          { label:'Auto-create Invoice', value: client.create_invoice_automatically },
                        ].map(item => (
                          <div key={item.label} style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                            <span style={{ fontSize:13, color:T.text2 }}>{item.label}</span>
                            <span style={{ fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:20,
                              background: item.value?(darkMode?'rgba(16,185,129,0.12)':'#f0fdf4'):(darkMode?'rgba(100,116,139,0.12)':'#f8fafc'),
                              color: item.value?'#16a34a':T.text3,
                            }}>
                              {item.value ? 'Enabled' : 'Disabled'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Right: Contact + Notes */}
                  <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
                    <div style={{ background:T.bg2, borderRadius:14, padding:20, border:`1px solid ${T.cardBorder}` }}>
                      <h3 style={{ fontSize:10, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.08em', margin:'0 0 14px' }}>Contact Details</h3>
                      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                        {[
                          { label:'Mobile',           value: client.mobile },
                          { label:'Secondary Mobile', value: client.secondary_mobile },
                          { label:'Email',            value: client.email },
                          { label:'Address',          value: client.address },
                          { label:'City',             value: client.city },
                          { label:'State',            value: client.state },
                          { label:'Pincode',          value: client.pincode },
                        ].map(item => item.value ? (
                          <div key={item.label} style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
                            <span style={{ fontSize:12, color:T.text3, width:130, flexShrink:0 }}>{item.label}</span>
                            <span style={{ fontSize:13, color:T.text, fontWeight:500 }}>{item.value}</span>
                          </div>
                        ) : null)}
                      </div>
                    </div>
                    <ClientTelegramCard clientId={client.id} token={token} darkMode={darkMode} T={T} />
                    {client.notes && (
                      <div style={{ background:T.bg2, borderRadius:14, padding:20, border:`1px solid ${T.cardBorder}` }}>
                        <h3 style={{ fontSize:10, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.08em', margin:'0 0 10px' }}>Notes</h3>
                        <p style={{ fontSize:13, color:T.text2, lineHeight:1.6, margin:0 }}>{client.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'services'    && <ServicesTab    clientId={client.id} token={token} darkMode={darkMode} T={T} />}
              {activeTab === 'retainers'   && <RetainersTab   clientId={client.id} token={token} darkMode={darkMode} T={T} />}
              {activeTab === 'documents'   && <DocumentsTab   clientId={client.id} token={token} darkMode={darkMode} T={T} />}
              {activeTab === 'passwords'   && <PasswordsTab   clientId={client.id} token={token} darkMode={darkMode} T={T} />}
              {activeTab === 'expenses'    && <ExpensesTab    clientId={client.id} token={token} darkMode={darkMode} T={T} />}
              {activeTab === 'renewals'    && <RenewalsTab    clientId={client.id} token={token} darkMode={darkMode} T={T} />}
              {activeTab === 'docs_inout'  && <DocsInOutTab   clientId={client.id} token={token} darkMode={darkMode} T={T} />}
              {activeTab === 'management'  && <ManagementTab  clientId={client.id} token={token} darkMode={darkMode} T={T} />}
              {activeTab === 'tasks'       && <ClientTasksTab clientId={client.id} token={token} darkMode={darkMode} T={T} />}
              {activeTab === 'ledger'      && <ClientLedgerInline clientId={client.id} token={token} darkMode={darkMode} T={T} />}
              {activeTab === 'quotations'  && <QuotationsTab  clientId={client.id} token={token} darkMode={darkMode} T={T} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// SERVICES TAB — two panels: "Availed Services" (any service this client has
// ever had a task against, any status) and "Recurring Services" (active
// enrollment via Service Module → service → Clients tab), each recurring
// entry tagged with its current/upcoming period.
function ServicesTab({ clientId, token, darkMode = false, T: theme }) {
  const T = theme || { bg:'#f1f5f9', bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9' };
  const [data, setData] = useState({ availed: [], recurring: [] });
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [tasksByService, setTasksByService] = useState({});

  useEffect(() => {
    fetch(`${BASE}/api/clients/${clientId}/services`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setData({ availed: Array.isArray(d.availed) ? d.availed : [], recurring: Array.isArray(d.recurring) ? d.recurring : [] }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [clientId]);

  // Expand-to-view — fetches this client's tasks once, then filters
  // client-side per service (small dataset per client, no need for a
  // dedicated per-service endpoint).
  const toggleExpand = async (serviceId) => {
    if (expandedId === serviceId) { setExpandedId(null); return; }
    setExpandedId(serviceId);
    if (tasksByService[serviceId]) return;
    try {
      const res = await fetch(`${BASE}/api/tasks?client_id=${clientId}`, { headers: { 'Authorization': `Bearer ${token}` } });
      const all = await res.json();
      const grouped = {};
      (Array.isArray(all) ? all : []).forEach(t => {
        if (!t.service_id) return;
        (grouped[t.service_id] = grouped[t.service_id] || []).push(t);
      });
      setTasksByService(grouped);
    } catch { /* leave collapsed on error */ }
  };

  const categoryColor = (cat) => {
    const map = {
      'GST & Indirect Tax': 'bg-emerald-50 text-emerald-700 border-emerald-200',
      'Income Tax & TDS': 'bg-blue-50 text-blue-700 border-blue-200',
      'Company Law (ROC/MCA)': 'bg-purple-50 text-purple-700 border-purple-200',
      'Audit & Assurance': 'bg-orange-50 text-orange-700 border-orange-200',
      'Accounting & Bookkeeping': 'bg-yellow-50 text-yellow-700 border-yellow-200',
      'Registration & Licenses': 'bg-pink-50 text-pink-700 border-pink-200',
      'Payroll & HR': 'bg-cyan-50 text-cyan-700 border-cyan-200',
    };
    return map[cat] || 'bg-gray-50 text-gray-600 border-gray-200';
  };

  if (loading) return <div className="py-12 text-center text-gray-400 text-sm">Loading services...</div>;

  const { availed, recurring } = data;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      {/* AVAILED SERVICES */}
      <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-800">Availed Services</h3>
          <p className="text-xs text-gray-400 mt-0.5">{availed.length} service{availed.length !== 1 ? 's' : ''}</p>
        </div>
        {availed.length === 0 ? (
          <div className="py-12 text-center px-5">
            <svg className="w-10 h-10 text-gray-200 mx-auto mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg>
            <p className="text-gray-400 text-xs">No services availed yet — this fills in once a task is created for this client with a service linked.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50 max-h-[520px] overflow-y-auto">
            {availed.map(s => (
              <div key={s.id}>
                <button onClick={() => toggleExpand(s.id)}
                  className="w-full flex items-center justify-between gap-3 px-5 py-3 hover:bg-gray-50 transition text-left">
                  <div className="min-w-0 flex items-center gap-2">
                    {s.category && (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border flex-shrink-0 ${categoryColor(s.category)}`}>
                        {s.category.split(' ')[0]}
                      </span>
                    )}
                    <span className="text-sm font-medium text-blue-600 truncate">{s.name}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[11px] text-gray-400">{s.completed_count}/{s.task_count} done</span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                      className="text-gray-300" style={{ transform: expandedId === s.id ? 'rotate(180deg)' : 'none', transition:'transform 0.15s' }}>
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  </div>
                </button>
                {expandedId === s.id && (
                  <div className="px-5 pb-3 pl-8 space-y-1.5">
                    {(tasksByService[s.id] || []).map(t => (
                      <button key={t.id}
                        onClick={() => window.dispatchEvent(new CustomEvent('navigateTo', { detail: { page: 'tasks', taskId: t.id } }))}
                        className="w-full flex items-center justify-between text-xs px-2.5 py-1.5 rounded-lg hover:bg-blue-50 transition">
                        <span className="font-mono font-semibold text-blue-600">{t.task_number || `#${t.id}`}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${t.status === 'completed' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                          {t.status === 'completed' ? (t.completed_date ? new Date(t.completed_date).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : 'Completed') : (t.status || 'pending')}
                        </span>
                      </button>
                    ))}
                    {!tasksByService[s.id] && <p className="text-[11px] text-gray-300">Loading…</p>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* RECURRING SERVICES */}
      <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">Recurring Services</h3>
            <p className="text-xs text-gray-400 mt-0.5">{recurring.length} active enrollment{recurring.length !== 1 ? 's' : ''}</p>
          </div>
          <button onClick={() => window.dispatchEvent(new CustomEvent('navigateTo', { detail: { page: 'services' } }))}
            title="Manage in Service Module"
            className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:bg-blue-50 hover:text-blue-600 transition flex-shrink-0">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
          </button>
        </div>
        {recurring.length === 0 ? (
          <div className="py-12 text-center px-5">
            <svg className="w-10 h-10 text-gray-200 mx-auto mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M23 4v6h-6M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
            <p className="text-gray-400 text-xs">Not enrolled in any recurring service yet — enroll from Service Module → the service → Clients tab.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50 max-h-[520px] overflow-y-auto">
            {recurring.map(s => (
              <div key={s.id} className="flex items-center justify-between gap-3 px-5 py-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {s.category && (
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border flex-shrink-0 ${categoryColor(s.category)}`}>
                        {s.category.split(' ')[0]}
                      </span>
                    )}
                    <span className="text-sm font-medium text-gray-800 truncate">{s.name}</span>
                  </div>
                  {s.auto_task_frequency && s.auto_task_frequency !== 'none' && (
                    <p className="text-[11px] text-gray-400 mt-0.5 capitalize">{s.auto_task_frequency.replace('_', '-')}</p>
                  )}
                </div>
                {s.period_label && (
                  <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold flex-shrink-0"
                    style={{ background: darkMode ? 'rgba(59,130,246,0.15)' : '#eff6ff', color: '#3b82f6' }}>
                    {s.period_label}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// RETAINERS TAB
function RetainersTab({ clientId, token }) {
  const [retainers, setRetainers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    retainer_name: '', billing_frequency: 'monthly', amount: '',
    start_date: '', renew_automatically: false, gst_rate: '18',
    sac_code: '', billing_mode: 'postpaid', create_invoice_automatically: true
  });
  const [saving, setSaving] = useState(false);

  const ic = "w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500 bg-white";

  const fetchRetainers = () => fetch(`${BASE}/api/clients/${clientId}/retainers`, {
    headers: { 'Authorization': `Bearer ${token}` }
  }).then(r => r.json()).then(d => setRetainers(Array.isArray(d) ? d : [])).catch(console.error);

  useEffect(() => { fetchRetainers(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    await fetch(`${BASE}/api/clients/${clientId}/retainers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(form)
    });
    setSaving(false);
    setShowForm(false);
    setForm({ retainer_name: '', billing_frequency: 'monthly', amount: '', start_date: '', renew_automatically: false, gst_rate: '18', sac_code: '', billing_mode: 'postpaid', create_invoice_automatically: true });
    fetchRetainers();
  };

  const freqLabel = (f) => ({ monthly:'Monthly', quarterly:'Quarterly', half_yearly:'Half Yearly', yearly:'Yearly', one_time:'One-Time', non_billable:'Non-Billable' }[f] || f);
  const freqColor = (f) => ({ monthly:'bg-blue-50 text-blue-700', quarterly:'bg-purple-50 text-purple-700', half_yearly:'bg-orange-50 text-orange-700', yearly:'bg-green-50 text-green-700' }[f] || 'bg-gray-100 text-gray-600');

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-sm font-semibold text-gray-700">Retainers</h3>
          <p className="text-xs text-gray-400 mt-0.5">{retainers.length} active retainer{retainers.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => setShowForm(s => !s)}
          className="px-4 py-2 rounded-lg text-white text-sm font-medium flex items-center gap-1.5"
          style={{ background: '#0f1f45' }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Retainer
        </button>
      </div>

      {showForm && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-6 shadow-sm">
          <h4 className="text-sm font-semibold text-gray-700 mb-5 pb-3 border-b border-gray-100">New Retainer</h4>
          <form onSubmit={handleAdd}>
            <div className="grid grid-cols-2 gap-5">
              {/* Left column */}
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Retainer Name *</label>
                  <input required type="text" placeholder="e.g. Annual Compliance Package" value={form.retainer_name}
                    onChange={e => setForm(f => ({ ...f, retainer_name: e.target.value }))} className={ic} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Billing Frequency *</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[['monthly','Monthly'],['quarterly','Quarterly'],['half_yearly','Half Yearly'],['yearly','Yearly'],['one_time','One-Time'],['non_billable','Non-Billable']].map(([v,l]) => (
                      <button key={v} type="button"
                        onClick={() => setForm(f => ({ ...f, billing_frequency: v }))}
                        className={`py-1.5 rounded-lg text-xs font-medium border transition ${form.billing_frequency === v ? 'text-white border-transparent' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                        style={form.billing_frequency === v ? { background: '#0f1f45' } : {}}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Total Amount (₹) *</label>
                    <input required type="number" placeholder="0.00" value={form.amount}
                      onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} className={ic} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">GST Rate (%)</label>
                    <select value={form.gst_rate} onChange={e => setForm(f => ({ ...f, gst_rate: e.target.value }))} className={ic}>
                      {['0','5','12','18','28'].map(r => <option key={r} value={r}>{r}%</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">SAC Code</label>
                  <input type="text" placeholder="e.g. 998231" value={form.sac_code}
                    onChange={e => setForm(f => ({ ...f, sac_code: e.target.value }))} className={ic} />
                </div>
              </div>

              {/* Right column */}
              <div className="space-y-4">
                <SmartDatePicker label="Billing Starts From *" value={form.start_date}
                  onChange={v => setForm(f => ({ ...f, start_date: v }))} />

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Billing Mode *</label>
                  <div className="flex gap-2">
                    {[['prepaid','Prepaid'],['postpaid','Postpaid']].map(([v,l]) => (
                      <button key={v} type="button"
                        onClick={() => setForm(f => ({ ...f, billing_mode: v }))}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium border transition ${form.billing_mode === v ? 'text-white border-transparent' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                        style={form.billing_mode === v ? { background: '#0f1f45' } : {}}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  {[
                    { key: 'renew_automatically', label: 'Renew Automatically', desc: 'Auto-renew when period ends' },
                    { key: 'create_invoice_automatically', label: 'Create Invoice Automatically', desc: 'Generate invoice each cycle' },
                  ].map(({ key, label, desc }) => (
                    <div key={key} className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-700">{label}</p>
                        <p className="text-xs text-gray-400">{desc}</p>
                      </div>
                      <button type="button" onClick={() => setForm(f => ({ ...f, [key]: !f[key] }))}
                        className={`relative w-10 h-5 rounded-full transition flex-shrink-0 ${form[key] ? 'bg-blue-600' : 'bg-gray-300'}`}>
                        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form[key] ? 'translate-x-5' : ''}`}></span>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-5 pt-4 border-t border-gray-100">
              <button type="button" onClick={() => setShowForm(false)}
                className="px-5 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={saving}
                className="px-6 py-2 rounded-lg text-white text-sm font-medium ml-auto disabled:opacity-50"
                style={{ background: '#0f1f45' }}>
                {saving ? 'Saving...' : 'Save Retainer'}
              </button>
            </div>
          </form>
        </div>
      )}

      {retainers.length === 0 && !showForm ? (
        <div className="py-16 text-center">
          <svg className="w-12 h-12 text-gray-200 mx-auto mb-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          <p className="text-gray-500 font-medium text-sm">No retainers yet</p>
          <p className="text-gray-400 text-xs mt-1">Add recurring billing arrangements for this client</p>
        </div>
      ) : (
        <div className="space-y-3">
          {retainers.map(r => (
            <div key={r.id} className="bg-white border border-gray-100 rounded-xl p-4 hover:shadow-sm transition">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${freqColor(r.billing_frequency)}`}>
                      {freqLabel(r.billing_frequency)}
                    </span>
                    {r.renew_automatically && (
                      <span className="px-2 py-0.5 bg-green-50 text-green-600 rounded-md text-xs font-medium flex items-center gap-1">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 4v6h-6M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
                        Auto-renew
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-semibold text-gray-800">{r.retainer_name}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                    {r.start_date && <span>From {new Date(r.start_date).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}</span>}
                    {r.billing_mode && <span className="capitalize">{r.billing_mode}</span>}
                    {r.sac_code && <span>SAC: {r.sac_code}</span>}
                    {r.gst_rate && <span>GST {r.gst_rate}%</span>}
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-4">
                  <p className="text-lg font-bold text-gray-800">
                    {r.amount ? `₹${Number(r.amount).toLocaleString('en-IN', { minimumFractionDigits: 0 })}` : '—'}
                  </p>
                  <p className="text-xs text-gray-400">per {freqLabel(r.billing_frequency).toLowerCase()}</p>
                  {r.billing_status && (
                    <span className={`mt-1 inline-block px-2 py-0.5 rounded-full text-xs font-medium ${r.billing_status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {r.billing_status}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// DOCUMENTS TAB
function DocumentsTab({ clientId, token, darkMode = false, T: theme }) {
  const [confirm, setConfirm] = useState(null);
  const showConfirm = (title, message, onConfirm) => setConfirm({ title, message, onConfirm });
  const [toasts, showToast] = useToasts();
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff' };
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetch(`${BASE}/api/clients/${clientId}/documents`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setDocuments(Array.isArray(d) ? d : [])).catch(console.error);
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { showToast('File too large. Max size is 10MB.', 'error'); e.target.value = ''; return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${BASE}/api/clients/${clientId}/documents/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        showToast(d.message || 'Upload failed', 'error');
      } else {
        const doc = await res.json();
        setDocuments(prev => [doc, ...prev]);
        showToast('Document uploaded successfully.', 'success');
      }
    } catch (err) {
      showToast('Cannot connect to server.', 'error');
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleDelete = (docId) => {
    showConfirm('Delete Document', 'Delete this document? This cannot be undone.', async () => {
      try {
        const res = await fetch(`${BASE}/api/clients/${clientId}/documents/${docId}`, {
          method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) { setDocuments(prev => prev.filter(d => d.id !== docId)); showToast('Document deleted successfully.', 'success'); }
        else showToast('Could not delete document', 'error');
      } catch { showToast('Server error', 'error'); }
    });
  };

  return (
    <div>
      <ConfirmModal open={!!confirm} title={confirm?.title||''} message={confirm?.message||''} onConfirm={()=>{confirm?.onConfirm();setConfirm(null);}} onCancel={()=>setConfirm(null)} />
      <ToastStack toasts={toasts} />
      {/* Upload Area */}
      <div style={{ border:`2px dashed ${T.border2}`, borderRadius:12, padding:32, textAlign:'center', marginBottom:16, transition:'border-color 0.2s' }}
        onMouseEnter={e => e.currentTarget.style.borderColor='#3b82f6'}
        onMouseLeave={e => e.currentTarget.style.borderColor=T.border2}>
        <input type="file" id="doc-upload" style={{ display:'none' }} onChange={handleUpload}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.zip" />
        <label htmlFor="doc-upload" style={{ cursor:'pointer' }}>
          <div style={{ display:'flex', justifyContent:'center', marginBottom:10 }}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
          </div>
          <p style={{ fontSize:13, fontWeight:600, color:T.text2, margin:0 }}>
            {uploading ? 'Uploading...' : 'Drop files here or click to upload'}
          </p>
          <p style={{ fontSize:11, color:T.text3, marginTop:6 }}>PDF, DOC, XLS, JPG, PNG, ZIP — Max 10MB</p>
        </label>
      </div>

      {/* Files List */}
      {documents.length === 0 ? (
        <p style={{ textAlign:'center', color:T.text3, padding:'16px 0', fontSize:13 }}>No documents uploaded yet.</p>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {documents.map(doc => (
            <div key={doc.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', border:`1px solid ${T.border}`, borderRadius:10, background:T.bg2, transition:'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background=T.bg3}
              onMouseLeave={e => e.currentTarget.style.background=T.bg2}>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                {/* File type icon */}
                <div style={{ width:36, height:36, borderRadius:8, background: darkMode?'rgba(59,130,246,0.12)':'#eff6ff', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                    <polyline points="10 9 9 9 8 9"/>
                  </svg>
                </div>
                <div>
                  <p style={{ fontSize:13, fontWeight:600, color:T.text, margin:0 }}>{doc.file_name}</p>
                  <p style={{ fontSize:11, color:T.text3, margin:'2px 0 0' }}>{doc.file_size} · Uploaded by {doc.uploaded_by_name}</p>
                </div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                {doc.file_url && doc.file_url !== '#' && (
                  <a href={doc.file_url.startsWith('http') ? doc.file_url : `${BASE}${doc.file_url}`} target="_blank" rel="noreferrer"
                    style={{ display:'flex', alignItems:'center', gap:5, fontSize:12, fontWeight:600, color:'#3b82f6', textDecoration:'none' }}
                    onMouseEnter={e => e.currentTarget.style.textDecoration='underline'}
                    onMouseLeave={e => e.currentTarget.style.textDecoration='none'}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    Download
                  </a>
                )}
                <button onClick={() => handleDelete(doc.id)}
                  style={{ background:'none', border:'none', cursor:'pointer', color:'#ef4444', padding:6, borderRadius:6, display:'flex', alignItems:'center' }}
                  onMouseEnter={e => e.currentTarget.style.background=darkMode?'rgba(239,68,68,0.12)':'#fef2f2'}
                  onMouseLeave={e => e.currentTarget.style.background='none'}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// PORTAL DROPDOWN — same rounded-list/hover-highlight/inline-create pattern
// as TypeDropdown (Client Type), just wired to portals {id, name, url}
// instead of {value, name}.
function PortalDropdown({ portals, value, onSelect, onCreate, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setAdding(false); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const selected = portals.find(p => String(p.id) === String(value));

  const submitNew = async () => {
    const name = newName.trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      const created = await onCreate(name);
      if (created?.id) onSelect(created);
      setNewName(''); setAdding(false); setOpen(false);
    } finally { setBusy(false); }
  };

  return (
    <div ref={ref} style={{ position:'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{
          width:'100%', border:`1px solid ${open ? '#3b82f6' : T.inputBorder}`, borderRadius:9,
          padding:'9px 12px', fontSize:13, textAlign:'left', display:'flex', alignItems:'center',
          justifyContent:'space-between', background:T.inputBg, cursor:'pointer', minHeight:38,
          transition:'border-color 0.15s', boxSizing:'border-box',
        }}>
        <span style={{ color: selected ? T.text : T.text3, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
          {selected ? selected.name : 'Select a portal...'}
        </span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"
          style={{ flexShrink:0, marginLeft:6, transform: open ? 'rotate(180deg)' : 'none', transition:'transform 0.2s' }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {open && (
        <div style={{
          position:'absolute', top:'calc(100% + 6px)', left:0, right:0, background:T.bg2,
          border:`1px solid ${T.border2}`, borderRadius:12, zIndex:9999, overflow:'hidden',
          boxShadow: darkMode ? '0 12px 40px rgba(0,0,0,0.5)' : '0 12px 40px rgba(15,31,69,0.14)',
        }}>
          <div className="cx-noscroll" style={{ maxHeight:210, overflowY:'auto', padding:5 }}>
            {portals.map(p => {
              const sel = String(p.id) === String(value);
              return (
                <button key={p.id} type="button" onClick={() => { onSelect(p); setOpen(false); }}
                  style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, color:T.text, textAlign:'left', fontWeight: sel ? 700 : 500, background: sel ? (darkMode?'rgba(59,130,246,0.12)':'#eff6ff') : 'transparent', transition:'background 0.12s' }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = T.bg3; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = 'transparent'; }}>
                  <span style={{ flex:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{p.name}</span>
                  {sel && <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                </button>
              );
            })}
            {portals.length === 0 && <p style={{ fontSize:12, color:T.text3, padding:'10px' }}>No portals configured</p>}
          </div>

          <div style={{ borderTop:`1px solid ${T.border}`, padding:5 }}>
            {adding ? (
              <div style={{ display:'flex', gap:6, alignItems:'center', padding:'2px' }}>
                <input autoFocus type="text" value={newName} placeholder="e.g. GST Portal"
                  onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submitNew(); } if (e.key === 'Escape') { setAdding(false); setNewName(''); } }}
                  style={{ flex:1, border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:12, outline:'none', background:T.bg3, color:T.text, boxSizing:'border-box' }} />
                <button type="button" onClick={submitNew} disabled={busy || !newName.trim()}
                  style={{ padding:'7px 12px', borderRadius:8, border:'none', background:'#0f1f45', color:'#fff', fontSize:12, fontWeight:600, cursor: busy||!newName.trim() ? 'default' : 'pointer', opacity: busy||!newName.trim() ? 0.5 : 1, flexShrink:0 }}>
                  {busy ? '…' : 'Add'}
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => setAdding(true)}
                style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderRadius:8, border:'none', background:'transparent', cursor:'pointer', fontSize:12.5, fontWeight:600, color:'#3b82f6', textAlign:'left' }}
                onMouseEnter={e => e.currentTarget.style.background = darkMode?'rgba(59,130,246,0.1)':'#eff6ff'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Add new portal
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// PASSWORDS TAB
function PasswordsTab({ clientId, token, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [confirm, setConfirm] = useState(null);
  const showConfirm = (title, message, onConfirm) => setConfirm({ title, message, onConfirm });
  const [toasts, showToast] = useToasts();
  const [passwords, setPasswords] = useState([]);
  const [portals, setPortals] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showPwd, setShowPwd] = useState({});
  const [copied, setCopied] = useState({});
  const [saving, setSaving] = useState(false);
  const [selectedPortalId, setSelectedPortalId] = useState('');
  const [form, setForm] = useState({ portal_name: '', portal_url: '', username: '', password_encrypted: '', notes: '' });

  const ic = "w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500 bg-white";

  const fetchPasswords = () => fetch(`${BASE}/api/clients/${clientId}/passwords`, {
    headers: { 'Authorization': `Bearer ${token}` }
  }).then(r => r.json()).then(d => setPasswords(Array.isArray(d) ? d : [])).catch(console.error);

  const fetchPortals = () => fetch(`${BASE}/api/portals`, { headers: { 'Authorization': `Bearer ${token}` } })
    .then(r => r.json()).then(d => setPortals(Array.isArray(d) ? d : [])).catch(() => setPortals([]));

  useEffect(() => {
    fetchPasswords();
    fetchPortals();
  }, []);

  const handlePortalSelect = (portal) => {
    setSelectedPortalId(portal.id);
    setForm(f => ({ ...f, portal_name: portal.name, portal_url: portal.url || '' }));
  };

  const handleCreatePortal = async (name) => {
    try {
      const res = await fetch(`${BASE}/api/portals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ name })
      });
      const portal = await res.json();
      if (portal?.id) {
        setPortals(prev => [...prev, portal].sort((a, b) => a.name.localeCompare(b.name)));
      } else {
        showToast(portal?.message || 'Could not create portal', 'error');
      }
      return portal;
    } catch {
      showToast('Error creating portal', 'error');
      return null;
    }
  };

  const resetForm = () => {
    setForm({ portal_name: '', portal_url: '', username: '', password_encrypted: '', notes: '' });
    setSelectedPortalId('');
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    await fetch(`${BASE}/api/clients/${clientId}/passwords`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(form)
    });
    setSaving(false);
    setShowForm(false);
    resetForm();
    fetchPasswords();
  };

  const handleDelete = (pwdId) => {
    showConfirm('Delete Credential', 'Delete this credential? This cannot be undone.', async () => {
      await fetch(`${BASE}/api/clients/${clientId}/passwords/${pwdId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      setPasswords(prev => prev.filter(p => p.id !== pwdId));
    });
  };

  const copyText = (key, text) => {
    navigator.clipboard.writeText(text);
    setCopied(prev => ({ ...prev, [key]: true }));
    setTimeout(() => setCopied(prev => ({ ...prev, [key]: false })), 2000);
  };

  // Portal icon by name
  const portalIcon = (name = '') => {
    const n = name.toLowerCase();
    if (n.includes('gst')) return { bg: 'bg-orange-50', color: 'text-orange-600', letter: 'G' };
    if (n.includes('mca') || n.includes('roc')) return { bg: 'bg-purple-50', color: 'text-purple-600', letter: 'M' };
    if (n.includes('income') || n.includes('itr') || n.includes('tax')) return { bg: 'bg-blue-50', color: 'text-blue-600', letter: 'I' };
    if (n.includes('tds') || n.includes('traces')) return { bg: 'bg-cyan-50', color: 'text-cyan-600', letter: 'T' };
    if (n.includes('epf') || n.includes('pf')) return { bg: 'bg-green-50', color: 'text-green-600', letter: 'E' };
    if (n.includes('esic')) return { bg: 'bg-teal-50', color: 'text-teal-600', letter: 'ES' };
    if (n.includes('gmail') || n.includes('mail')) return { bg: 'bg-red-50', color: 'text-red-500', letter: '@' };
    return { bg: 'bg-gray-100', color: 'text-gray-600', letter: name.charAt(0).toUpperCase() };
  };

  return (
    <div>
      <ConfirmModal open={!!confirm} title={confirm?.title||''} message={confirm?.message||''} onConfirm={()=>{confirm?.onConfirm();setConfirm(null);}} onCancel={()=>setConfirm(null)} />
      <ToastStack toasts={toasts} />
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-sm font-semibold text-gray-700">Portal Credentials</h3>
          <p className="text-xs text-gray-400 mt-0.5">{passwords.length} saved credential{passwords.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => window.dispatchEvent(new CustomEvent('navigateTo', { detail: { page: 'settings', section: 'portals' } }))}
            className="px-3 py-2 rounded-lg border border-gray-200 text-gray-500 text-xs hover:bg-gray-50 flex items-center gap-1.5">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
            Manage Portals
          </button>
          <button onClick={() => setShowForm(s => !s)}
            className="px-4 py-2 rounded-lg text-white text-sm font-medium flex items-center gap-1.5"
            style={{ background: '#0f1f45' }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add Credential
          </button>
        </div>
      </div>

      {showForm && (
        <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-6 shadow-sm">
          <h4 className="text-sm font-semibold text-gray-700 mb-4 pb-3 border-b border-gray-100">New Portal Credential</h4>
          <form onSubmit={handleAdd}>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Select Portal
                  <span className="text-gray-400 font-normal ml-1">(from portal list)</span>
                </label>
                <PortalDropdown portals={portals} value={selectedPortalId} onSelect={handlePortalSelect}
                  onCreate={handleCreatePortal} darkMode={darkMode} T={T} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Portal URL</label>
                <input type="text" placeholder="https://portal.example.com" value={form.portal_url}
                  onChange={e => setForm(f => ({ ...f, portal_url: e.target.value }))} className={ic} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Username / Login ID *</label>
                <input required type="text" placeholder="Username or email" value={form.username}
                  onChange={e => setForm(f => ({ ...f, username: e.target.value }))} className={ic} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Password *</label>
                <input required type="text" placeholder="Enter password" value={form.password_encrypted}
                  onChange={e => setForm(f => ({ ...f, password_encrypted: e.target.value }))} className={ic} />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                <input type="text" placeholder="Optional notes (e.g. 2FA enabled)" value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className={ic} />
              </div>
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => { setShowForm(false); resetForm(); }}
                className="px-5 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={saving}
                className="px-6 py-2 rounded-lg text-white text-sm font-medium ml-auto disabled:opacity-50"
                style={{ background: '#0f1f45' }}>
                {saving ? 'Saving...' : 'Save Credential'}
              </button>
            </div>
          </form>
        </div>
      )}

      {passwords.length === 0 && !showForm ? (
        <div className="py-16 text-center">
          <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          </div>
          <p className="text-gray-500 font-medium text-sm">No credentials saved</p>
          <p className="text-gray-400 text-xs mt-1">Store portal login details securely for this client</p>
        </div>
      ) : (
        <div className="space-y-2">
          {passwords.map(p => {
            const icon = portalIcon(p.portal_name);
            return (
              <div key={p.id} className="bg-white border border-gray-100 rounded-xl px-4 py-3.5 flex items-center gap-4 hover:shadow-sm transition">
                {/* Portal Icon */}
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 ${icon.bg} ${icon.color}`}>
                  {icon.letter}
                </div>

                {/* Portal Name */}
                <div className="w-36 flex-shrink-0">
                  {p.portal_url ? (
                    <a href={p.portal_url.startsWith('http') ? p.portal_url : `https://${p.portal_url}`}
                      target="_blank" rel="noreferrer"
                      className="text-sm font-semibold text-blue-600 hover:underline flex items-center gap-1">
                      {p.portal_name}
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                    </a>
                  ) : (
                    <p className="text-sm font-semibold text-gray-800">{p.portal_name}</p>
                  )}
                  {p.notes && <p className="text-xs text-gray-400 truncate max-w-32 mt-0.5">{p.notes}</p>}
                </div>

                {/* Username */}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">Username</p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700 font-mono truncate">{p.username}</span>
                    <button onClick={() => copyText(`u-${p.id}`, p.username)}
                      title="Copy username"
                      className="flex-shrink-0 w-6 h-6 rounded-md hover:bg-gray-100 flex items-center justify-center transition">
                      {copied[`u-${p.id}`]
                        ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                        : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>}
                    </button>
                  </div>
                </div>

                {/* Password */}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">Password</p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700 font-mono">
                      {showPwd[p.id] ? p.password_encrypted : '•'.repeat(Math.min(p.password_encrypted?.length || 8, 12))}
                    </span>
                    <button onClick={() => setShowPwd(prev => ({ ...prev, [p.id]: !prev[p.id] }))}
                      className="flex-shrink-0 w-6 h-6 rounded-md hover:bg-gray-100 flex items-center justify-center transition">
                      {showPwd[p.id]
                        ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                        : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
                    </button>
                    <button onClick={() => copyText(`p-${p.id}`, p.password_encrypted)}
                      className="flex-shrink-0 w-6 h-6 rounded-md hover:bg-gray-100 flex items-center justify-center transition">
                      {copied[`p-${p.id}`]
                        ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                        : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>}
                    </button>
                  </div>
                </div>

                {/* Delete */}
                <button onClick={() => handleDelete(p.id)}
                  className="w-8 h-8 rounded-lg hover:bg-red-50 flex items-center justify-center transition flex-shrink-0" title="Delete">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// NAMED-ITEM DROPDOWN — the same rounded-list/hover-highlight/inline-create
// pattern as TypeDropdown (Client Type) and PortalDropdown (Select Portal),
// generalized to any simple {name}-keyed master list (expense categories,
// renewal types, ...). Matches/selects by `name` string, same as those two.
function NamedDropdown({ items, value, onChange, onCreate, placeholder = 'Select...', addLabel = 'Add new', emptyText = 'No items configured', createPlaceholder = 'Enter name', darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setAdding(false); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const selected = items.find(i => String(i.name) === String(value));

  const submitNew = async () => {
    const name = newName.trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      const created = await onCreate(name);
      if (created?.name) onChange(created.name);
      setNewName(''); setAdding(false); setOpen(false);
    } finally { setBusy(false); }
  };

  return (
    <div ref={ref} style={{ position:'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{
          width:'100%', border:`1px solid ${open ? '#3b82f6' : T.inputBorder}`, borderRadius:9,
          padding:'9px 12px', fontSize:13, textAlign:'left', display:'flex', alignItems:'center',
          justifyContent:'space-between', background:T.inputBg, cursor:'pointer', minHeight:38,
          transition:'border-color 0.15s', boxSizing:'border-box',
        }}>
        <span style={{ color: selected ? T.text : T.text3, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
          {selected ? selected.name : placeholder}
        </span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"
          style={{ flexShrink:0, marginLeft:6, transform: open ? 'rotate(180deg)' : 'none', transition:'transform 0.2s' }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {open && (
        <div style={{
          position:'absolute', top:'calc(100% + 6px)', left:0, right:0, background:T.bg2,
          border:`1px solid ${T.border2}`, borderRadius:12, zIndex:9999, overflow:'hidden',
          boxShadow: darkMode ? '0 12px 40px rgba(0,0,0,0.5)' : '0 12px 40px rgba(15,31,69,0.14)',
        }}>
          <div className="cx-noscroll" style={{ maxHeight:210, overflowY:'auto', padding:5 }}>
            {items.map(i => {
              const sel = String(i.name) === String(value);
              return (
                <button key={i.id || i.name} type="button" onClick={() => { onChange(i.name); setOpen(false); }}
                  style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, color:T.text, textAlign:'left', fontWeight: sel ? 700 : 500, background: sel ? (darkMode?'rgba(59,130,246,0.12)':'#eff6ff') : 'transparent', transition:'background 0.12s' }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = T.bg3; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = 'transparent'; }}>
                  <span style={{ flex:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{i.name}</span>
                  {sel && <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                </button>
              );
            })}
            {items.length === 0 && <p style={{ fontSize:12, color:T.text3, padding:'10px' }}>{emptyText}</p>}
          </div>

          {onCreate && (
            <div style={{ borderTop:`1px solid ${T.border}`, padding:5 }}>
              {adding ? (
                <div style={{ display:'flex', gap:6, alignItems:'center', padding:'2px' }}>
                  <input autoFocus type="text" value={newName} placeholder={createPlaceholder}
                    onChange={e => setNewName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submitNew(); } if (e.key === 'Escape') { setAdding(false); setNewName(''); } }}
                    style={{ flex:1, border:`1px solid ${T.inputBorder}`, borderRadius:8, padding:'7px 10px', fontSize:12, outline:'none', background:T.bg3, color:T.text, boxSizing:'border-box' }} />
                  <button type="button" onClick={submitNew} disabled={busy || !newName.trim()}
                    style={{ padding:'7px 12px', borderRadius:8, border:'none', background:'#0f1f45', color:'#fff', fontSize:12, fontWeight:600, cursor: busy||!newName.trim() ? 'default' : 'pointer', opacity: busy||!newName.trim() ? 0.5 : 1, flexShrink:0 }}>
                    {busy ? '…' : 'Add'}
                  </button>
                </div>
              ) : (
                <button type="button" onClick={() => setAdding(true)}
                  style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'8px 10px', borderRadius:8, border:'none', background:'transparent', cursor:'pointer', fontSize:12.5, fontWeight:600, color:'#3b82f6', textAlign:'left' }}
                  onMouseEnter={e => e.currentTarget.style.background = darkMode?'rgba(59,130,246,0.1)':'#eff6ff'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  {addLabel}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// EXPENSES TAB
const EMPTY_EXPENSE_FORM = { expense_date: '', category: '', mode: 'cash', bank_account_id: '', notes: '', amount: '', billing_status: 'unbilled', receipt_url: '' };
function ExpensesTab({ clientId, token, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [confirm, setConfirm] = useState(null);
  const showConfirm = (title, message, onConfirm) => setConfirm({ title, message, onConfirm });
  const [toasts, showToast] = useToasts();
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_EXPENSE_FORM);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  const ic = "w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-blue-500 bg-white";

  const fetchCategories = () => fetch(`${BASE}/api/expense-categories`, { headers: { 'Authorization': `Bearer ${token}` } })
    .then(r => r.json()).then(d => setCategories(Array.isArray(d) ? d : [])).catch(() => setCategories([]));

  useEffect(() => {
    fetch(`${BASE}/api/clients/${clientId}/expenses`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setExpenses(Array.isArray(d) ? d : [])).catch(console.error);
    fetchCategories();
    fetch(`${BASE}/api/bank/accounts`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setBankAccounts(Array.isArray(d) ? d.filter(a => a.is_active !== false) : [])).catch(() => setBankAccounts([]));
  }, []);

  const fetchExpenses = () => fetch(`${BASE}/api/clients/${clientId}/expenses`, {
    headers: { 'Authorization': `Bearer ${token}` }
  }).then(r => r.json()).then(d => setExpenses(Array.isArray(d) ? d : [])).catch(console.error);

  // Inline "+ Add new category" quick-create, called by NamedDropdown.
  const handleAddCategory = async (name) => {
    try {
      const res = await fetch(`${BASE}/api/expense-categories`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ name })
      });
      const created = await res.json().catch(() => ({}));
      if (res.ok && created?.name) {
        setCategories(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      } else {
        showToast(created?.message || 'Could not create category', 'error');
      }
      return created;
    } catch {
      showToast('Error creating category', 'error');
      return null;
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const isEdit = !!editingId;
    const url = isEdit
      ? `${BASE}/api/clients/${clientId}/expenses/${editingId}`
      : `${BASE}/api/clients/${clientId}/expenses`;
    try {
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        showToast(d.message || `Could not ${isEdit ? 'update' : 'add'} expense`, 'error');
        return;
      }
      setShowForm(false);
      setEditingId(null);
      setForm(EMPTY_EXPENSE_FORM);
      showToast(`Expense ${isEdit ? 'updated' : 'added'} successfully.`, 'success');
      fetchExpenses();
    } catch {
      showToast('Cannot connect to server.', 'error');
    }
  };

  const handleEditClick = (exp) => {
    setEditingId(exp.id);
    setForm({
      expense_date: exp.expense_date ? new Date(exp.expense_date).toISOString().split('T')[0] : '',
      category: exp.category || '',
      mode: exp.mode || 'cash',
      bank_account_id: exp.bank_account_id ? String(exp.bank_account_id) : '',
      notes: exp.notes || '',
      amount: exp.amount || '',
      billing_status: exp.billing_status || 'unbilled',
      receipt_url: exp.receipt_url || '',
    });
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_EXPENSE_FORM);
  };

  const handleDelete = (exp) => {
    showConfirm('Delete Expense', `Delete this ₹${Number(exp.amount).toLocaleString('en-IN')} expense (${exp.category || 'uncategorized'})? This cannot be undone.`, async () => {
      try {
        const res = await fetch(`${BASE}/api/clients/${clientId}/expenses/${exp.id}`, {
          method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) { setExpenses(prev => prev.filter(e => e.id !== exp.id)); showToast('Expense deleted successfully.', 'success'); }
        else { const d = await res.json().catch(() => ({})); showToast(d.message || 'Could not delete expense', 'error'); }
      } catch { showToast('Server error', 'error'); }
    });
  };

  const handleDocUpload = async (file) => {
    if (!file) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData(); fd.append('file', file); fd.append('upload_preset', 'ca_software');
      const res = await fetch('https://api.cloudinary.com/v1_1/your_cloud_name/raw/upload', { method: 'POST', body: fd });
      const data = await res.json();
      setForm(f => ({ ...f, receipt_url: data.secure_url || '' }));
    } catch {
      setForm(f => ({ ...f, receipt_url: file.name + ' (local)' }));
    }
    setUploadingDoc(false);
  };

  const totalAmount = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);
  const unbilled = expenses.filter(e => e.billing_status === 'unbilled').reduce((s, e) => s + Number(e.amount || 0), 0);
  const billed = expenses.filter(e => e.billing_status === 'billed').reduce((s, e) => s + Number(e.amount || 0), 0);
  const nonBillable = expenses.filter(e => e.billing_status === 'non_billable').reduce((s, e) => s + Number(e.amount || 0), 0);

  const MODES = ['cash','bank_transfer','upi','cheque','card','online','other'];

  return (
    <div>
      <ConfirmModal open={!!confirm} title={confirm?.title||''} message={confirm?.message||''} onConfirm={()=>{confirm?.onConfirm();setConfirm(null);}} onCancel={()=>setConfirm(null)} />
      <ToastStack toasts={toasts} />
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-sm font-semibold text-gray-700">Client Expenses</h3>
          <p className="text-xs text-gray-400 mt-0.5">{expenses.length} expense{expenses.length !== 1 ? 's' : ''} · ₹{totalAmount.toLocaleString('en-IN')} total</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => window.dispatchEvent(new CustomEvent('navigateTo', { detail: { page: 'settings', section: 'expense-categories' } }))}
            className="px-3 py-2 rounded-lg border border-gray-200 text-gray-500 text-xs hover:bg-gray-50 flex items-center gap-1.5">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
            Manage Categories
          </button>
          <button onClick={() => { setEditingId(null); setForm(EMPTY_EXPENSE_FORM); setShowForm(s => !s); }}
            className="px-4 py-2 rounded-lg text-white text-sm font-medium flex items-center gap-1.5"
            style={{ background: '#0f1f45' }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add Expense
          </button>
        </div>
      </div>

      {/* Summary strip */}
      {expenses.length > 0 && (
        <div className="grid grid-cols-4 gap-3 mb-5">
          {[
            { label: 'Total Expenses', value: `₹${totalAmount.toLocaleString('en-IN')}`, color: 'text-gray-800' },
            { label: 'Pending Billing', value: `₹${unbilled.toLocaleString('en-IN')}`, color: 'text-orange-600' },
            { label: 'Billed', value: `₹${billed.toLocaleString('en-IN')}`, color: 'text-green-600' },
            { label: 'Borne by Firm', value: `₹${nonBillable.toLocaleString('en-IN')}`, color: 'text-gray-500' },
          ].map(s => (
            <div key={s.label} className="bg-white border border-gray-100 rounded-xl px-4 py-3">
              <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">{s.label}</p>
              <p className={`text-lg font-bold mt-1 ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-6 shadow-sm">
          <h4 className="text-sm font-semibold text-gray-700 mb-4 pb-3 border-b border-gray-100">{editingId ? 'Edit Expense' : 'Add Expense'}</h4>
          <form onSubmit={handleAdd}>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <SmartDatePicker label="Expense Date *" value={form.expense_date}
                onChange={v => setForm(f => ({ ...f, expense_date: v }))} />
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                <NamedDropdown items={categories} value={form.category}
                  onChange={v => setForm(f => ({ ...f, category: v }))}
                  onCreate={handleAddCategory}
                  placeholder="Select category..." addLabel="Add new category"
                  emptyText="No categories configured" createPlaceholder="e.g. Stamp Duty"
                  darkMode={darkMode} T={T} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Bank Account *</label>
                <select value={form.bank_account_id} onChange={e => setForm(f => ({ ...f, bank_account_id: e.target.value }))} className={ic}>
                  <option value="">Cash</option>
                  {bankAccounts.map(a => <option key={a.id} value={a.id}>{a.bank_name}{a.nickname ? ` — ${a.nickname}` : ''}{a.account_last4 ? ` (••${a.account_last4})` : ''}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Payment Mode</label>
                <select value={form.mode} onChange={e => setForm(f => ({ ...f, mode: e.target.value }))} className={ic}>
                  {MODES.map(m => <option key={m} value={m}>{m.replace(/_/g,' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Amount (₹) *</label>
                <input required type="number" placeholder="0.00" value={form.amount}
                  onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} className={ic} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Billing Required?</label>
                {form.billing_status === 'billed' ? (
                  <div className="py-2.5 px-3 rounded-lg border border-gray-200 bg-gray-50 text-sm font-medium text-green-700">
                    ✓ Already billed to client
                  </div>
                ) : (
                  <div className="flex gap-2">
                    {[['unbilled','Yes'],['non_billable','No']].map(([v,l]) => (
                      <button key={v} type="button"
                        onClick={() => setForm(f => ({ ...f, billing_status: v }))}
                        className={`flex-1 py-2.5 rounded-lg text-sm font-medium border transition ${form.billing_status === v ? 'text-white border-transparent' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                        style={form.billing_status === v ? { background: '#0f1f45' } : {}}>
                        {l}
                      </button>
                    ))}
                  </div>
                )}
                <p className="text-[10px] text-gray-400 mt-1 leading-tight">
                  {form.billing_status==='non_billable'
                    ? 'Borne by the firm — stays out of any client invoice.'
                    : form.billing_status==='billed' ? '' : 'Pending — will show up when creating this client\'s invoice.'}
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                <input type="text" placeholder="Description or remarks" value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className={ic} />
              </div>
            </div>

            {/* Document upload */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-600 mb-2">Proof / Receipt Document</label>
              <label className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-dashed cursor-pointer transition
                ${form.receipt_url ? 'border-green-300 bg-green-50' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'}`}>
                <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png"
                  onChange={e => handleDocUpload(e.target.files[0])} />
                {form.receipt_url ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                    <span className="text-sm text-green-700 font-medium">Document attached</span>
                    <button type="button" onClick={() => setForm(f => ({ ...f, receipt_url: '' }))}
                      className="ml-auto text-xs text-red-400 hover:text-red-600">Remove</button>
                  </>
                ) : uploadingDoc ? (
                  <><span className="text-sm text-gray-400">Uploading...</span></>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                    <span className="text-sm text-gray-400">Upload receipt or proof (PDF, JPG, PNG)</span>
                  </>
                )}
              </label>
            </div>

            <div className="flex gap-3">
              <button type="button" onClick={handleCancelForm}
                className="px-5 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm hover:bg-gray-50">Cancel</button>
              <button type="submit" className="px-6 py-2 rounded-lg text-white text-sm font-medium ml-auto"
                style={{ background: '#0f1f45' }}>{editingId ? 'Update Expense' : 'Save Expense'}</button>
            </div>
          </form>
        </div>
      )}

      {expenses.length === 0 && !showForm ? (
        <div className="py-16 text-center">
          <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
          </div>
          <p className="text-gray-500 font-medium text-sm">No expenses recorded</p>
          <p className="text-gray-400 text-xs mt-1">Track billable and non-billable expenses for this client</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
              <tr>
                {['Date','Category','Mode','Amount','Billing','Task Ref','Notes','Proof','Actions'].map(h => (
                  <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {expenses.map(e => (
                <tr key={e.id} className="border-b border-gray-50 hover:bg-gray-50 transition">
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                    {e.expense_date ? new Date(e.expense_date).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-700">
                    {e.category || '—'}
                    {e.bank_txn_id && (
                      <div className="mt-1" title={`Linked to a Bank Statement row${e.bank_txn_bank_name ? ` — ${e.bank_txn_bank_name}${e.bank_txn_bank_nickname ? ` (${e.bank_txn_bank_nickname})` : ''}` : ''}${e.bank_txn_date ? `, ${new Date(e.bank_txn_date).toLocaleDateString('en-IN')}` : ''}`}>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold text-blue-700 bg-blue-50 border border-blue-100">
                          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                          Bank Statement
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 capitalize">{e.mode?.replace(/_/g,' ') || '—'}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-800">₹{Number(e.amount).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      e.billing_status === 'billed' ? 'bg-green-50 text-green-700'
                        : e.billing_status === 'non_billable' ? 'bg-gray-100 text-gray-500'
                        : 'bg-orange-50 text-orange-700'
                    }`}>
                      {e.billing_status === 'billed' ? 'Billed' : e.billing_status === 'non_billable' ? 'Not Required' : 'Pending Billing'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {e.task_number ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-mono font-semibold text-blue-700 bg-blue-50 border border-blue-100" title={e.task_title || ''}>
                        {e.task_number}
                      </span>
                    ) : <span className="text-gray-300 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 max-w-32 truncate">{e.notes || '—'}</td>
                  <td className="px-4 py-3">
                    {e.receipt_url && e.receipt_url !== '' ? (
                      <a href={e.receipt_url} target="_blank" rel="noreferrer"
                        className="flex items-center gap-1 text-blue-600 hover:underline text-xs font-medium">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                        View
                      </a>
                    ) : <span className="text-gray-300 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {e.billing_status === 'billed' ? (
                      <span className="text-gray-300 text-xs" title="Already billed on an invoice — remove it from that invoice first to edit or delete.">Locked</span>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => handleEditClick(e)} title="Edit"
                          className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:bg-blue-50 hover:text-blue-600 transition">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        <button onClick={() => handleDelete(e)} title="Delete"
                          className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:bg-red-50 hover:text-red-600 transition">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// RENEWALS TAB
function RenewalsTab({ clientId, token, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', inputBg:'#fff', inputBorder:'#e2e8f0' };
  const [toasts, showToast] = useToasts();
  const [renewals, setRenewals] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [renewalTypesList, setRenewalTypesList] = useState([]);
  const [form, setForm] = useState({
    renewal_type: '',
    issue_date: '',
    expiry_date: '',
    notes: '',
    alert_before_days: 30
  });

  const headers = { 'Authorization': `Bearer ${token}` };

  const fetchRenewalTypes = () => {
    fetch(`${BASE}/api/renewal-types`, { headers })
      .then(r => r.json())
      .then(d => {
        setRenewalTypesList(Array.isArray(d) ? d : []);
      })
      .catch(() => setRenewalTypesList([]));
  };

  const fetchRenewals = () => {
    fetch(`${BASE}/api/clients/${clientId}/renewals`, { headers })
      .then(r => r.json())
      .then(d => setRenewals(Array.isArray(d) ? d : []))
      .catch(console.error);
  };

  useEffect(() => {
    fetchRenewalTypes();
    fetchRenewals();
  }, [clientId, token]);

  // Inline "+ Add new renewal type" quick-create, called by NamedDropdown.
  const handleAddRenewalType = async (name) => {
    try {
      const res = await fetch(`${BASE}/api/renewal-types`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name })
      });
      const created = await res.json().catch(() => ({}));
      if (res.ok && created?.name) {
        setRenewalTypesList(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      } else {
        showToast(created?.message || 'Could not create renewal type', 'error');
      }
      return created;
    } catch {
      showToast('Error creating renewal type', 'error');
      return null;
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();

    await fetch(`${BASE}/api/clients/${clientId}/renewals`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(form)
    });

    setShowForm(false);
    setForm({
      renewal_type: '',
      issue_date: '',
      expiry_date: '',
      notes: '',
      alert_before_days: 30
    });

    fetchRenewals();
  };

  const daysUntilExpiry = (date) => {
    const diff = new Date(date) - new Date();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const expiryStatus = (date) => {
    const days = daysUntilExpiry(date);
    if (days < 0) return { label: 'Expired', class: 'bg-red-100 text-red-700' };
    if (days <= 30) return { label: `${days}d left`, class: 'bg-orange-100 text-orange-700' };
    if (days <= 90) return { label: `${days}d left`, class: 'bg-yellow-100 text-yellow-700' };
    return { label: `${days}d left`, class: 'bg-green-100 text-green-700' };
  };

  const ic = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500";

  return (
    <div>
      <ToastStack toasts={toasts} />
      <div className="flex justify-between items-center mb-5">
        <div>
          <h3 className="text-sm font-semibold text-gray-700">Renewals & Expiry Tracker</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            {renewals.length} renewal{renewals.length !== 1 ? 's' : ''} tracked
          </p>
        </div>

        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 rounded-lg text-white text-sm font-medium flex items-center gap-1.5"
          style={{ background: '#0f1f45' }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Add Renewal
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleAdd} className="bg-gray-50 rounded-xl p-4 mb-4 border border-gray-100">
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Renewal Type *</label>
              <NamedDropdown items={renewalTypesList} value={form.renewal_type}
                onChange={v => setForm(f => ({ ...f, renewal_type: v }))}
                onCreate={handleAddRenewalType}
                placeholder="Select renewal type..." addLabel="Add new renewal type"
                emptyText="No renewal types configured" createPlaceholder="e.g. Trade License"
                darkMode={darkMode} T={T} />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Alert Before Days</label>
              <input
                type="number"
                value={form.alert_before_days}
                onChange={e => setForm(f => ({ ...f, alert_before_days: e.target.value }))}
                className={ic}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Issue Date</label>
              <input
                type="date"
                value={form.issue_date}
                onChange={e => setForm(f => ({ ...f, issue_date: e.target.value }))}
                className={ic}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Expiry Date *</label>
              <input
                required
                type="date"
                value={form.expiry_date}
                onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))}
                className={ic}
              />
            </div>

            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
              <input
                type="text"
                placeholder="Certificate number, remarks, authority, etc."
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                className={ic}
              />
            </div>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="flex-1 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={!form.renewal_type || !form.expiry_date}
              className="flex-1 py-2 rounded-lg text-white text-sm disabled:opacity-50"
              style={{ background: '#0f1f45' }}
            >
              Add Renewal
            </button>
          </div>
        </form>
      )}

      {renewals.length === 0 ? (
        <p className="text-center text-gray-400 py-8 text-sm">No renewals tracked yet.</p>
      ) : (
        <div className="space-y-3">
          {renewals.map(r => {
            const status = expiryStatus(r.expiry_date);

            return (
              <div key={r.id} className="flex items-start justify-between p-4 border border-gray-200 rounded-xl hover:bg-gray-50">
                <div>
                  <p className="text-sm font-semibold text-gray-800">{r.renewal_type}</p>

                  <p className="text-xs text-gray-500 mt-1">
                    {r.issue_date && `Issued: ${new Date(r.issue_date).toLocaleDateString('en-IN')} · `}
                    Expires: {new Date(r.expiry_date).toLocaleDateString('en-IN')}
                  </p>

                  {r.notes && <p className="text-xs text-gray-600 mt-1">{r.notes}</p>}
                </div>

                <span className={`px-3 py-1 rounded-full text-xs font-medium flex-shrink-0 ${status.class}`}>
                  {status.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// DOCS IN-OUT TAB
function DocsInOutTab({ clientId, token }) {
  const [docs, setDocs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ direction: 'out', document_name: '', document_date: '', given_received_by: '', notes: '' });

  useEffect(() => {
    fetch(`${BASE}/api/clients/${clientId}/docs-inout`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setDocs(Array.isArray(d) ? d : [])).catch(console.error);
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    await fetch(`${BASE}/api/clients/${clientId}/docs-inout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(form)
    });
    setShowForm(false);
    fetch(`${BASE}/api/clients/${clientId}/docs-inout`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json()).then(d => setDocs(Array.isArray(d) ? d : []));
  };

  const ic = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500";

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-semibold text-gray-700">Document In-Out Register</h3>
        <button onClick={() => setShowForm(!showForm)}
          className="px-3 py-1.5 rounded-lg text-white text-xs font-medium" style={{ background: '#0f1f45' }}>
          + Add Entry
        </button>
      </div>
      {showForm && (
        <form onSubmit={handleAdd} className="bg-gray-50 rounded-xl p-4 mb-4">
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Direction *</label>
              <select value={form.direction} onChange={e => setForm(f => ({ ...f, direction: e.target.value }))} className={ic}>
                <option value="out">OUT — Document given to client</option>
                <option value="in">IN — Document received from client</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Date *</label>
              <input required type="date" value={form.document_date} onChange={e => setForm(f => ({ ...f, document_date: e.target.value }))} className={ic} />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Document Name *</label>
              <input required type="text" placeholder="e.g. Original PAN Card, MOA" value={form.document_name} onChange={e => setForm(f => ({ ...f, document_name: e.target.value }))} className={ic} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Given / Received By</label>
              <input type="text" placeholder="Person name" value={form.given_received_by} onChange={e => setForm(f => ({ ...f, given_received_by: e.target.value }))} className={ic} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
              <input type="text" placeholder="Any notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className={ic} />
            </div>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm">Cancel</button>
            <button type="submit" className="flex-1 py-2 rounded-lg text-white text-sm" style={{ background: '#0f1f45' }}>Save</button>
          </div>
        </form>
      )}
      {docs.length === 0 ? (
        <p className="text-center text-gray-400 py-8 text-sm">No document movements recorded yet.</p>
      ) : (
        <table className="w-full">
          <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
            <tr>
              {['Date', 'Direction', 'Document', 'Given/Received By', 'Notes'].map(h => (
                <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {docs.map(d => (
              <tr key={d.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-600">
                  {new Date(d.document_date).toLocaleDateString('en-IN')}
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${d.direction === 'out' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                    {d.direction === 'out' ? '↗ OUT' : '↙ IN'}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm font-medium text-gray-800">{d.document_name}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{d.given_received_by || '-'}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{d.notes || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
function PlaceholderTab({ icon, title, desc }) {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="text-center">
        <div className="text-5xl mb-3">{icon}</div>
        <h3 className="text-lg font-semibold text-gray-700">{title}</h3>
        <p className="text-sm text-gray-400 mt-1">{desc}</p>
        <span className="mt-3 inline-block px-3 py-1 bg-blue-50 text-blue-600 text-xs rounded-full">
          🚧 Coming Soon
        </span>
      </div>
    </div>
  );
}

function QuotationsTab({ clientId, token }) {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${BASE}/api/clients/${clientId}`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json()),
      fetch(`${BASE}/api/proposals/all`, { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json())
    ]).then(([clientData, allProposals]) => {
      const filtered = Array.isArray(allProposals) ? allProposals.filter(p =>
        p.lead_company?.toLowerCase() === clientData?.client_name?.toLowerCase() ||
        p.lead_name?.toLowerCase() === clientData?.client_name?.toLowerCase()
      ) : [];
      setQuotations(filtered);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [clientId, token]);

  const STATUS_STYLE = {
    draft:    'bg-gray-50 text-gray-600 border border-gray-200',
    sent:     'bg-blue-50 text-blue-700 border border-blue-200',
    accepted: 'bg-green-50 text-green-700 border border-green-200',
    rejected: 'bg-red-50 text-red-700 border border-red-200',
  };

  const openQuotation = (q) => {
    window.dispatchEvent(new CustomEvent('navigateTo', { detail: { page: 'crm', tab: 'quotation', quoteId: q.id } }));
  };

  const totalAccepted = quotations.filter(q => q.status === 'accepted').reduce((s, q) => s + Number(q.amount || 0), 0);

  if (loading) return <div className="py-12 text-center text-gray-400 text-sm">Loading quotations...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-sm font-semibold text-gray-700">Quotations from CRM</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            {quotations.length} quotation{quotations.length !== 1 ? 's' : ''}
            {totalAccepted > 0 && ` · ₹${totalAccepted.toLocaleString('en-IN')} accepted`}
          </p>
        </div>
        <button onClick={() => window.dispatchEvent(new CustomEvent('navigateTo', { detail: { page: 'crm' } }))}
          className="px-3 py-2 rounded-lg border border-gray-200 text-gray-500 text-xs hover:bg-gray-50 flex items-center gap-1.5">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
          Open in CRM
        </button>
      </div>

      {quotations.length === 0 ? (
        <div className="py-16 text-center">
          <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
          </div>
          <p className="text-gray-500 font-medium text-sm">No quotations found</p>
          <p className="text-gray-400 text-xs mt-1">Quotations are auto-linked from CRM leads with matching company name</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
              <tr>
                {['Date','Quotation No.','Title','Amount','Status'].map(h => (
                  <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {quotations.map(q => (
                <tr key={q.id} className="border-b border-gray-50 hover:bg-gray-50 transition">
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                    {new Date(q.created_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => openQuotation(q)}
                      className="text-xs font-mono font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded hover:bg-blue-100 transition">
                      #{q.quote_number}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{q.title || '—'}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-800">
                    {q.amount ? `₹${Number(q.amount).toLocaleString('en-IN', { minimumFractionDigits: 0 })}` : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${STATUS_STYLE[q.status] || 'bg-gray-50 text-gray-500'}`}>
                      {q.status || 'draft'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ClientTasksTab({ clientId, token }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterService, setFilterService] = useState('');

  useEffect(() => {
    fetch(`${BASE}/api/tasks?client_id=${clientId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    }).then(r => r.json()).then(data => {
      setTasks(Array.isArray(data) ? data : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const STATUS_STYLE = {
    pending: 'bg-amber-50 text-amber-700 border border-amber-200',
    in_progress: 'bg-blue-50 text-blue-700 border border-blue-200',
    hold: 'bg-orange-50 text-orange-700 border border-orange-200',
    completed: 'bg-green-50 text-green-700 border border-green-200',
    cancelled: 'bg-red-50 text-red-700 border border-red-200',
  };
  const STATUS_LABEL = { pending:'Pending', in_progress:'In Progress', hold:'Hold', completed:'Completed', cancelled:'Cancelled' };

  const services = [...new Set(tasks.map(t => t.service_name).filter(Boolean))];
  const filtered = tasks.filter(t =>
    (!filterStatus || t.status === filterStatus) &&
    (!filterService || t.service_name === filterService)
  );

  return (
    <div>
      {/* Filter bar */}
      <div className="flex items-center gap-3 mb-5">
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 bg-white">
          <option value="">All Statuses</option>
          {Object.entries(STATUS_LABEL).map(([v,l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={filterService} onChange={e => setFilterService(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 bg-white min-w-40">
          <option value="">All Services</option>
          {services.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {(filterStatus || filterService) && (
          <button onClick={() => { setFilterStatus(''); setFilterService(''); }}
            className="px-3 py-2 rounded-lg border border-gray-200 text-gray-500 text-xs hover:bg-gray-50">
            Clear
          </button>
        )}
        <span className="ml-auto text-xs text-gray-400">{filtered.length} task{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-400 text-sm">Loading tasks...</div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center">
          <svg className="w-12 h-12 text-gray-200 mx-auto mb-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg>
          <p className="text-gray-500 font-medium text-sm">No tasks found</p>
          <p className="text-gray-400 text-xs mt-1">Create tasks from the Tasks module</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
              <tr>
                {['Task ID', 'Service Name', 'Due Date', 'Completed Date', 'User', 'Status'].map(h => (
                  <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map(task => (
                <tr key={task.id} className="hover:bg-gray-50 transition">
                  <td className="px-4 py-3">
                    <button
                      onClick={() => window.dispatchEvent(new CustomEvent('navigateTo', { detail: { page: 'tasks', taskId: task.id } }))}
                      className="text-xs font-mono font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded hover:bg-blue-100 transition">
                      {task.task_number || `#${task.id}`}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-gray-800">{task.service_name || task.title || '—'}</p>
                    {task.title && task.service_name && (
                      <p className="text-xs text-gray-400 mt-0.5 truncate max-w-48">{task.title}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                    {task.due_date ? new Date(task.due_date).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                    {task.completed_date ? new Date(task.completed_date).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {task.assigned_name ? (
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 overflow-hidden"
                          style={{ background: task.assigned_avatar ? 'transparent' : '#0f1f45' }}>
                          {task.assigned_avatar
                            ? <img src={`${BASE}${task.assigned_avatar}`} alt={task.assigned_name} className="w-full h-full object-cover" />
                            : task.assigned_name.charAt(0)}
                        </div>
                        <span className="text-sm text-gray-600 whitespace-nowrap">{task.assigned_name}</span>
                      </div>
                    ) : <span className="text-gray-300 text-sm">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${STATUS_STYLE[task.status] || 'bg-gray-100 text-gray-600'}`}>
                      {STATUS_LABEL[task.status] || task.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
function ClientLedgerInline({ clientId, token, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9' };
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const createMenuRef = useRef(null);
  useEffect(() => {
    const h = (e) => { if (createMenuRef.current && !createMenuRef.current.contains(e.target)) setShowCreateMenu(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Jump to Finance and open a blank Create Invoice / Create Receipt form
  // pre-filled with this client — same handoff mechanism the rest of the app
  // already uses for "create X for this client" (localStorage key the
  // target tab reads on mount, then a navigateTo page switch).
  const handleCreateInvoice = () => {
    setShowCreateMenu(false);
    localStorage.setItem('pendingInvoice', JSON.stringify({ client_id: clientId }));
    window.dispatchEvent(new CustomEvent('navigateTo', { detail: { page: 'finance' } }));
  };
  const handleCreateReceipt = () => {
    setShowCreateMenu(false);
    localStorage.setItem('pendingReceiptFromInvoice', JSON.stringify({ client_id: clientId }));
    window.dispatchEvent(new CustomEvent('navigateTo', { detail: { page: 'finance', tab: 'receipts' } }));
  };

  useEffect(() => {
    fetch(`${BASE}/api/invoices/ledger/${clientId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    }).then(r => r.json()).then(data => {
      setLedger(data); setLoading(false);
    }).catch(() => setLoading(false));
  }, [clientId, token]);

  // Same firm-branded letterhead pattern every other Finance document uses
  // (Invoice/Receipt/Quotation/Credit Note) — prints the real DOM via the
  // browser's own "Save as PDF", so a long statement lays out onto proper
  // A4 pages with real selectable text instead of a rasterized image.
  const handleDownloadPdf = async () => {
    if (!ledger || downloadingPdf) return;
    setDownloadingPdf(true);
    try {
      const settings = await fetch(`${BASE}/api/invoices/settings/config`, { headers: { 'Authorization': `Bearer ${token}` } })
        .then(r => r.json()).catch(() => ({}));
      await printComponent(
        <ClientLedgerLetterhead settings={settings} ledger={ledger} />,
        `Statement of Account — ${ledger.client?.client_name || ''}`
      );
    } catch (e) {
      // Pop-up-blocked is the only realistic failure here; nothing else to
      // surface a toast through at this call site, so a plain alert is fine.
      alert(e.message || 'Could not generate PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) return <div style={{ padding:'48px 0', textAlign:'center', color:T.text3, fontSize:13 }}>Loading ledger...</div>;
  if (!ledger) return <div style={{ padding:'48px 0', textAlign:'center', color:T.text3, fontSize:13 }}>No ledger data available</div>;

  const fmt = (n) => '₹' + Math.abs(Number(n || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 });

  const openReference = (entry) => {
    if (entry.type === 'Invoice' && entry.id) {
      localStorage.setItem('openInvoiceId', String(entry.id));
      window.dispatchEvent(new CustomEvent('navigateTo', { detail: { page: 'finance' } }));
    } else if ((entry.type === 'Receipt' || entry.type === 'Payment') && entry.id) {
      localStorage.setItem('openReceiptId', String(entry.id));
      window.dispatchEvent(new CustomEvent('navigateTo', { detail: { page: 'finance', tab: 'receipts' } }));
    }
  };

  const totalInvoiced = ledger.entries?.filter(e => e.type === 'Invoice').reduce((s, e) => s + Number(e.debit || 0), 0) || 0;
  const totalPaid = ledger.entries?.filter(e => e.type === 'Payment' || e.type === 'Receipt').reduce((s, e) => s + Number(e.credit || 0), 0) || 0;
  const balance = Number(ledger.closing_balance || 0);
  // balance > 0 — client still owes the firm (Receivable, green: healthy, no
  // action needed). balance < 0 — client has paid in excess (Advance, red:
  // sits as a liability the firm needs to adjust or refund). 0 — settled.
  const balanceInfo = balance === 0
    ? { label: 'No Outstanding', sub: 'Fully settled', color: T.text3 }
    : balance > 0
      ? { label: 'Receivable', sub: 'Amount due from client', color: '#16a34a' }
      : { label: 'Advance', sub: 'Excess received — adjust or refund', color: '#dc2626' };

  const TYPE_CONFIG = {
    Invoice:  { label: 'Invoice',  dot: 'bg-gray-400' },
    Receipt:  { label: 'Receipt',  dot: 'bg-gray-500' },
    Payment:  { label: 'Payment',  dot: 'bg-gray-500' },
    'Opening Balance': { label: 'Opening Balance', dot: 'bg-gray-300' },
  };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:10 }}>
        <div ref={createMenuRef} style={{ position:'relative' }}>
          <button onClick={() => setShowCreateMenu(o => !o)} title="Create Invoice or Receipt for this client"
            style={{
              display:'flex', alignItems:'center', justifyContent:'center', width:32, height:32, borderRadius:9,
              cursor:'pointer', border:'none', background:'linear-gradient(135deg,#0f1f45,#1a3a6b)', color:'#fff',
            }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          </button>
          {showCreateMenu && (
            <div style={{
              position:'absolute', top:'calc(100% + 6px)', right:0, minWidth:170, background:T.bg2,
              border:`1px solid ${T.border}`, borderRadius:12, zIndex:20, overflow:'hidden',
              boxShadow: darkMode ? '0 12px 40px rgba(0,0,0,0.5)' : '0 12px 40px rgba(15,31,69,0.14)',
            }}>
              <button onClick={handleCreateInvoice}
                style={{ width:'100%', display:'flex', alignItems:'center', gap:9, padding:'10px 14px', border:'none', background:'transparent', cursor:'pointer', fontSize:13, fontWeight:500, color:T.text, textAlign:'left' }}
                onMouseEnter={e => e.currentTarget.style.background = T.bg3}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                Create Invoice
              </button>
              <button onClick={handleCreateReceipt}
                style={{ width:'100%', display:'flex', alignItems:'center', gap:9, padding:'10px 14px', border:'none', borderTop:`1px solid ${T.border}`, background:'transparent', cursor:'pointer', fontSize:13, fontWeight:500, color:T.text, textAlign:'left' }}
                onMouseEnter={e => e.currentTarget.style.background = T.bg3}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 4H3a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                Create Receipt
              </button>
            </div>
          )}
        </div>
      </div>
      {/* Summary Cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:16 }}>
        {[
          { label:'Total Invoiced',  value: fmt(totalInvoiced), sub:`${ledger.entries?.filter(e=>e.type==='Invoice').length||0} invoices`, color: darkMode?'#e2e8f0':'#0f1f45' },
          { label:'Total Collected', value: fmt(totalPaid),     sub:`${ledger.entries?.filter(e=>e.type==='Payment'||e.type==='Receipt').length||0} receipts`, color:'#16a34a' },
          { label: balanceInfo.label, value: balance===0?'NIL':fmt(Math.abs(balance)), sub: balanceInfo.sub, color: balanceInfo.color },
        ].map(card => (
          <div key={card.label} style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:10, padding:'10px 14px', boxShadow: darkMode?'0 2px 8px rgba(0,0,0,0.2)':'0 1px 4px rgba(15,31,69,0.05)' }}>
            <p style={{ fontSize:9.5, fontWeight:700, color:T.text3, textTransform:'uppercase', letterSpacing:'0.06em', margin:'0 0 4px' }}>{card.label}</p>
            <p style={{ fontSize:16, fontWeight:800, color:card.color, margin:0, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{card.value}</p>
            <p style={{ fontSize:10.5, color:T.text3, marginTop:2 }}>{card.sub}</p>
          </div>
        ))}
      </div>

      {/* Ledger Table */}
      <div style={{ background:T.bg2, border:`1px solid ${T.cardBorder}`, borderRadius:12, overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead style={{ background:'linear-gradient(135deg,#0f1f45,#1a3a6b)' }}>
            <tr>
              {['Date','Type','Reference No.','Debit (₹)','Credit (₹)','Balance (₹)'].map(h => (
                <th key={h} style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap' }}>{h}</th>
              ))}
              <th style={{ padding:'6px 10px', textAlign:'right', width:1 }}>
                <button onClick={handleDownloadPdf} disabled={downloadingPdf} title="Download Statement of Account (PDF)"
                  style={{
                    display:'inline-flex', alignItems:'center', justifyContent:'center', width:26, height:26, borderRadius:7,
                    cursor: downloadingPdf ? 'default' : 'pointer', border:'1px solid rgba(255,255,255,0.25)', background:'rgba(255,255,255,0.08)',
                    opacity: downloadingPdf ? 0.5 : 1, transition:'background 0.15s',
                  }}
                  onMouseEnter={e => { if (!downloadingPdf) e.currentTarget.style.background='rgba(255,255,255,0.18)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background='rgba(255,255,255,0.08)'; }}>
                  {downloadingPdf ? (
                    <span style={{ width:12, height:12, border:'2px solid rgba(255,255,255,0.35)', borderTopColor:'#fff', borderRadius:'50%', display:'inline-block', animation:'cx-spin 0.7s linear infinite' }} />
                  ) : (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  )}
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {!ledger.entries?.length ? (
              <tr><td colSpan="7" style={{ padding:'40px 16px', textAlign:'center', color:T.text3, fontSize:13 }}>No transactions recorded yet</td></tr>
            ) : ledger.entries.map((entry, i) => {
              const TYPE_DOT = { Invoice:'#6366f1', Receipt:'#10b981', Payment:'#10b981', 'Opening Balance':'#94a3b8' };
              const isClickable = (entry.type==='Invoice'||entry.type==='Receipt'||entry.type==='Payment') && entry.id;
              const bal = Number(entry.balance||0);
              return (
                <tr key={i} style={{ borderBottom:`1px solid ${T.border}`, background: darkMode?(i%2===0?T.bg2:T.bg3):(i%2===0?'#fff':'#fafbff'), transition:'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background=darkMode?'#1e3a5f':'#f0f4ff'}
                  onMouseLeave={e => e.currentTarget.style.background=darkMode?(i%2===0?T.bg2:T.bg3):(i%2===0?'#fff':'#fafbff')}>
                  <td style={{ padding:'10px 16px', fontSize:13, color:T.text2, whiteSpace:'nowrap' }}>
                    {new Date(entry.date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}
                  </td>
                  <td style={{ padding:'10px 16px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                      <span style={{ width:7, height:7, borderRadius:'50%', background: TYPE_DOT[entry.type]||T.text3, flexShrink:0 }}/>
                      <span style={{ fontSize:12, fontWeight:500, color:T.text2 }}>{entry.type}</span>
                    </div>
                  </td>
                  <td style={{ padding:'10px 16px' }}>
                    {isClickable ? (
                      <button onClick={() => openReference(entry)}
                        style={{ fontSize:13, fontWeight:600, color:'#3b82f6', background:'none', border:'none', cursor:'pointer', padding:0 }}
                        onMouseEnter={e => e.currentTarget.style.textDecoration='underline'}
                        onMouseLeave={e => e.currentTarget.style.textDecoration='none'}>
                        {entry.reference}
                      </button>
                    ) : (
                      <span style={{ fontSize:13, color:T.text2 }}>{entry.reference||'—'}</span>
                    )}
                  </td>
                  <td style={{ padding:'10px 16px', fontSize:13, fontWeight:600, color: Number(entry.debit)>0?(darkMode?'#fca5a5':'#dc2626'):T.text3 }}>
                    {Number(entry.debit)>0 ? fmt(entry.debit) : '—'}
                  </td>
                  <td style={{ padding:'10px 16px', fontSize:13, fontWeight:600, color: Number(entry.credit)>0?(darkMode?'#6ee7b7':'#16a34a'):T.text3 }}>
                    {Number(entry.credit)>0 ? fmt(entry.credit) : '—'}
                  </td>
                  <td style={{ padding:'10px 16px', fontSize:13, fontWeight:700, whiteSpace:'nowrap' }}>
                    <span style={{ color: bal>0?'#dc2626':bal<0?'#16a34a':T.text3 }}>
                      {bal===0 ? 'NIL' : fmt(bal)+(bal>0?' Dr':' Cr')}
                    </span>
                  </td>
                  <td />
                </tr>
              );
            })}
          </tbody>
        </table>
        {/* Footer */}
        <div style={{ padding:'12px 16px', background:T.bg3, borderTop:`1px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <span style={{ fontSize:11, fontWeight:700, color:T.text3, textTransform:'uppercase', letterSpacing:'0.08em' }}>Closing Balance</span>
          <span style={{ fontSize:14, fontWeight:800, color: balanceInfo.color }}>
            {balance===0 ? 'No Outstanding' : `${balanceInfo.label} — ${fmt(Math.abs(balance))}`}
          </span>
        </div>
      </div>
    </div>
  );
}


const ROLE_COLORS_M = {
  'Director':           'bg-blue-50 text-blue-700 border-blue-200',
  'Designated Partner': 'bg-purple-50 text-purple-700 border-purple-200',
  'Partner':            'bg-indigo-50 text-indigo-700 border-indigo-200',
  'Proprietor':         'bg-green-50 text-green-700 border-green-200',
  'Trustee':            'bg-amber-50 text-amber-700 border-amber-200',
  'Karta':              'bg-orange-50 text-orange-700 border-orange-200',
  'Governing Member':   'bg-teal-50 text-teal-700 border-teal-200',
  'Secretary':          'bg-cyan-50 text-cyan-700 border-cyan-200',
  'Member':             'bg-slate-50 text-slate-700 border-slate-200',
  'Other':              'bg-gray-50 text-gray-600 border-gray-200',
};

function ManagementTab({ clientId, token, darkMode = false, T: theme }) {
  const T = theme || { bg2:'#fff', bg3:'#f8faff', border:'#e8edf5', border2:'#e2e8f0', text:'#0f1f45', text2:'#4a5568', text3:'#94a3b8', cardBg:'#fff', cardBorder:'#f1f5f9' };
  const [persons, setPersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const avatarColors = ['#0f1f45','#3b82f6','#8b5cf6','#10b981','#f59e0b','#ef4444','#ec4899'];
  const avatarColor = name => avatarColors[(name?.charCodeAt(0)||0) % avatarColors.length];

  useEffect(() => {
    setLoading(true);
    fetch(`${BASE}/api/persons/by-client/${clientId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    }).then(r => r.json())
      .then(d => setPersons(Array.isArray(d) ? d : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [clientId, token]);

  const active   = persons.filter(p => p.is_active !== false);
  const inactive = persons.filter(p => p.is_active === false);

  const ROLE_META = {
    'Director':           { color:'#2563eb', bg: darkMode?'rgba(59,130,246,0.12)':'#eff6ff',   border: darkMode?'rgba(59,130,246,0.25)':'#bfdbfe'  },
    'Designated Partner': { color:'#7c3aed', bg: darkMode?'rgba(139,92,246,0.12)':'#f5f3ff',   border: darkMode?'rgba(139,92,246,0.25)':'#ddd6fe'  },
    'Partner':            { color:'#4f46e5', bg: darkMode?'rgba(99,102,241,0.12)':'#eef2ff',   border: darkMode?'rgba(99,102,241,0.25)':'#c7d2fe'  },
    'Proprietor':         { color:'#16a34a', bg: darkMode?'rgba(16,185,129,0.12)':'#f0fdf4',   border: darkMode?'rgba(16,185,129,0.25)':'#bbf7d0'  },
    'Trustee':            { color:'#d97706', bg: darkMode?'rgba(245,158,11,0.12)':'#fffbeb',   border: darkMode?'rgba(245,158,11,0.25)':'#fde68a'  },
    'Karta':              { color:'#ea580c', bg: darkMode?'rgba(249,115,22,0.12)':'#fff7ed',   border: darkMode?'rgba(249,115,22,0.25)':'#fed7aa'  },
    'Governing Member':   { color:'#0891b2', bg: darkMode?'rgba(8,145,178,0.12)':'#ecfeff',    border: darkMode?'rgba(8,145,178,0.25)':'#a5f3fc'   },
    'Secretary':          { color:'#0e7490', bg: darkMode?'rgba(14,116,144,0.12)':'#ecfeff',   border: darkMode?'rgba(14,116,144,0.25)':'#a5f3fc'  },
    'Member':             { color:'#475569', bg: darkMode?'rgba(71,85,105,0.12)':'#f8fafc',    border: darkMode?'rgba(71,85,105,0.25)':'#e2e8f0'   },
    'Other':              { color: T.text3,  bg: T.bg3, border: T.border2 },
  };

  const InfoRow = ({ label, value, mono = false }) => value ? (
    <div style={{ display:'flex', alignItems:'flex-start', gap:8 }}>
      <span style={{ fontSize:11, color:T.text3, width:64, flexShrink:0 }}>{label}</span>
      <span style={{ fontSize:12, color:T.text, fontWeight: mono?600:500, fontFamily: mono?'monospace':'inherit' }}>{value}</span>
    </div>
  ) : null;

  const PersonCard = ({ p }) => {
    const rm = ROLE_META[p.role] || ROLE_META['Other'];
    const isSel = selected?.id === p.id;
    return (
      <div onClick={() => setSelected(isSel ? null : p)}
        style={{
          background: T.bg2, border:`1px solid ${isSel?(darkMode?'rgba(59,130,246,0.4)':'#93c5fd'):T.cardBorder}`,
          borderRadius:14, padding:16, cursor:'pointer', transition:'all 0.15s',
          opacity: p.is_active===false ? 0.6 : 1,
          boxShadow: isSel?(darkMode?'0 0 0 3px rgba(59,130,246,0.2)':'0 0 0 3px rgba(59,130,246,0.1)'):(darkMode?'0 2px 8px rgba(0,0,0,0.2)':'0 1px 4px rgba(15,31,69,0.05)'),
        }}
        onMouseEnter={e => { if(!isSel) e.currentTarget.style.boxShadow=darkMode?'0 4px 16px rgba(0,0,0,0.3)':'0 4px 16px rgba(15,31,69,0.1)'; }}
        onMouseLeave={e => { if(!isSel) e.currentTarget.style.boxShadow=darkMode?'0 2px 8px rgba(0,0,0,0.2)':'0 1px 4px rgba(15,31,69,0.05)'; }}>
        {/* Header */}
        <div style={{ display:'flex', alignItems:'flex-start', gap:12, marginBottom:12 }}>
          {p.photo_url ? (
            <img src={p.photo_url} alt={p.full_name} style={{ width:44, height:44, borderRadius:'50%', objectFit:'cover', border:`2px solid ${T.border}`, flexShrink:0 }} />
          ) : (
            <div style={{ width:44, height:44, borderRadius:'50%', background:`linear-gradient(135deg,${avatarColor(p.full_name)},${avatarColor(p.full_name)}cc)`, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:700, fontSize:17, flexShrink:0 }}>
              {p.full_name?.charAt(0)?.toUpperCase()}
            </div>
          )}
          <div style={{ flex:1, minWidth:0 }}>
            <p style={{ fontSize:13, fontWeight:700, color:T.text, margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.full_name}</p>
            <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:5, flexWrap:'wrap' }}>
              <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:20, color:rm.color, background:rm.bg, border:`1px solid ${rm.border}` }}>{p.role}</span>
              {p.designation && <span style={{ fontSize:11, color:T.text3 }}>{p.designation}</span>}
            </div>
          </div>
          {p.is_active===false && (
            <span style={{ fontSize:10, padding:'2px 8px', background:T.bg3, color:T.text3, borderRadius:20, flexShrink:0, border:`1px solid ${T.border}` }}>Inactive</span>
          )}
        </div>

        {/* Key Info */}
        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
          <InfoRow label="PAN"    value={p.pan_number}   mono />
          <InfoRow label="DIN"    value={p.din && (
            <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
              <span style={{ fontFamily:'monospace', fontWeight:600, color:T.text }}>{p.din}</span>
              {p.din_status && <span style={{ fontSize:10, padding:'1px 6px', borderRadius:4, fontWeight:600, background: p.din_status==='Active'?(darkMode?'rgba(16,185,129,0.12)':'#f0fdf4'):(darkMode?'rgba(239,68,68,0.12)':'#fef2f2'), color: p.din_status==='Active'?'#16a34a':'#dc2626' }}>{p.din_status}</span>}
            </span>
          )} />
          {p.dpin && <InfoRow label="DPIN"   value={p.dpin}   mono />}
          <InfoRow label="Mobile" value={p.primary_mobile} />
          <InfoRow label="Email"  value={p.email} />
          {p.shareholding_percent && <InfoRow label="Holding" value={`${p.shareholding_percent}%`} />}
          {p.since_date && <InfoRow label="Since" value={new Date(p.since_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})} />}
        </div>

        {/* Expanded */}
        {isSel && (
          <div style={{ marginTop:12, paddingTop:12, borderTop:`1px solid ${T.border}`, display:'flex', flexDirection:'column', gap:6 }}>
            {p.aadhaar_number && <InfoRow label="Aadhaar"    value={p.aadhaar_number} mono />}
            {p.date_of_birth  && <InfoRow label="DOB"        value={new Date(p.date_of_birth).toLocaleDateString('en-IN',{day:'2-digit',month:'long',year:'numeric'})} />}
            {p.father_name    && <InfoRow label="Father"     value={p.father_name} />}
            {p.secondary_mobile && <InfoRow label="Mobile 2" value={p.secondary_mobile} />}
            {p.address_aadhaar && <InfoRow label="Address"   value={p.address_aadhaar} />}
            {p.dsc_expiry && (
              <InfoRow label="DSC Exp" value={
                <span style={{ color: new Date(p.dsc_expiry)<new Date()?'#ef4444':T.text, fontWeight:500 }}>
                  {new Date(p.dsc_expiry).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}
                  {new Date(p.dsc_expiry)<new Date()?' ⚠ Expired':''}
                </span>
              } />
            )}
            {p.bank_name && (
              <div style={{ marginTop:8, paddingTop:8, borderTop:`1px solid ${T.border}` }}>
                <p style={{ fontSize:10, fontWeight:700, color:'#fff', textTransform:'uppercase', marginBottom:6 }}>Bank Details</p>
                {p.bank_name    && <InfoRow label="Bank"    value={p.bank_name} />}
                {p.bank_account && <InfoRow label="Account" value={p.bank_account} mono />}
                {p.bank_ifsc    && <InfoRow label="IFSC"    value={p.bank_ifsc} mono />}
              </div>
            )}
            {p.notes && (
              <div style={{ marginTop:8, padding:'8px 10px', background:T.bg3, borderRadius:8, border:`1px solid ${T.border}` }}>
                <span style={{ fontSize:11, color:T.text3 }}>Notes: </span>
                <span style={{ fontSize:12, color:T.text2 }}>{p.notes}</span>
              </div>
            )}
            <p style={{ fontSize:11, color:T.text3, marginTop:4 }}>Click to collapse</p>
          </div>
        )}
      </div>
    );
  };

  if (loading) return <div style={{ padding:'64px 0', textAlign:'center', color:T.text3, fontSize:13 }}>Loading management...</div>;

  if (persons.length === 0) return (
    <div style={{ padding:'64px 0', textAlign:'center' }}>
      <div style={{ width:64, height:64, background:T.bg3, border:`2px dashed ${T.border2}`, borderRadius:16, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px' }}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
      </div>
      <p style={{ fontSize:14, fontWeight:600, color:T.text2, margin:0 }}>No persons linked to this client</p>
      <p style={{ fontSize:12, color:T.text3, marginTop:6, maxWidth:280, margin:'6px auto 0' }}>
        Go to the <strong>People</strong> module → select a person → Associated Companies → link to this client
      </p>
    </div>
  );

  return (
    <div>
      {/* Stats */}
      <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:20 }}>
        <div style={{ display:'flex', alignItems:'center', gap:7 }}>
          <div style={{ width:8, height:8, borderRadius:'50%', background:'#10b981' }}/>
          <span style={{ fontSize:13, color:T.text2, fontWeight:500 }}>{active.length} Active</span>
        </div>
        {inactive.length > 0 && (
          <div style={{ display:'flex', alignItems:'center', gap:7 }}>
            <div style={{ width:8, height:8, borderRadius:'50%', background:T.text3 }}/>
            <span style={{ fontSize:13, color:T.text3 }}>{inactive.length} Inactive</span>
          </div>
        )}
        <span style={{ fontSize:11, color:T.text3 }}>· Click any card to expand full details</span>
      </div>

      {active.length > 0 && (
        <>
          <p style={{ fontSize:10, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:12 }}>Active</p>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14, marginBottom:20 }}>
            {active.map(p => <PersonCard key={p.id} p={p} />)}
          </div>
        </>
      )}
      {inactive.length > 0 && (
        <>
          <p style={{ fontSize:10, fontWeight:700, color:'#fff', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:12 }}>Past / Inactive</p>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14 }}>
            {inactive.map(p => <PersonCard key={p.id} p={p} />)}
          </div>
        </>
      )}
    </div>
  );
}

export default Clients;